/**
 * PackOpeningModal — Interactive 3D Cyber Booster Pack Opening Experience.
 * 
 * Flow:
 * 1. Pack Showcase & Purchase (150 Fugazi)
 * 2. Booster Foil Tear Animation
 * 3. 5 Face-Down Cards with interactive 3D flip & reveal
 * 4. Specular Shimmer on Gold Editions & Cyber Glow on Tickets
 * 5. Summary & Re-open option
 */

import { packs, PACK_COST_FUGAZI, PACK_DEFINITIONS } from '../engine/packs.js';
import { wallet } from '../engine/wallet.js';
import { directives } from '../engine/directives.js';
import { inventory } from '../engine/inventory.js';
import { drawSpacePlanet } from '../engine/render/background.js';
import { hubNavigator } from './hubNavigator.js';
import { getCardImageUrl, getCardByName } from '../data/cards.js';
import { getCardThumbUrl } from '../data/cardThumbUrl.js';
import { getCardFaceUrl } from '../data/cardFaces.js';
import { isAbOn } from './abFlags.js';
import { checkIsMobile } from '../engine/performance.js';
import { getIconSvg } from './icons.js';

// Maps playAudioSfx()'s internal beat names to their cargo_bay registry
// entity id, so an audio file assigned in the Sound Review tool replaces
// the synthesized tear-sequence layer below without touching call sites.
const PACK_TEAR_SFX_REGISTRY_IDS = {
  grip: 'pack_lock_in',
  lock_in: 'pack_lock_in',
  charge: 'pack_charge_tone',
  arc_crack: 'pack_arc_crack',
  latch_snap: 'pack_latch_snap',
  detonation: 'pack_detonation_sfx'
};

const escapeHtml = (v) => String(v ?? '').replace(/[&<>"']/g, (c) => (
  { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]
));

// Warp Speed timing (ms). One flip per interval, never more than two per frame; a Gold Edition card
// holds the cascade for its sting; the haul replaces the cards once the last flip has settled.
export const WARP_FLIP_INTERVAL_MS = { row: 42, carousel: 58 };
export const WARP_GOLD_STING_MS = 350;
export const WARP_LEAD_MS = 80;       // flash peak comes first, the first flip follows
export const WARP_SETTLE_MS = 130;    // last flip -> end flash
export const WARP_SFX_MIN_GAP_MS = 55; // voice budget: at most one flip sound per gap

/**
 * What the Warp Speed result screen shows: counters for everything, cards only for what is worth
 * looking at (Gold Editions and cards new to the collection). Tickets are collapsed into one tile and
 * duplicates are never shown (only tallied in the result for callers/tests); a pull of nothing but copies gets one
 * "Copies only" tile instead.
 */
export function summarizeHaul(cards) {
  const list = Array.isArray(cards) ? cards : [];
  const out = { total: list.length, gold: 0, newCards: 0, tickets: 0, duplicates: 0, highlights: [] };
  for (const card of list) {
    if (!card) continue;
    if (card.type === 'ticket') {
      out.tickets++;
    } else if (card.type === 'special_edition') {
      out.gold++;
      if (card.isNew) out.newCards++;
      out.highlights.push(card);
    } else if (card.isNew) {
      out.newCards++;
      out.highlights.push(card);
    } else {
      out.duplicates++;
    }
  }
  // Gold first, then the other new cards, in pull order within each group.
  out.highlights.sort((a, b) => (b.type === 'special_edition') - (a.type === 'special_edition'));
  return out;
}

export class PackOpeningModal {
  constructor(audioEngine = null, game = null) {
    this.audio = audioEngine;
    this.game = game;
    this.modalEl = document.getElementById('pack-opening-modal');
    this.stageContainer = document.getElementById('pack-modal-stage');
    this.btnClose = document.getElementById('btn-close-pack-modal');
    this.btnBackdrop = document.getElementById('pack-modal-backdrop');

    this._selectedPackId = 'core_pack';
    this._selectedPackCount = 1;
    this._currentPackResult = null;
    this._predecodePromise = null;
    this._cardsRevealedCount = 0;
    this._state = 'idle'; // 'idle', 'tearing', 'opened', 'revealed'

    // Mobile swipe carousel & directional slide transitions
    this._isTransitioning = false;
    this._isSwiping = false;
    this._lastDragEndTime = 0;
    this._incomingSlideDir = null;
    this._currentMobileCardIdx = 0;
    this._isAutoRevealing = false;
    this._goldAscendEndTime = 0;

    // Warp Speed (the accelerated Reveal All): armed by the first click, active after the second.
    this._warpArmed = false;
    this._warpActive = false;
    this._warpGen = 0;          // bumped on every reset so a stale cascade/timer stops itself
    this._warpRaf = null;
    this._warpGeom = null;
    this._warpLastSfxAt = 0;
    this._warpStreaks = [];
    this._warpFlashEl = null;
    this._warpFlashAnim = null;

    // High-performance Particle & Lighting Systems (Zero-stutter architecture)
    this.jitterMultiplier = 0.65;
    this.arcsIntensity = 1.30;
    this.sparksIntensity = 2.00;
    this.durationMultiplier = 0.80;
    this.timeScale = 1.0;
    this.arcsEnabled = true;
    this.sparksEnabled = true;
    this.isPaused = false;

    this.arcsCanvas = null;
    this.arcsCtx = null;
    this.sparksCanvas = null;
    this.sparksCtx = null;

    // Zero-allocation spark pool (pre-allocated to eliminate GC pauses)
    this.sparks = [];
    this.sparkPool = [];
    for (let i = 0; i < 3000; i++) {
      this.sparkPool.push({ shapeOffsets: [0, 0, 0, 0] });
    }
    this.giantArcs = [];
    this._lightningScratchPoints = [];
    this._cachedPackCenter = null;
    this._preChargingTimer = null;
    this._stormTimer = null;
    this._arcsCutoff = false;
    this.floorY = 600;

    // GPU-cached sprites & render loop flags
    this._flashSpriteCache = new Map();
    this._streakSprite = null;
    this._vfxLoopRunning = false;
    this.animationFrameId = null;
    this.lastFrameTime = performance.now();
    this.activeTimeouts = [];

    // Pre-warmed Web Audio buffers for zero-latency audio dispatch
    this.synthAudioCtx = null;
    this._audioBuffersPrewarmed = false;
    this._detonationNoiseBuffer = null;
    this._arcNoiseBuffer = null;
    this._hissNoiseBuffer = null;

    this.init();
  }

  init() {
    if (this.btnClose) {
      this.btnClose.addEventListener('click', () => this.close());
    }
    if (this.btnBackdrop) {
      this.btnBackdrop.addEventListener('click', () => {
        if (this._state !== 'tearing') this.close();
      });
    }

    // Reactive wallet & inventory balance updates if open
    wallet.addListener(() => {
      if (this.isOpen() && this._state === 'idle') {
        this.renderBuyStage();
      }
    });

    inventory.addListener(() => {
      if (this.isOpen() && this._state === 'idle') {
        this.renderBuyStage();
      }
    });

    // Empty-pedestal quest slot: swaps between the DAILY QUESTS link and the reset timer
    directives.addListener(() => this._syncQuestSlot());
    this._questSlotTimer = null;
    this._onCardsRowMouseMove = null;
    this._onCardsRowMouseUp = null;

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen()) {
        if (this.game?.directivesModal?.isOpen?.()) return;
        if (this._state !== 'tearing') {
          e.stopPropagation();
          hubNavigator.navigateTo('archive');
        }
      }
    });

    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible' && this.isOpen()) {
        if (this._state === 'opened') {
          const revealView = this.stageContainer?.querySelector('.pack-reveal-view');
          if (revealView) {
            this._settleRevealEntrance(revealView);
          }
        }
      }
    });

    this._setupParallax();
    this._syncMobileState();
    this._setupCanvases();
    this._slotCenters = null;
    this._cargoPlanetsRenderedSize = null;
    this._cachedPackCenter = null;
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', () => {
        this._slotCenters = null;
        this._cargoPlanetsRenderedSize = null;
        this._cachedPackCenter = null;
        this._syncMobileState();
        if (this.isOpen()) {
          this._renderCargoPlanet();
          this._setupCanvases();
        }
      });
    }
  }

  _syncMobileState() {
    const isMobile = this._isMobile();
    if (this.modalEl) {
      this.modalEl.classList.toggle('is-mobile', isMobile);
    }
    const scene = document.getElementById('cargo-scene');
    if (scene) {
      scene.classList.toggle('is-mobile', isMobile);
    }
    if (this._state === 'opened' && !this._usesSwipeReveal()) {
      if (this._isAutoRevealing) {
        this._isAutoRevealing = false;
        const total = this._currentPackResult?.cards?.length || 5;
        for (let i = 0; i < total; i++) {
          this.revealCard(i);
        }
      }
      if (this._cardRevealDebounceTimer) {
        clearTimeout(this._cardRevealDebounceTimer);
        this._cardRevealDebounceTimer = null;
      }
      if (this._snapRestoreTimer) {
        clearTimeout(this._snapRestoreTimer);
        this._snapRestoreTimer = null;
      }
      this._isSwiping = false;
      const cardsRow = document.getElementById('pack-cards-row');
      if (cardsRow) {
        const slots = cardsRow.querySelectorAll('.pack-card-slot');
        slots.forEach(slot => {
          slot.classList.remove('centered', 'just-centered');
        });
      }
    }
  }

  _setupCanvases() {
    this.arcsCanvas = document.getElementById('cargo-arcs-canvas');
    this.sparksCanvas = document.getElementById('cargo-sparks-canvas');
    const scene = document.getElementById('cargo-scene');

    // Defensively create canvases if missing
    if (scene && (!this.arcsCanvas || !this.sparksCanvas)) {
      if (!this.arcsCanvas) {
        this.arcsCanvas = document.createElement('canvas');
        this.arcsCanvas.id = 'cargo-arcs-canvas';
        this.arcsCanvas.className = 'cargo-fx-canvas cargo-arcs-canvas';
        scene.appendChild(this.arcsCanvas);
      }
      if (!this.sparksCanvas) {
        this.sparksCanvas = document.createElement('canvas');
        this.sparksCanvas.id = 'cargo-sparks-canvas';
        this.sparksCanvas.className = 'cargo-fx-canvas cargo-sparks-canvas';
        scene.appendChild(this.sparksCanvas);
      }
    }

    this.arcsCtx = this.arcsCanvas?.getContext('2d');
    this.sparksCtx = this.sparksCanvas?.getContext('2d');

    const isMobile = this._isMobile();
    const dpr = isMobile ? 1 : Math.min(window.devicePixelRatio || 1, 2);
    this.dpr = dpr;
    const rect = scene ? scene.getBoundingClientRect() : null;
    const w = Math.round(scene?.clientWidth || rect?.width || (typeof window !== 'undefined' ? window.innerWidth : 800) || 800);
    const h = Math.round(scene?.clientHeight || rect?.height || (typeof window !== 'undefined' ? window.innerHeight : 600) || 600);
    this.floorY = h > 100 ? h - 40 : 560;

    [this.arcsCanvas, this.sparksCanvas].forEach(c => {
      if (!c) return;
      const targetW = Math.round(w * dpr);
      const targetH = Math.round(h * dpr);
      if (c.width !== targetW || c.height !== targetH) {
        c.width = targetW;
        c.height = targetH;
      }
      c.style.width = '100%';
      c.style.height = '100%';
      const ctx = c.getContext('2d');
      if (ctx) ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    });
    // Resizing a canvas blanks it, so nothing is left to clear.
    this._arcsDrawn = false;
    this._sparksDrawn = false;
  }

  _isAudioMuted() {
    if (this.audio && typeof this.audio.isMuted === 'boolean') {
      if (this.audio.isMuted) return true;
      if (typeof this.audio.sfxVolume === 'number' && this.audio.sfxVolume <= 0) return true;
      return false;
    }
    if (this.game?.audio && typeof this.game.audio.isMuted === 'boolean') {
      if (this.game.audio.isMuted) return true;
      if (typeof this.game.audio.sfxVolume === 'number' && this.game.audio.sfxVolume <= 0) return true;
      return false;
    }
    if (typeof window !== 'undefined' && window.tcgGame?.audio && typeof window.tcgGame.audio.isMuted === 'boolean') {
      if (window.tcgGame.audio.isMuted) return true;
      if (typeof window.tcgGame.audio.sfxVolume === 'number' && window.tcgGame.audio.sfxVolume <= 0) return true;
      return false;
    }
    try {
      if (typeof localStorage !== 'undefined') {
        const storedSVol = localStorage.getItem('ucg_sfx_volume');
        if (storedSVol !== null && Number(storedSVol) <= 0) return true;
        const stored = localStorage.getItem('ucg_sound_muted');
        if (stored !== null) {
          return stored === 'true';
        }
      }
    } catch {}
    return true; // UCG default sound mode is OFF
  }

  _getVolume() {
    if (this.audio?.sfxVolume !== undefined) {
      return this.audio.sfxVolume;
    }
    if (this.game?.audio?.sfxVolume !== undefined) {
      return this.game.audio.sfxVolume;
    }
    return 0.6;
  }

  _isMobile() {
    if (typeof window === 'undefined') return false;
    if (checkIsMobile && checkIsMobile()) return true;
    if (window.matchMedia && (
      window.matchMedia('(max-width: 900px), (orientation: landscape) and (max-height: 560px), (pointer: coarse)').matches ||
      window.matchMedia('(max-width: 900px), (orientation: landscape) and (max-height: 500px), (pointer: coarse)').matches
    )) {
      return true;
    }
    return window.innerWidth <= 900 || window.innerHeight <= 560 || (typeof navigator !== 'undefined' && /iPhone|iPad|iPod|Android|Mobi/i.test(navigator.userAgent || ''));
  }

  _usesSwipeReveal() {
    if (typeof window === 'undefined') return false;
    const isLand = (window.matchMedia && (
      window.matchMedia('(orientation: landscape)').matches ||
      window.matchMedia('(orientation: landscape) and (max-height: 560px)').matches ||
      window.matchMedia('(orientation: landscape) and (max-height: 500px)').matches
    )) || (window.innerWidth > window.innerHeight);

    if (isLand) return false;

    // Wide portrait foldables / tablets (width >= 650px) have room for 5 cards in a row.
    // Only narrow portrait phones need the 1-card swipe carousel.
    if (window.innerWidth >= 650) return false;

    return this._isMobile();
  }


  /* ============================================================
     PRE-WARMED WEB AUDIO SYNTHESIZER (ZERO-LATENCY TRANSIENTS)
     ============================================================ */
  _getSynthAudioContext() {
    if (this._isAudioMuted()) return null;
    const globalCtx = this.audio?.ctx || this.game?.audio?.ctx || window.tcgGame?.audio?.ctx || window.Howler?.ctx;
    if (globalCtx) {
      if (globalCtx.state === 'suspended') {
        globalCtx.resume?.();
      }
      this._prewarmAudioBuffers(globalCtx);
      return globalCtx;
    }
    if (!this.synthAudioCtx) {
      const AudioCtxClass = typeof window !== 'undefined' ? (window.AudioContext || window.webkitAudioContext) : null;
      if (AudioCtxClass) {
        this.synthAudioCtx = new AudioCtxClass();
        this._prewarmAudioBuffers(this.synthAudioCtx);
      }
    }
    if (this.synthAudioCtx && this.synthAudioCtx.state === 'suspended') {
      this.synthAudioCtx.resume();
      this._prewarmAudioBuffers(this.synthAudioCtx);
    }
    return this.synthAudioCtx;
  }

  _prewarmAudioBuffers(ctx) {
    if (this._audioBuffersPrewarmed || !ctx) return;
    try {
      // 1. Detonation explosive plasma roar buffer (0.6s) - Zero-delay detonation
      const detBufSize = Math.round(ctx.sampleRate * 0.6);
      this._detonationNoiseBuffer = ctx.createBuffer(1, detBufSize, ctx.sampleRate);
      const detData = this._detonationNoiseBuffer.getChannelData(0);
      const detDecay = ctx.sampleRate * 0.16;
      for (let i = 0; i < detBufSize; i++) {
        detData[i] = (Math.random() * 2 - 1) * Math.exp(-i / detDecay);
      }

      // 2. High-voltage plasma discharge arc buffer (0.08s)
      const arcBufSize = Math.round(ctx.sampleRate * 0.08);
      this._arcNoiseBuffer = ctx.createBuffer(1, arcBufSize, ctx.sampleRate);
      const arcData = this._arcNoiseBuffer.getChannelData(0);
      const arcDecay = ctx.sampleRate * 0.02;
      for (let i = 0; i < arcBufSize; i++) {
        arcData[i] = (Math.random() * 2 - 1) * Math.exp(-i / arcDecay);
      }

      // 3. Pneumatic hiss / pressure release buffer (0.18s)
      const hissBufSize = Math.round(ctx.sampleRate * 0.18);
      this._hissNoiseBuffer = ctx.createBuffer(1, hissBufSize, ctx.sampleRate);
      const hissData = this._hissNoiseBuffer.getChannelData(0);
      const hissDecay = ctx.sampleRate * 0.05;
      for (let i = 0; i < hissBufSize; i++) {
        hissData[i] = (Math.random() * 2 - 1) * Math.exp(-i / hissDecay);
      }

      this._audioBuffersPrewarmed = true;
    } catch (err) {
      console.warn('Audio pre-warm error:', err);
    }
  }

  playAudioSfx(type) {
    if (this._isAudioMuted()) return;
    try {
      const registryId = PACK_TEAR_SFX_REGISTRY_IDS[type];
      if (registryId && this.audio && typeof this.audio.playRegistrySound === 'function') {
        const res = this.audio.playRegistrySound('cargo_bay', registryId, 'fire');
        if (res !== null) return;
      }
      const ctx = this._getSynthAudioContext();
      if (!ctx) return;
      const vol = this._getVolume();
      const now = ctx.currentTime;

      if (type === 'grip' || type === 'lock_in') {
        const subOsc = ctx.createOscillator();
        const subGain = ctx.createGain();
        subOsc.type = 'sine';
        subOsc.frequency.setValueAtTime(55, now);
        subOsc.frequency.exponentialRampToValueAtTime(22, now + 0.38);
        subGain.gain.setValueAtTime(0.7 * vol, now);
        subGain.gain.exponentialRampToValueAtTime(0.001, now + 0.38);
        subOsc.connect(subGain);
        subGain.connect(ctx.destination);
        subOsc.start(now);
        subOsc.stop(now + 0.40);

        const snapOsc = ctx.createOscillator();
        const snapGain = ctx.createGain();
        snapOsc.type = 'triangle';
        snapOsc.frequency.setValueAtTime(680, now);
        snapOsc.frequency.exponentialRampToValueAtTime(110, now + 0.12);
        snapGain.gain.setValueAtTime(0.55 * vol, now);
        snapGain.gain.exponentialRampToValueAtTime(0.001, now + 0.12);
        snapOsc.connect(snapGain);
        snapGain.connect(ctx.destination);
        snapOsc.start(now);
        snapOsc.stop(now + 0.14);

        const hiss = ctx.createBufferSource();
        if (this._hissNoiseBuffer) {
          hiss.buffer = this._hissNoiseBuffer;
        }
        const hissFilter = ctx.createBiquadFilter();
        hissFilter.type = 'bandpass';
        hissFilter.frequency.value = 3200;
        hissFilter.Q.value = 2.0;
        const hissGain = ctx.createGain();
        hissGain.gain.setValueAtTime(0.35 * vol, now);
        hissGain.gain.exponentialRampToValueAtTime(0.001, now + 0.16);
        hiss.connect(hissFilter);
        hissFilter.connect(hissGain);
        hissGain.connect(ctx.destination);
        hiss.start(now);

        const chirpOsc = ctx.createOscillator();
        const chirpGain = ctx.createGain();
        chirpOsc.type = 'sine';
        chirpOsc.frequency.setValueAtTime(880, now + 0.08);
        chirpOsc.frequency.setValueAtTime(1320, now + 0.14);
        chirpGain.gain.setValueAtTime(0.001, now);
        chirpGain.gain.setValueAtTime(0.18 * vol, now + 0.08);
        chirpGain.gain.exponentialRampToValueAtTime(0.001, now + 0.28);
        chirpOsc.connect(chirpGain);
        chirpGain.connect(ctx.destination);
        chirpOsc.start(now + 0.08);
        chirpOsc.stop(now + 0.30);
      } else if (type === 'charge') {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        const filter = ctx.createBiquadFilter();
        osc.type = 'sawtooth';
        filter.type = 'bandpass';
        filter.Q.value = 5.0;

        const duration = (2.2 * (this.durationMultiplier || 0.8)) / (this.timeScale || 1.0);
        osc.frequency.setValueAtTime(110, now);
        osc.frequency.exponentialRampToValueAtTime(1600, now + duration);
        filter.frequency.setValueAtTime(180, now);
        filter.frequency.exponentialRampToValueAtTime(3200, now + duration);

        gain.gain.setValueAtTime(0.02 * vol, now);
        gain.gain.linearRampToValueAtTime(0.24 * vol, now + duration * 0.88);
        gain.gain.exponentialRampToValueAtTime(0.001, now + duration);

        osc.connect(filter);
        filter.connect(gain);
        gain.connect(ctx.destination);
        osc.start(now);
        osc.stop(now + duration);
      } else if (type === 'arc_crack') {
        const whiteNoise = ctx.createBufferSource();
        if (this._arcNoiseBuffer) {
          whiteNoise.buffer = this._arcNoiseBuffer;
        }
        const filter = ctx.createBiquadFilter();
        filter.type = 'bandpass';
        filter.frequency.value = 1800 + Math.random() * 1200;
        filter.Q.value = 3.0;

        const gain = ctx.createGain();
        gain.gain.setValueAtTime(0.35 * vol, now);
        gain.gain.exponentialRampToValueAtTime(0.01, now + 0.07);

        whiteNoise.connect(filter);
        filter.connect(gain);
        gain.connect(ctx.destination);
        whiteNoise.start(now);
      } else if (type === 'latch_snap') {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'triangle';
        osc.frequency.setValueAtTime(320, now);
        osc.frequency.exponentialRampToValueAtTime(38, now + 0.28);
        gain.gain.setValueAtTime(0.55 * vol, now);
        gain.gain.exponentialRampToValueAtTime(0.001, now + 0.28);
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.start(now);
        osc.stop(now + 0.29);
      } else if (type === 'detonation') {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'sine';
        osc.frequency.setValueAtTime(110, now);
        osc.frequency.exponentialRampToValueAtTime(26, now + 1.1);
        gain.gain.setValueAtTime(0.7 * vol, now);
        gain.gain.exponentialRampToValueAtTime(0.001, now + 1.1);
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.start(now);
        osc.stop(now + 1.12);

        const noise = ctx.createBufferSource();
        if (this._detonationNoiseBuffer) {
          noise.buffer = this._detonationNoiseBuffer;
        }
        const nGain = ctx.createGain();
        nGain.gain.setValueAtTime(0.5 * vol, now);
        nGain.gain.exponentialRampToValueAtTime(0.001, now + 0.6);
        noise.connect(nGain);
        nGain.connect(ctx.destination);
        noise.start(now);
      }
    } catch (err) {
      console.warn('Audio sfx error:', err);
    }
  }

  // Registry-first (cargo_bay.<registryId>) generic-click helper, replacing
  // the various `audio.playBark?.('ui_click')` call sites below (a
  // pseudo-race click abused as a generic UI-click synth - see audio.js's
  // playBark() comment). An assigned file wins; otherwise falls through to
  // the original synthesized click unchanged.
  _playCargoBaySfx(registryId, opts = {}) {
    if (!this.audio || this._isAudioMuted()) return;
    try {
      if (typeof this.audio.playRegistrySound === 'function') {
        const res = this.audio.playRegistrySound('cargo_bay', registryId, 'fire', opts);
        if (res !== null) return;
      }
      this.audio.playBark?.('ui_click');
    } catch (e) {
      // Audio fallback silent
    }
  }

  // BEAT 1B: Sekundäres Einrasten, 30ms nach BEAT 1 (siehe startPackTear()).
  // Registry-first (cargo_bay.pack_lock_confirm, seeded mit
  // more/workhome/lock_4.m4a); hardcoded Fallback auf dieselbe Datei, falls
  // der Pool über die Sound Review geleert wird - anders als
  // _playCargoBaySfx() hat dieser Beat keine sinnvolle Synthese-Alternative.
  _playPackLockConfirmSfx() {
    if (!this.audio || this._isAudioMuted()) return;
    try {
      const res = this.audio.playRegistrySound?.('cargo_bay', 'pack_lock_confirm', 'fire');
      if (res !== null && res !== undefined) return;
      this.audio.playSample?.('more/workhome/lock_4.m4a', { category: 'ui', volume: 0.8 });
    } catch (e) {
      // Audio fallback silent
    }
  }

  // Android/Chrome-only physical feedback for the tear/burst beats;
  // iOS Safari has no Vibration API and this is simply a no-op there.
  _pulseHaptic(pattern) {
    try { navigator.vibrate && navigator.vibrate(pattern); } catch (e) { /* unsupported - ignore */ }
  }

  // Cursor-driven depth parallax: background layers drift opposite the
  // cursor (subtle), the pack itself drifts with it (more, foreground)
  // - smoothed via a damped requestAnimationFrame Lerp loop that sleeps
  // automatically when the mouse is stationary (zero idle CPU overhead).
  _setupParallax() {
    const scene = document.getElementById('cargo-scene');
    if (!scene) return;
    const bgLayers = scene.querySelectorAll('[data-parallax]');

    let packStack = null;
    let packFoil = null;
    let cachedRect = null;
    const updateRect = () => {
      if (this._isMobile()) return;
      cachedRect = scene.getBoundingClientRect();
      packStack = null;
      packFoil = null;
    };

    scene.addEventListener('pointerenter', updateRect);
    window.addEventListener('resize', updateRect);

    let targetPx = 0;
    let targetPy = 0;
    let currentPx = 0;
    let currentPy = 0;
    let isRunning = false;

    const animate = () => {
      if (!isRunning) return;

      const dx = targetPx - currentPx;
      const dy = targetPy - currentPy;
      currentPx += dx * 0.14;
      currentPy += dy * 0.14;

      if (this._state === 'idle') {
        for (let i = 0; i < bgLayers.length; i++) {
          const layer = bgLayers[i];
          const depth = parseFloat(layer.dataset.parallax) || 0;
          layer.style.transform = `translate3d(${(-currentPx * depth).toFixed(1)}px, ${(-currentPy * depth).toFixed(1)}px, 0)`;
        }

        if (!packStack || (typeof packStack.isConnected === 'boolean' && !packStack.isConnected)) {
          packStack = document.getElementById('pack-booster-stack');
        }
        if (!packFoil || (typeof packFoil.isConnected === 'boolean' && !packFoil.isConnected)) {
          packFoil = document.getElementById('pack-booster-foil');
        }
        if (packStack) {
          packStack.style.transform = `translate3d(${(currentPx * 14).toFixed(1)}px, ${(currentPy * 10).toFixed(1)}px, 0)`;
        }
        if (packFoil) {
          const tiltX = (3.5 - currentPy * 6).toFixed(1);
          const tiltY = (-10 + currentPx * 8).toFixed(1);
          packFoil.style.transform = `perspective(1000px) rotateX(${tiltX}deg) rotateY(${tiltY}deg)`;
          packFoil.style.setProperty('--glint-dx', `${Math.round(currentPx * 120)}px`);
          packFoil.style.setProperty('--glint-dy', `${Math.round(currentPy * 160)}px`);
        }
      }

      if (Math.abs(dx) < 0.001 && Math.abs(dy) < 0.001) {
        currentPx = targetPx;
        currentPy = targetPy;
        isRunning = false;
      } else {
        requestAnimationFrame(animate);
      }
    };

    this._resetParallaxTargets = () => {
      packStack = null;
      packFoil = null;
      cachedRect = null;
      if (this._isMobile() || this._state !== 'idle') return;
      packStack = document.getElementById('pack-booster-stack');
      packFoil = document.getElementById('pack-booster-foil');
      if (packStack) {
        packStack.style.transform = `translate3d(${(currentPx * 14).toFixed(1)}px, ${(currentPy * 10).toFixed(1)}px, 0)`;
      }
      if (packFoil) {
        const tiltX = (3.5 - currentPy * 6).toFixed(1);
        const tiltY = (-10 + currentPx * 8).toFixed(1);
        packFoil.style.transform = `perspective(1000px) rotateX(${tiltX}deg) rotateY(${tiltY}deg)`;
        packFoil.style.setProperty('--glint-dx', `${Math.round(currentPx * 120)}px`);
        packFoil.style.setProperty('--glint-dy', `${Math.round(currentPy * 160)}px`);
      }
    };

    this._stopParallax = () => {
      isRunning = false;
      packStack = null;
      packFoil = null;
    };

    scene.addEventListener('pointermove', (e) => {
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      if (this._state !== 'idle') return;
      // On mobile touch devices, skip pointermove parallax to prevent thrashing CALayers during swipes
      if (e.pointerType === 'touch' || this._isMobile() || (window.matchMedia && window.matchMedia('(pointer: coarse)').matches)) return;
      if (!cachedRect || cachedRect.width === 0) updateRect();
      targetPx = (e.clientX - cachedRect.left) / cachedRect.width - 0.5;
      targetPy = (e.clientY - cachedRect.top) / cachedRect.height - 0.5;
      if (!isRunning) {
        isRunning = true;
        requestAnimationFrame(animate);
      }
    });

    scene.addEventListener('pointerleave', (e) => {
      if (e.pointerType === 'touch' || this._isMobile() || (window.matchMedia && window.matchMedia('(pointer: coarse)').matches)) return;
      targetPx = 0;
      targetPy = 0;
      if (!isRunning && this._state === 'idle') {
        isRunning = true;
        requestAnimationFrame(animate);
      }
    });
  }

  _getPackSurfaceMarkup(packId = (this._selectedPackId || 'core_pack')) {
    if (packId === 'sul_pack') {
      return `
        <!-- CNC Precision Vector Frame: Sul Bio-Chitin Theme -->
        <svg class="pack-chassis-svg" viewBox="0 0 400 600" preserveAspectRatio="none">
          <defs>
            <linearGradient id="chassisOuterBezelSul" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stop-color="#f472b6" />
              <stop offset="25%" stop-color="#d946ef" />
              <stop offset="60%" stop-color="#86198f" />
              <stop offset="100%" stop-color="#3b0764" />
            </linearGradient>
            <radialGradient id="plateCoreGlowSul" cx="50%" cy="45%" r="60%">
              <stop offset="0%" stop-color="rgba(217, 70, 239, 0.42)" />
              <stop offset="45%" stop-color="rgba(147, 51, 234, 0.15)" />
              <stop offset="100%" stop-color="transparent" />
            </radialGradient>
          </defs>

          <!-- CNC Outer Slab Face with 24px corner fillets -->
          <rect class="pack-svg-bezel"
                x="2" y="2" width="396" height="596" rx="24" ry="24"
                fill="none"
                stroke="url(#chassisOuterBezelSul)"
                stroke-width="3" />

          <!-- Core Radial Glow -->
          <rect x="2" y="2" width="396" height="596" rx="24" ry="24"
                fill="url(#plateCoreGlowSul)" />

          <!-- Inner Recessed Groove -->
          <rect class="pack-svg-groove"
                x="12" y="12" width="376" height="576" rx="16" ry="16"
                fill="none"
                stroke="rgba(217, 70, 239, 0.55)"
                stroke-width="1.5" />
        </svg>

        <!-- Full-surface Carbon/Chitin Plate -->
        <div class="pack-plate-carbon" style="background: radial-gradient(circle at 50% 30%, #1e0b2e 0%, #0c0314 100%);"></div>

        <!-- Clean Central Seam Line -->
        <div class="pack-pressure-seal">
          <span class="seal-line-t" style="background: linear-gradient(to bottom, #f472b6, transparent);"></span>
          <span class="seal-line-b" style="background: linear-gradient(to top, #f472b6, transparent);"></span>
        </div>

        <!-- Raised 3D Insignia: Sul Fractured Brood-Pyramid Crest (Concept B) -->
        <div class="pack-crest-relief">
          <div class="crest-shield-wrap sul-crest">
            <svg class="crest-svg" viewBox="0 0 160 140">
              <defs>
                <!-- Gold/Amber metallic gradient for ancient trims & hieroglyphs -->
                <linearGradient id="crestSulGoldGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stop-color="#fef08a" />
                  <stop offset="50%" stop-color="#eab308" />
                  <stop offset="100%" stop-color="#a16207" />
                </linearGradient>

                <!-- Bio-Chitin Carapace Gradient: Magenta to Deep Eldritch Purple -->
                <linearGradient id="crestSulChitinGrad" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" stop-color="#f472b6" />
                  <stop offset="40%" stop-color="#d946ef" />
                  <stop offset="75%" stop-color="#7e22ce" />
                  <stop offset="100%" stop-color="#3b0764" />
                </linearGradient>

                <!-- Dark Basalt/Obsidian Stone Gradient for Pyramid Facets -->
                <linearGradient id="crestSulStoneLeft" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stop-color="#241038" />
                  <stop offset="60%" stop-color="#140622" />
                  <stop offset="100%" stop-color="#0a0212" />
                </linearGradient>

                <linearGradient id="crestSulStoneRight" x1="100%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" stop-color="#1a0b28" />
                  <stop offset="60%" stop-color="#0e0417" />
                  <stop offset="100%" stop-color="#06010a" />
                </linearGradient>

                <!-- Pulsing Fissure Brood Core Glow -->
                <radialGradient id="sulBroodGlow" cx="50%" cy="50%" r="50%">
                  <stop offset="0%" stop-color="#ffffff" />
                  <stop offset="25%" stop-color="#ff2a8d" />
                  <stop offset="60%" stop-color="#a21caf" />
                  <stop offset="85%" stop-color="#581c87" />
                  <stop offset="100%" stop-color="transparent" />
                </radialGradient>

                <!-- Hot Core Eye Gradient -->
                <linearGradient id="sulCoreFlame" x1="0%" y1="100%" x2="0%" y2="0%">
                  <stop offset="0%" stop-color="#a21caf" />
                  <stop offset="45%" stop-color="#f43f5e" />
                  <stop offset="80%" stop-color="#fde047" />
                  <stop offset="100%" stop-color="#ffffff" />
                </linearGradient>
              </defs>

              <!-- 1. Flanking Bio-Chitin Mandibles / Pincers (Symmetrical Left & Right) -->
              <g class="sul-crest-mandibles">
                <!-- Left Mandible (Upper sweeping arc & serrated chitin horns) -->
                <path d="M 46,28 C 30,12 16,8 6,4 C 10,18 20,38 34,54 C 20,44 10,40 2,42 C 12,56 22,72 32,84 C 24,80 18,80 12,84 C 22,96 34,106 46,112 Z" 
                      fill="url(#crestSulChitinGrad)" opacity="0.95" />
                <path d="M 44,32 C 32,20 22,18 12,14 C 18,24 26,40 36,52 Z" 
                      fill="url(#crestSulGoldGrad)" opacity="0.8" />

                <!-- Right Mandible (Mirror) -->
                <path d="M 114,28 C 130,12 144,8 154,4 C 150,18 140,38 126,54 C 140,44 150,40 158,42 C 148,56 138,72 128,84 C 136,80 142,80 148,84 C 138,96 126,106 114,112 Z" 
                      fill="url(#crestSulChitinGrad)" opacity="0.95" />
                <path d="M 116,32 C 128,20 138,18 148,14 C 142,24 134,40 124,52 Z" 
                      fill="url(#crestSulGoldGrad)" opacity="0.8" />
              </g>

              <!-- 2. The Pyramid Monolith: Left and Right Stone Wings -->
              <!-- Outer Gold/Magenta Frame of the Pyramid -->
              <polygon points="80,10 126,116 34,116" 
                       fill="#160824" 
                       stroke="url(#crestSulChitinGrad)" 
                       stroke-width="2.5" 
                       stroke-linejoin="round" />

              <!-- Left Facet Slab -->
              <path d="M 80,12 L 36,114 L 75,114 L 73,88 L 68,76 L 75,60 L 71,44 L 79,28 Z" 
                    fill="url(#crestSulStoneLeft)" 
                    stroke="rgba(217, 70, 239, 0.4)" 
                    stroke-width="1" />

              <!-- Right Facet Slab -->
              <path d="M 80,12 L 124,114 L 85,114 L 87,88 L 92,76 L 85,60 L 89,44 L 81,28 Z" 
                    fill="url(#crestSulStoneRight)" 
                    stroke="rgba(217, 70, 239, 0.4)" 
                    stroke-width="1" />

              <!-- Stepped Masonry Grooves (horizontal lines on slabs) -->
              <line x1="48" y1="92" x2="73" y2="92" stroke="rgba(217, 70, 239, 0.35)" stroke-width="1" />
              <line x1="87" y1="92" x2="112" y2="92" stroke="rgba(217, 70, 239, 0.35)" stroke-width="1" />
              <line x1="56" y1="68" x2="71" y2="68" stroke="rgba(217, 70, 239, 0.35)" stroke-width="1" />
              <line x1="89" y1="68" x2="104" y2="68" stroke="rgba(217, 70, 239, 0.35)" stroke-width="1" />
              <line x1="66" y1="44" x2="74" y2="44" stroke="rgba(217, 70, 239, 0.35)" stroke-width="1" />
              <line x1="86" y1="44" x2="94" y2="44" stroke="rgba(217, 70, 239, 0.35)" stroke-width="1" />

              <!-- Pyramidion Capstone (Solid Golden/Purple Top) -->
              <polygon points="80,12 87,28 73,28" 
                       fill="url(#crestSulGoldGrad)" 
                       stroke="#f472b6" 
                       stroke-width="1.2" />

              <!-- 3. Left Hieroglyphic Cartouche Column (Sul Castes) -->
              <g class="sul-glyph" opacity="0.92">
                <!-- Vulture / Hawk silhouette (𓅃) at Y=48-56 -->
                <path d="M 52,50 Q 56,47 60,49 Q 63,52 61,56 Q 58,58 53,56 L 50,58 L 52,54 Z" fill="url(#crestSulGoldGrad)" />
                <!-- Pillar (𓊽 Djed) at Y=70-80 -->
                <line x1="57" y1="71" x2="57" y2="82" stroke="url(#crestSulGoldGrad)" stroke-width="1.8" />
                <line x1="53" y1="73" x2="61" y2="73" stroke="url(#crestSulGoldGrad)" stroke-width="1.4" />
                <line x1="53" y1="76" x2="61" y2="76" stroke="url(#crestSulGoldGrad)" stroke-width="1.4" />
                <line x1="53" y1="79" x2="61" y2="79" stroke="url(#crestSulGoldGrad)" stroke-width="1.4" />
                <!-- Bread Loaf (𓏏) at Y=96-104 -->
                <path d="M 48,103 A 8,8 0 0,1 64,103 Z" fill="#3b0764" stroke="url(#crestSulGoldGrad)" stroke-width="1.4" />
                <line x1="46" y1="105" x2="66" y2="105" stroke="url(#crestSulGoldGrad)" stroke-width="1.6" />
              </g>

              <!-- 4. Right Hieroglyphic Cartouche Column (Sul Castes & Brood) -->
              <g class="sul-glyph" opacity="0.92">
                <!-- Scarab Beetle (𓆣) at Y=48-56 -->
                <ellipse cx="102" cy="53" rx="4.5" ry="5.5" fill="url(#crestSulGoldGrad)" />
                <path d="M 98,49 Q 95,46 96,44 M 106,49 Q 109,46 108,44" stroke="url(#crestSulGoldGrad)" stroke-width="1.2" fill="none" />
                <!-- Lock / Bolt (𓊃) at Y=72-78 -->
                <path d="M 96,73 L 108,73 L 108,77 L 96,77 Z" fill="none" stroke="url(#crestSulGoldGrad)" stroke-width="1.4" />
                <circle cx="102" cy="75" r="1.5" fill="url(#crestSulGoldGrad)" />
                <!-- Loaf / Eye (𓏏) at Y=96-104 -->
                <path d="M 95,103 A 8,8 0 0,1 111,103 Z" fill="#3b0764" stroke="url(#crestSulGoldGrad)" stroke-width="1.4" />
                <line x1="93" y1="105" x2="113" y2="105" stroke="url(#crestSulGoldGrad)" stroke-width="1.6" />
              </g>

              <!-- 5. Central Ruptured Fissure & Glowing Brood Core -->
              <!-- Dark void cavity inside the rupture -->
              <path d="M 79,28 L 71,44 L 75,60 L 68,76 L 73,88 L 75,114 L 85,114 L 87,88 L 92,76 L 85,60 L 89,44 L 81,28 Z" 
                    fill="#0d0218" />

              <!-- Background Pulsing Brood Glow -->
              <circle cx="80" cy="68" r="22" fill="url(#sulBroodGlow)" opacity="0.9" class="sul-fissure-glow" />

              <!-- Bio-Organic Sinew Strands (Fibers holding the cracked stone) -->
              <g class="sul-sinews" opacity="0.85">
                <path d="M 69,52 Q 78,56 89,48" stroke="#f472b6" stroke-width="1.6" fill="none" />
                <path d="M 72,62 Q 82,65 91,59" stroke="#d946ef" stroke-width="1.4" fill="none" />
                <path d="M 68,74 Q 80,72 92,78" stroke="#f472b6" stroke-width="1.6" fill="none" />
                <path d="M 72,86 Q 79,83 88,89" stroke="#d946ef" stroke-width="1.4" fill="none" />
                <path d="M 74,102 Q 80,98 86,104" stroke="#c084fc" stroke-width="1.2" fill="none" />
              </g>

              <!-- Pulsing Bio-Plasma Brood Egg / Core -->
              <g class="sul-core-egg">
                <!-- Organic Egg Contour -->
                <path d="M 80,52 C 87,52 92,61 90,73 C 88,82 84,86 80,86 C 76,86 72,82 70,73 C 68,61 73,52 80,52 Z" 
                      fill="url(#sulCoreFlame)" />
                <!-- Vertical Slit Eye / Embryo Pupil -->
                <ellipse cx="80" cy="69" rx="2.2" ry="7.5" fill="#ffffff" />
                <circle cx="80" cy="69" r="1.4" fill="#ff007f" />
              </g>

              <!-- 6. Basal Bio-Tendrils / Root Claws (Crawling from bottom) -->
              <g class="sul-root-tendrils">
                <path d="M 54,115 Q 42,126 28,124 Q 38,120 50,116 Z" fill="url(#crestSulChitinGrad)" opacity="0.9" />
                <path d="M 70,115 Q 60,132 44,136 Q 56,128 68,116 Z" fill="url(#crestSulChitinGrad)" opacity="0.85" />
                <path d="M 106,115 Q 118,126 132,124 Q 122,120 110,116 Z" fill="url(#crestSulChitinGrad)" opacity="0.9" />
                <path d="M 90,115 Q 100,132 116,136 Q 104,128 92,116 Z" fill="url(#crestSulChitinGrad)" opacity="0.85" />
                <!-- Center drip/root -->
                <path d="M 78,114 Q 80,128 79,134 Q 82,128 82,114 Z" fill="url(#crestSulGoldGrad)" opacity="0.8" />
              </g>
            </svg>
          </div>

          <!-- Embossed Typographic Relief -->
          <div class="crest-typography">
            <span class="pack-word" style="color: #f472b6; text-shadow: 0 0 12px #d946ef;">SUL</span>
            <span class="pack-sub" style="color: #e879f9;">SWARM BIO-CHITIN</span>
            <div class="pack-badge-row">
              <span class="pack-badge-pill" style="border-color: #d946ef; color: #f472b6;">𓆣 BROOD PYRAMID</span>
              <span class="pack-badge-spec">5 CARDS</span>
            </div>
          </div>
        </div>
      `;
    }

    if (packId === 'human_pack') {
      return `
        <!-- CNC Precision Vector Frame: Human Vanguard Theme -->
        <svg class="pack-chassis-svg" viewBox="0 0 400 600" preserveAspectRatio="none">
          <defs>
            <linearGradient id="chassisOuterBezelHuman" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stop-color="#38bdf8" />
              <stop offset="25%" stop-color="#0284c7" />
              <stop offset="60%" stop-color="#034568" />
              <stop offset="100%" stop-color="#041b29" />
            </linearGradient>
            <radialGradient id="plateCoreGlowHuman" cx="50%" cy="45%" r="60%">
              <stop offset="0%" stop-color="rgba(0, 229, 255, 0.38)" />
              <stop offset="45%" stop-color="rgba(2, 132, 199, 0.12)" />
              <stop offset="100%" stop-color="transparent" />
            </radialGradient>
          </defs>

          <!-- CNC Outer Slab Face with 24px corner fillets -->
          <rect class="pack-svg-bezel"
                x="2" y="2" width="396" height="596" rx="24" ry="24"
                fill="none"
                stroke="url(#chassisOuterBezelHuman)"
                stroke-width="3" />

          <!-- Core Radial Glow -->
          <rect x="2" y="2" width="396" height="596" rx="24" ry="24"
                fill="url(#plateCoreGlowHuman)" />

          <!-- Inner Recessed Groove -->
          <rect class="pack-svg-groove"
                x="12" y="12" width="376" height="576" rx="16" ry="16"
                fill="none"
                stroke="rgba(0, 229, 255, 0.5)"
                stroke-width="1.5" />
        </svg>

        <div class="pack-plate-carbon"></div>

        <div class="pack-pressure-seal">
          <span class="seal-line-t"></span>
          <span class="seal-line-b"></span>
        </div>

        <div class="pack-crest-relief">
          <div class="crest-shield-wrap">
            <svg class="crest-svg" viewBox="0 0 160 140">
              <defs>
                <linearGradient id="crestGoldGradH" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stop-color="#fef08a" />
                  <stop offset="30%" stop-color="#eab308" />
                  <stop offset="70%" stop-color="#ca8a04" />
                  <stop offset="100%" stop-color="#713f12" />
                </linearGradient>
                <linearGradient id="crestWingGradH" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" stop-color="#38bdf8" />
                  <stop offset="50%" stop-color="#0284c7" />
                  <stop offset="100%" stop-color="#082f49" />
                </linearGradient>
                <radialGradient id="plasmaGlowH" cx="50%" cy="50%" r="50%">
                  <stop offset="0%" stop-color="#ffffff" />
                  <stop offset="35%" stop-color="#00e5ff" />
                  <stop offset="75%" stop-color="#0284c7" />
                  <stop offset="100%" stop-color="transparent" />
                </radialGradient>
              </defs>

              <!-- Flanking Aerospace Wing Chevrons -->
              <g class="crest-wings">
                <path d="M 44,48 L 10,64 L 10,72 L 44,60 Z" fill="url(#crestWingGradH)" opacity="0.9" />
                <path d="M 44,65 L 16,80 L 16,87 L 44,76 Z" fill="url(#crestWingGradH)" opacity="0.8" />
                <path d="M 44,81 L 24,94 L 24,100 L 44,91 Z" fill="url(#crestWingGradH)" opacity="0.7" />
                <path d="M 116,48 L 150,64 L 150,72 L 116,60 Z" fill="url(#crestWingGradH)" opacity="0.9" />
                <path d="M 116,65 L 144,80 L 144,87 L 116,76 Z" fill="url(#crestWingGradH)" opacity="0.8" />
                <path d="M 116,81 L 136,94 L 136,100 L 116,91 Z" fill="url(#crestWingGradH)" opacity="0.7" />
              </g>

              <polygon points="80,8 126,34 126,88 80,114 34,88 34,34"
                       fill="#081724"
                       stroke="url(#crestGoldGradH)"
                       stroke-width="3"
                       stroke-linejoin="round" />

              <polygon points="80,16 118,38 118,84 80,106 42,84 42,38"
                       fill="#0f2334"
                       stroke="rgba(0, 229, 255, 0.45)"
                       stroke-width="1.5" />

              <circle cx="80" cy="61" r="22" fill="url(#plasmaGlowH)" opacity="0.9" />
              <polygon points="80,48 91,55 91,67 80,74 69,67 69,55"
                       fill="none"
                       stroke="#ffffff"
                       stroke-width="1.8" />
              <circle cx="80" cy="61" r="4.5" fill="#ffffff" />
            </svg>
          </div>

          <div class="crest-typography">
            <span class="pack-word">MARINE</span>
            <span class="pack-sub">HUMAN FORCES</span>
            <div class="pack-badge-row">
              <span class="pack-badge-pill">TERRAN MIL</span>
              <span class="pack-badge-spec">5 CARDS</span>
            </div>
          </div>
        </div>
      `;
    }

    // Default: core_pack (Option 3: Heavy Cargo Vault — Tungsten & Hazard Theme)
    return `
      <!-- CNC Precision Vector Frame: Heavy Cargo Vault Theme (Tungsten & Hazard) -->
      <svg class="pack-chassis-svg" viewBox="0 0 400 600" preserveAspectRatio="none">
        <defs>
          <!-- Industrial Hazard Diagonal Stripes -->
          <pattern id="hazardStripesCore" width="24" height="24" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
            <rect width="12" height="24" fill="#f59e0b" />
            <rect x="12" width="12" height="24" fill="#0f172a" />
          </pattern>

          <linearGradient id="chassisOuterTungsten" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stop-color="#94a3b8" />
            <stop offset="20%" stop-color="#64748b" />
            <stop offset="55%" stop-color="#334155" />
            <stop offset="100%" stop-color="#0f172a" />
          </linearGradient>

          <radialGradient id="plateCoreGlowVault" cx="50%" cy="45%" r="60%">
            <stop offset="0%" stop-color="rgba(245, 158, 11, 0.35)" />
            <stop offset="45%" stop-color="rgba(217, 119, 6, 0.12)" />
            <stop offset="100%" stop-color="transparent" />
          </radialGradient>
        </defs>

        <!-- Outer Heavy Steel Slab Face with 20px corner fillets -->
        <rect class="pack-svg-bezel"
              x="2" y="2" width="396" height="596" rx="20" ry="20"
              fill="none"
              stroke="url(#chassisOuterTungsten)"
              stroke-width="3.5" />

        <!-- Core Radial Amber Pressure Glow -->
        <rect x="2" y="2" width="396" height="596" rx="20" ry="20"
              fill="url(#plateCoreGlowVault)" />

        <!-- Top Hazard Caution Band -->
        <rect x="14" y="14" width="372" height="14" rx="4" ry="4"
              fill="url(#hazardStripesCore)" opacity="0.85" stroke="#f59e0b" stroke-width="1" />

        <!-- Bottom Hazard Caution Band -->
        <rect x="14" y="572" width="372" height="14" rx="4" ry="4"
              fill="url(#hazardStripesCore)" opacity="0.85" stroke="#f59e0b" stroke-width="1" />

        <!-- Inner Recessed Armor Groove -->
        <rect class="pack-svg-groove"
              x="14" y="34" width="372" height="532" rx="14" ry="14"
              fill="none"
              stroke="rgba(245, 158, 11, 0.5)"
              stroke-width="1.5" />

        <!-- Reinforced Industrial Hex Bolts (4 Corners + Mid Flanges) -->
        <g fill="#64748b" stroke="#f59e0b" stroke-width="1.2">
          <circle cx="28" cy="48" r="4.5" />
          <circle cx="372" cy="48" r="4.5" />
          <circle cx="28" cy="552" r="4.5" />
          <circle cx="372" cy="552" r="4.5" />
          <circle cx="28" cy="300" r="4" />
          <circle cx="372" cy="300" r="4" />
        </g>
      </svg>

      <!-- Full-surface Ballistic Tungsten Armor Plate -->
      <div class="pack-plate-carbon" style="background: radial-gradient(circle at 50% 35%, #1e293b 0%, #0f172a 65%, #080d14 100%);"></div>

      <!-- Clean Central Seam Line with Amber Laser Seal -->
      <div class="pack-pressure-seal">
        <span class="seal-line-t" style="background: linear-gradient(180deg, #f59e0b, transparent); box-shadow: 0 0 6px #f59e0b;"></span>
        <span class="seal-line-b" style="background: linear-gradient(0deg, #f59e0b, transparent); box-shadow: 0 0 6px #f59e0b;"></span>
      </div>

      <!-- Raised 3D Heavy Cargo Vault Insignia: Armored Gyro-Sphere (Kugel) -->
      <div class="pack-crest-relief">
        <div class="crest-shield-wrap vault-crest">
          <svg class="crest-svg" viewBox="0 0 160 140">
            <defs>
              <!-- 3D Spherical Volume Radial Gradient: Key light top-left, deep shadow bottom-right -->
              <radialGradient id="sphereVolGrad" cx="35%" cy="28%" fx="26%" fy="20%" r="72%">
                <stop offset="0%" stop-color="#ffffff" />
                <stop offset="10%" stop-color="#fef3c7" />
                <stop offset="26%" stop-color="#f59e0b" />
                <stop offset="48%" stop-color="#92400e" />
                <stop offset="70%" stop-color="#1e293b" />
                <stop offset="90%" stop-color="#0f172a" />
                <stop offset="100%" stop-color="#020617" />
              </radialGradient>

              <!-- Spherical Rim Glow: Amber backlighting along the sphere curvature -->
              <radialGradient id="sphereRimGrad" cx="50%" cy="50%" r="50%">
                <stop offset="76%" stop-color="transparent" />
                <stop offset="92%" stop-color="rgba(245, 158, 11, 0.45)" />
                <stop offset="100%" stop-color="rgba(251, 191, 36, 0.85)" />
              </radialGradient>

              <!-- Heavy Industrial Gimbal Ring Gradient -->
              <linearGradient id="sphereGimbalGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stop-color="#cbd5e1" />
                <stop offset="25%" stop-color="#64748b" />
                <stop offset="50%" stop-color="#f59e0b" />
                <stop offset="75%" stop-color="#334155" />
                <stop offset="100%" stop-color="#0f172a" />
              </linearGradient>

              <!-- Heavy Titanium Clamp Gradient -->
              <linearGradient id="sphereClampGrad" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stop-color="#94a3b8" />
                <stop offset="50%" stop-color="#475569" />
                <stop offset="100%" stop-color="#1e293b" />
              </linearGradient>

              <!-- Central Singularity Core Pulse Glow -->
              <radialGradient id="sphereCoreGlow" cx="50%" cy="50%" r="50%">
                <stop offset="0%" stop-color="#ffffff" />
                <stop offset="25%" stop-color="#fef08a" />
                <stop offset="55%" stop-color="#f59e0b" />
                <stop offset="80%" stop-color="#b45309" />
                <stop offset="100%" stop-color="transparent" />
              </radialGradient>

              <linearGradient id="vaultGoldGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stop-color="#fef08a" />
                <stop offset="35%" stop-color="#f59e0b" />
                <stop offset="75%" stop-color="#d97706" />
                <stop offset="100%" stop-color="#78350f" />
              </linearGradient>
            </defs>

            <!-- 1. Flanking Heavy Containment Pylons / Magnetic Calipers (Grip the Gimbal) -->
            <g class="crest-pylons">
              <path d="M 6,56 L 22,56 L 26,64 L 22,80 L 6,80 L 10,68 Z" fill="url(#sphereClampGrad)" stroke="#f59e0b" stroke-width="1.4" />
              <path d="M 154,56 L 138,56 L 134,64 L 138,80 L 154,80 L 150,68 Z" fill="url(#sphereClampGrad)" stroke="#f59e0b" stroke-width="1.4" />
              <!-- Hydraulic rivets on pylons -->
              <circle cx="13" cy="68" r="2.5" fill="#f59e0b" />
              <circle cx="147" cy="68" r="2.5" fill="#f59e0b" />
            </g>

            <!-- 2. Gyroscopic Orbital Ring — REAR ARC (Passes behind the sphere) -->
            <path d="M 22,68 C 22,40 50,30 80,30 C 110,30 138,40 138,68 L 133,68 C 133,43 108,35 80,35 C 52,35 27,43 27,68 Z"
                  fill="url(#sphereGimbalGrad)" opacity="0.65" stroke="rgba(245, 158, 11, 0.5)" stroke-width="1" />

            <!-- 3. THE MAIN SPHERICAL BODY (Die Kugel) -->
            <!-- Volumetric Sphere Base -->
            <circle cx="80" cy="68" r="46" fill="url(#sphereVolGrad)" />
            <!-- Spherical Rim Glow -->
            <circle cx="80" cy="68" r="46" fill="url(#sphereRimGrad)" />
            <!-- Outer Fine Golden Bezel Ring -->
            <circle cx="80" cy="68" r="46" fill="none" stroke="url(#vaultGoldGrad)" stroke-width="1.8" />

            <!-- 4. Spherical Topology: Latitude & Longitude Curvature (Unmistakable 3D Kugel) -->
            <g class="sphere-grid" opacity="0.85">
              <!-- Upper Latitude Band (North Polar Circle) -->
              <path d="M 52,41 A 38,13 0 0,0 108,41" fill="none" stroke="rgba(254, 240, 138, 0.45)" stroke-width="1.2" stroke-dasharray="3 2" />

              <!-- Lower Latitude Band (South Polar Circle) -->
              <path d="M 50,94 A 38,13 0 0,0 110,94" fill="none" stroke="rgba(245, 158, 11, 0.35)" stroke-width="1.2" stroke-dasharray="3 2" />

              <!-- Longitudinal Meridian: West Arc -->
              <path d="M 80,22 C 54,40 54,96 80,114" fill="none" stroke="rgba(245, 158, 11, 0.35)" stroke-width="1" />

              <!-- Longitudinal Meridian: East Arc -->
              <path d="M 80,22 C 106,40 106,96 80,114" fill="none" stroke="rgba(245, 158, 11, 0.45)" stroke-width="1" />

              <!-- Longitudinal Meridian: Prime Center Line -->
              <path d="M 80,22 L 80,114" fill="none" stroke="rgba(254, 243, 199, 0.5)" stroke-width="1.2" />

              <!-- Glowing Amber Equator Trench (Deep 3D perspective slit) -->
              <path d="M 34,68 A 46,17 0 0,0 126,68" fill="none" stroke="#92400e" stroke-width="4.5" />
              <path d="M 34,68 A 46,17 0 0,0 126,68" fill="none" stroke="#f59e0b" stroke-width="2.5" class="vault-equator-pulse" />
              <path d="M 35,68 A 46,17 0 0,0 125,68" fill="none" stroke="#ffffff" stroke-width="1" opacity="0.85" />
            </g>

            <!-- 5. Spherical Surface Specular Glint & Reflection Arc -->
            <!-- Curved Crescent Reflection (Top-Left spherical curvature) -->
            <path d="M 48,40 C 56,30 70,25 80,24 C 72,26 60,32 54,40 Z" fill="#ffffff" opacity="0.6" />
            <ellipse cx="60" cy="44" rx="10" ry="5" transform="rotate(-30 60 44)" fill="#ffffff" opacity="0.3" />
            <circle cx="56" cy="40" r="3" fill="#ffffff" opacity="0.85" />

            <!-- 6. Central Singularity / Spherical Iris Core -->
            <g class="sphere-core vault-sphere-core">
              <!-- Outer Locking Collar -->
              <circle cx="80" cy="72" r="15" fill="#0f172a" stroke="url(#vaultGoldGrad)" stroke-width="2" />
              <!-- Stepped Armor Ring -->
              <circle cx="80" cy="72" r="12" fill="#1e293b" stroke="rgba(245, 158, 11, 0.7)" stroke-width="1.2" />
              <!-- Singularity Radiant Pulse -->
              <circle cx="80" cy="72" r="9" fill="url(#sphereCoreGlow)" />
              <!-- Singularity Point Node -->
              <circle cx="80" cy="72" r="3.2" fill="#ffffff" />
            </g>

            <!-- 7. Gyroscopic Orbital Ring — FOREGROUND ARC (Passes in front of the sphere) -->
            <g class="crest-front-gimbal">
              <!-- Thick Outer Beveled Ring Arc -->
              <path d="M 22,68 C 22,96 50,110 80,110 C 110,110 138,96 138,68 L 132,68 C 132,92 106,104 80,104 C 54,104 28,92 28,68 Z"
                    fill="url(#sphereGimbalGrad)" stroke="#f59e0b" stroke-width="1.4" />
              <!-- Glowing Accent Ridge on Front Ring -->
              <path d="M 36,82 C 48,98 64,105 80,105 C 96,105 112,98 124,82"
                    fill="none" stroke="rgba(254, 240, 138, 0.7)" stroke-width="1.2" />

              <!-- Left Gimbal Swivel Bearing -->
              <circle cx="24" cy="68" r="4.5" fill="#1e293b" stroke="#f59e0b" stroke-width="1.5" />
              <circle cx="24" cy="68" r="2" fill="#fbbf24" />

              <!-- Right Gimbal Swivel Bearing -->
              <circle cx="136" cy="68" r="4.5" fill="#1e293b" stroke="#f59e0b" stroke-width="1.5" />
              <circle cx="136" cy="68" r="2" fill="#fbbf24" />
            </g>
          </svg>
        </div>

        <!-- Stenciled Industrial Typographic Relief -->
        <div class="crest-typography">
          <span class="pack-word" style="background: linear-gradient(180deg, #ffffff 0%, #fef3c7 35%, #f59e0b 70%, #b45309 100%); -webkit-background-clip: text; -webkit-text-fill-color: transparent; filter: drop-shadow(0 2px 4px rgba(0,0,0,0.9)) drop-shadow(0 0 12px rgba(245, 158, 11, 0.6));">UCG</span>
          <span class="pack-sub" style="color: #fbbf24; text-shadow: 0 0 10px rgba(245, 158, 11, 0.8);">HEAVY CARGO VAULT</span>
          <div class="pack-badge-row">
            <span class="pack-badge-pill" style="border-color: #f59e0b; color: #fbbf24; background: rgba(245, 158, 11, 0.16); text-shadow: 0 0 6px rgba(245, 158, 11, 0.6);">${getIconSvg('warning', 12)} SECURE ORDNANCE</span>
            <span class="pack-badge-spec">5 CARDS</span>
          </div>
        </div>
      </div>
    `;
  }

  _renderMiniPackLogo(packId) {
    if (packId === 'core_pack') {
      return `
        <svg class="cargo-tab-mini-logo core" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="9" cy="9" r="8" stroke="#f59e0b" stroke-width="1.1" stroke-dasharray="2.5 1.5" opacity="0.6"/>
          <circle cx="9" cy="9" r="6.2" fill="#0f172a" stroke="#d97706" stroke-width="1.3"/>
          <ellipse cx="9" cy="9" rx="6.2" ry="2.2" stroke="#fbbf24" stroke-width="1.1" fill="none"/>
          <circle cx="9" cy="9" r="2.2" fill="#fef08a" stroke="#b45309" stroke-width="0.8"/>
          <circle cx="9" cy="9" r="0.8" fill="#ffffff"/>
        </svg>
      `.trim();
    }
    if (packId === 'human_pack') {
      return `
        <svg class="cargo-tab-mini-logo marine" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M5 6.5 L1.5 8.5 L1.5 10 L5 9 Z" fill="#0284c7" opacity="0.85"/>
          <path d="M13 6.5 L16.5 8.5 L16.5 10 L13 9 Z" fill="#0284c7" opacity="0.85"/>
          <polygon points="9,1.5 14.5,4.5 14.5,11.5 9,15.5 3.5,11.5 3.5,4.5" fill="#081724" stroke="#38bdf8" stroke-width="1.3" stroke-linejoin="round"/>
          <polygon points="9,3.8 12.5,5.8 12.5,10.6 9,13.2 5.5,10.6 5.5,5.8" fill="#0c2336" stroke="rgba(0, 229, 255, 0.5)" stroke-width="0.8"/>
          <circle cx="9" cy="8.6" r="2.5" fill="#00e5ff" opacity="0.95"/>
          <circle cx="9" cy="8.6" r="1" fill="#ffffff"/>
        </svg>
      `.trim();
    }
    if (packId === 'sul_pack') {
      return `
        <svg class="cargo-tab-mini-logo swarm" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M5.5 14 C4 15.5 2 16 1.5 15" stroke="#d946ef" stroke-width="1" stroke-linecap="round"/>
          <path d="M12.5 14 C14 15.5 16 16 16.5 15" stroke="#d946ef" stroke-width="1" stroke-linecap="round"/>
          <polygon points="9,1.8 15,5.6 15,12 9,16 3,12 3,5.6" fill="#180424" stroke="#d946ef" stroke-width="1.3" stroke-linejoin="round"/>
          <polygon points="9,4.5 12.6,7 12.6,11 9,13.6 5.4,11 5.4,7" fill="#2e0840" stroke="#a855f7" stroke-width="0.8"/>
          <ellipse cx="9" cy="9" rx="2.2" ry="3.5" fill="#ec4899"/>
          <ellipse cx="9" cy="9" rx="0.8" ry="2" fill="#ffffff"/>
        </svg>
      `.trim();
    }
    return '';
  }

  open() {
    if (!this.modalEl) return;
    this.game?.pauseForMenu?.('cargoBay');
    // Tier 1 cleanup: shed match-only memory, drop prewarmed thumbs, and unpin warmed unit audio
    this.game?.releaseMenuCaches?.();
    this.game?.releaseMatchMemory?.();
    const audio = this.audio || this.game?.audio;
    audio?.trimSampleCache?.(this._isMobile() ? 12 : 24);
    audio?.preloadCargoBaySamples?.();
    this._syncMobileState();
    this.modalEl.classList.remove('hidden');
    this.modalEl.classList.add('active');
    hubNavigator.setActiveDeck('cargo');
    this._setupCanvases();
    this._cachedPackCenter = null;
    this._resetParallaxTargets?.();
    this._startQuestSlotTimer();
    this._state = 'idle';
    this.renderBuyStage();
  }

  close() {
    if (!this.modalEl) return;
    const wasOpen = this.isOpen();
    this.modalEl.classList.remove('active');
    this.modalEl.classList.add('hidden');
    this._stopQuestSlotTimer();
    this._cleanupDragListeners();
    this._stopParallax?.();
    this._resetParallaxTargets?.();
    this._clearTimeouts();
    this._resetWarp();
    this._stopVfxLoop();
    this._isAutoRevealing = false;
    this._goldAscendEndTime = 0;
    this._cachedPackCenter = null;
    this.sparks = [];
    this.giantArcs = [];
    if (this.synthAudioCtx && this.synthAudioCtx.state === 'running') {
      this.synthAudioCtx.suspend?.().catch?.(() => {});
    }
    this._state = 'idle';
    packs.flushNotifications();
    this._currentPackResult = null;
    this._clearPredecodedArt();
    this._predecodedArt = null;
    this._predecodePromise = null;
    // The reveal view (card art <img>s, fan-deal DOM, leftover FX) is torn down immediately
    // upon closing to free decoded GPU textures and avoid memory pressure during slot spins
    // or deck navigation on mobile devices.
    if (this.stageContainer) {
      const imgs = this.stageContainer.querySelectorAll('img');
      imgs.forEach(img => {
        img.onload = null;
        img.onerror = null;
        img.src = '';
      });
      this.stageContainer.innerHTML = '';
    }
    this.stageContainer?.classList.remove('stage-booster');
    document.getElementById('cargo-scene')?.classList.remove('tearing-active');
    document.getElementById('cargo-scene')?.classList.remove('cards-revealed');
    document.getElementById('cargo-vignette')?.classList.remove('active');
    document.getElementById('cargo-surge-bloom')?.classList.remove('active');
    document.getElementById('cargo-surge-burst')?.classList.remove('active');
    document.getElementById('cargo-surge-ring')?.classList.remove('active');
    document.getElementById('cargo-surge-blast')?.classList.remove('active');
    document.getElementById('pack-hearthstone-rays')?.classList.remove('active');
    document.querySelectorAll('.pack-charge-container, .pack-vent-container, .pack-shatter-container').forEach(el => el.remove());
    if (this._prebakedShatterContainer) {
      this._prebakedShatterContainer.remove();
      this._prebakedShatterContainer = null;
    }
    const wrapper = document.querySelector('.pack-booster-wrapper');
    if (wrapper) wrapper.classList.remove('gripped', 'locked');
    const stack = document.getElementById('pack-booster-stack');
    if (stack) {
      stack.classList.remove('locking-in', 'copper-linger', 'zappel-stage-1', 'zappel-stage-2', 'zappel-stage-3', 'zappel-recoil', 'overcharging', 'critical-mass', 'bursting', 'tearing');
    }
    if (wasOpen) {
      this.game?.resumeForMenu?.('cargoBay');
    }
  }

  isOpen() {
    return this.modalEl && this.modalEl.classList.contains('active');
  }

  _getQuestSlotHtml() {
    if (directives.hasOpenQuests()) {
      return `<button type="button" class="empty-wireframe-reason empty-quest-btn" id="empty-quest-slot" data-mode="quests">DAILY QUESTS</button>`;
    }
    const { hours, mins, secs } = directives.getResetCountdown();
    return `<span class="empty-wireframe-reason empty-quest-timer" id="empty-quest-slot" data-mode="timer">${hours}:${mins}:${secs}</span>`;
  }

  _startQuestSlotTimer() {
    this._stopQuestSlotTimer();
    this._syncQuestSlot();
    this._questSlotTimer = setInterval(() => this._syncQuestSlot(), 1000);
  }

  _stopQuestSlotTimer() {
    if (this._questSlotTimer) {
      clearInterval(this._questSlotTimer);
      this._questSlotTimer = null;
    }
  }

  _cleanupDragListeners() {
    if (this._onCardsRowMouseMove) {
      window.removeEventListener('mousemove', this._onCardsRowMouseMove);
      this._onCardsRowMouseMove = null;
    }
    if (this._onCardsRowMouseUp) {
      window.removeEventListener('mouseup', this._onCardsRowMouseUp);
      this._onCardsRowMouseUp = null;
    }
  }

  // Keeps the empty-pedestal quest slot current (timer tick, quest state change)
  _syncQuestSlot() {
    if (!this.isOpen()) return;
    const slot = document.getElementById('empty-quest-slot');
    if (!slot) return;
    const mode = directives.hasOpenQuests() ? 'quests' : 'timer';
    if (slot.dataset.mode !== mode) {
      slot.outerHTML = this._getQuestSlotHtml();
      this._bindQuestSlot(document.querySelector('.pack-booster-wrapper'));
    } else if (mode === 'timer') {
      const { hours, mins, secs } = directives.getResetCountdown();
      slot.textContent = `${hours}:${mins}:${secs}`;
    }
  }

  _bindQuestSlot(wrapper) {
    const btn = wrapper?.querySelector('#empty-quest-slot[data-mode="quests"]');
    if (!btn) return;
    btn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.game?.directivesModal?.open();
    });
  }

  _getPackWrapperHtml(packId = (this._selectedPackId || 'core_pack'), canOpen = true, slidePrepClass = '') {
    const packDef = PACK_DEFINITIONS[packId] || PACK_DEFINITIONS.core_pack;
    const fugazi = wallet.getFugazi();
    const surfaceHtml = this._getPackSurfaceMarkup(packId);
    const themeClass = packId === 'sul_pack' ? 'theme-sul' : (packId === 'human_pack' ? 'theme-human' : 'theme-core');
    const maxPacks = this._getMaxAffordablePacks();
    const count = Math.max(1, Math.min(this._selectedPackCount || 1, maxPacks || 1));

    return `
      <div class="pack-booster-wrapper ${canOpen ? 'has-pack' : 'empty-state'} ${slidePrepClass}">
        ${canOpen ? `
          <!-- Hearthstone God-Rays (Dynamic per-pack theme glow) -->
          <div class="pack-hearthstone-rays ${themeClass}" id="pack-hearthstone-rays"></div>
          <!-- Clickable 3D Pack: fully CSS/SVG drawn (no PNG) with genuine 3D slab thickness (UFL style) -->
          <div class="pack-booster-stack clickable ${themeClass}" id="pack-booster-stack" role="button" tabindex="0" title="Tap to tear open">
            <!-- Multi-pack lightweight CSS underlay (packs 2..5) -->
            <div class="pack-stack-underlay ${themeClass}" id="pack-stack-underlay" data-stack-count="${count}">
              <div class="pack-stack-plate plate-1"></div>
              <div class="pack-stack-plate plate-2"></div>
              <div class="pack-stack-plate plate-3"></div>
              <div class="pack-stack-plate plate-4"></div>
            </div>

            <!-- Multi-Pack Badge (visible when count > 1) -->
            <div class="pack-count-badge ${themeClass} ${count > 1 ? 'show' : ''}" id="pack-count-badge">${count > 1 ? count + 'x' : ''}</div>

            <div class="pack-booster-foil" id="pack-booster-foil">
              <!-- Physical 3D Extruded Chassis & Walls (slab thickness visible in perspective tilt) -->
              <div class="pack-slab-depth">
                <div class="pack-chassis-back"></div>
                <div class="pack-chassis-slice pack-chassis-slice-1"></div>
                <div class="pack-chassis-slice pack-chassis-slice-2"></div>
                <div class="pack-side pack-side-l"></div>
                <div class="pack-side pack-side-r"></div>
                <div class="pack-side pack-side-t"></div>
                <div class="pack-side pack-side-b"></div>
              </div>

              <!-- Independent 3D halves: retain full 56px thickness & fracture walls when splitting open -->
              <div class="pack-half pack-half-l ${packId === 'sul_pack' ? 'sul-theme' : ''}">
                <div class="pack-half-back"></div>
                <div class="pack-half-outer"></div>
                <div class="pack-half-top"></div>
                <div class="pack-half-bottom"></div>
                <div class="pack-half-fracture"></div>
                <div class="pack-half-face">
                  <div class="pack-half-inner">
                    <div class="pack-half-surface pack-half-surface-l"></div>
                  </div>
                </div>
              </div>
              <div class="pack-half pack-half-r ${packId === 'sul_pack' ? 'sul-theme' : ''}">
                <div class="pack-half-back"></div>
                <div class="pack-half-outer"></div>
                <div class="pack-half-top"></div>
                <div class="pack-half-bottom"></div>
                <div class="pack-half-fracture"></div>
                <div class="pack-half-face">
                  <div class="pack-half-inner">
                    <div class="pack-half-surface pack-half-surface-r"></div>
                  </div>
                </div>
              </div>
              <div class="foil-gap-glow" id="foil-gap-glow"></div>

              <!-- Unified front face: 100% continuous surface when idle (zero center seam / one solid slab) -->
              <div class="pack-face">
                ${surfaceHtml}
                <!-- Front Crystalline Glass & Multi-Stage Prismatic Sheen Pane (translateZ 24px) -->
                <div class="pack-front-glass"></div>
              </div>

              <div class="foil-burst-glow" id="foil-burst-glow"></div>
            </div>
          </div>
        ` : `
          <!-- Empty Pedestal State: indicates lack of Fugazi without the UCG Tactical Forces foil graphic -->
          <div class="pack-stack-empty" id="pack-stack-empty">
            <div class="empty-pedestal-grid"></div>
            <div class="empty-holo-frame">
              <div class="empty-wireframe-card">
                <span class="empty-holo-icon">${getIconSvg('energy', 16)}</span>
                <span class="empty-wireframe-title">FUGAZI SCARCE</span>
                ${this._getQuestSlotHtml()}
                <span class="empty-wireframe-cost">${packDef.cost} ⬡ required</span>
                <span class="empty-wireframe-balance">(Balance: ${fugazi.toLocaleString()} Fugazi)</span>
              </div>
            </div>
          </div>
        `}
      </div>
    `;
  }

  _bindPackStackEvents(wrapper, canOpen) {
    if (!canOpen) this._bindQuestSlot(wrapper);
    if (!canOpen || !wrapper) return;
    const packStack = wrapper.querySelector('#pack-booster-stack');
    if (packStack) {
      packStack.addEventListener('click', (e) => {
        if (this._isSwiping || this._isTransitioning || (Date.now() - this._lastDragEndTime < 260)) {
          e.preventDefault();
          e.stopPropagation();
          return;
        }
        this.startPackTear();
      });
      packStack.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this.startPackTear();
        }
      });
    }
  }

  _getMaxAffordablePacks() {
    const packDef = PACK_DEFINITIONS[this._selectedPackId] || PACK_DEFINITIONS.core_pack;
    const cost = packDef.cost ?? PACK_COST_FUGAZI;
    const freePacks = inventory.getBoosterPacks();
    const fugazi = wallet.getFugazi();
    const affordableFugazi = Math.floor(fugazi / cost);
    const totalPossible = freePacks + affordableFugazi;
    return Math.min(totalPossible, 5);
  }

  _getStepperCostText(count) {
    const packDef = PACK_DEFINITIONS[this._selectedPackId] || PACK_DEFINITIONS.core_pack;
    const cost = packDef.cost ?? PACK_COST_FUGAZI;
    const n = count || this._selectedPackCount || 1;
    const freePacks = inventory.getBoosterPacks();
    const freeUsed = Math.min(n, freePacks);
    const fugaziPacks = n - freeUsed;
    const fugaziCost = fugaziPacks * cost;

    if (fugaziCost === 0) {
      return `KOSTEN: ${freeUsed}x ${getIconSvg('crate', 12)} FREE`;
    }
    if (freeUsed === 0) {
      return `KOSTEN: ⬡ ${fugaziCost.toLocaleString()} FUGAZI`;
    }
    return `KOSTEN: ${freeUsed}x ${getIconSvg('crate', 12)} + ⬡ ${fugaziCost.toLocaleString()}`;
  }

  _renderStepperHtml(canOpen) {
    if (!canOpen) return '';
    const maxPacks = this._getMaxAffordablePacks();
    if (maxPacks <= 0) return '';
    this._selectedPackCount = Math.max(1, Math.min(this._selectedPackCount || 1, maxPacks));
    const count = this._selectedPackCount;
    const isMinusDisabled = count <= 1;
    const isPlusDisabled = count >= maxPacks;

    return `
      <div class="cargo-stepper-box" id="cargo-stepper-box" role="region" aria-label="Select pack quantity">
        <button type="button" class="cargo-stepper-btn stepper-btn-minus" id="btn-stepper-minus" aria-label="Fewer packs" ${isMinusDisabled ? 'disabled' : ''}><span class="stepper-btn-glyph">−</span></button>
        <button type="button" class="cargo-stepper-btn stepper-btn-plus" id="btn-stepper-plus" aria-label="More packs" ${isPlusDisabled ? 'disabled' : ''}><span class="stepper-btn-glyph">+</span></button>
      </div>
    `;
  }

  _bindStepperEvents(container, canOpen) {
    if (!canOpen || !container) return;
    const stepperBox = container.querySelector('#cargo-stepper-box');
    if (!stepperBox) return;

    ['click', 'touchstart', 'touchend', 'pointerdown', 'mousedown'].forEach(evt => {
      stepperBox.addEventListener(evt, (e) => e.stopPropagation(), { passive: evt.startsWith('touch') });
    });

    const btnMinus = stepperBox.querySelector('#btn-stepper-minus');
    const btnPlus = stepperBox.querySelector('#btn-stepper-plus');

    const updateStepperUI = () => {
      const maxPacks = this._getMaxAffordablePacks();
      this._selectedPackCount = Math.max(1, Math.min(this._selectedPackCount, maxPacks));
      if (btnMinus) btnMinus.disabled = this._selectedPackCount <= 1;
      if (btnPlus) btnPlus.disabled = this._selectedPackCount >= maxPacks;

      // Update multi-pack underlay plates and count badge in real time
      const underlay = container.querySelector('#pack-stack-underlay');
      if (underlay) {
        underlay.setAttribute('data-stack-count', String(this._selectedPackCount));
      }
      const badge = container.querySelector('#pack-count-badge');
      if (badge) {
        badge.textContent = this._selectedPackCount > 1 ? `${this._selectedPackCount}x` : '';
        badge.classList.toggle('show', this._selectedPackCount > 1);
      }
    };

    if (btnMinus) {
      btnMinus.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (this._selectedPackCount > 1) {
          this._selectedPackCount--;
          if (this.audio) this.audio.playUiClick?.();
          else if (this.game?.audio) this.game.audio.playUiClick?.();
          updateStepperUI();
        }
      });
    }

    if (btnPlus) {
      btnPlus.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        const maxPacks = this._getMaxAffordablePacks();
        if (this._selectedPackCount < maxPacks) {
          this._selectedPackCount++;
          if (this.audio) this.audio.playUiClick?.();
          else if (this.game?.audio) this.game.audio.playUiClick?.();
          updateStepperUI();
        }
      });
    }
  }

  renderBuyStage() {
    if (!this.stageContainer) return;
    this._resetWarp();
    this._isAutoRevealing = false;
    this._cachedPackCenter = null;
    this.stageContainer.classList.add('stage-booster');
    document.getElementById('cargo-scene')?.classList.remove('cards-revealed');
    this._setupCanvases();

    const packDef = PACK_DEFINITIONS[this._selectedPackId] || PACK_DEFINITIONS.core_pack;
    const fugazi = wallet.getFugazi();
    const boosterPacks = inventory.getBoosterPacks();
    const canOpen = boosterPacks > 0 || fugazi >= packDef.cost;

    const inDir = this._isMobile() ? this._incomingSlideDir : null;
    this._incomingSlideDir = null;
    let slidePrepClass = '';
    if (inDir === 1) slidePrepClass = 'sliding-in-prepare-right';
    else if (inDir === -1) slidePrepClass = 'sliding-in-prepare-left';

    const existingBuyView = this.stageContainer.querySelector('.pack-buy-view');
    if (existingBuyView) {
      // IN-PLACE UPDATE: Fast path preserving existing DOM shell, tab bar, and touch listeners
      const badge = existingBuyView.querySelector('.pack-badge');
      if (badge) badge.textContent = packDef.description;
      const title = existingBuyView.querySelector('.pack-title');
      if (title) title.textContent = packDef.name.toUpperCase();
      const beam = existingBuyView.querySelector('.pack-beam');
      if (beam) beam.style.background = `linear-gradient(90deg, transparent, ${packDef.themeColor || '#00e5ff'}, transparent)`;

      // Synchronize tabs & dots
      existingBuyView.querySelectorAll('.cargo-pack-tab').forEach(tab => {
        tab.classList.toggle('active', tab.getAttribute('data-pack-id') === this._selectedPackId);
      });
      existingBuyView.querySelectorAll('.cargo-pack-dot').forEach(dot => {
        dot.classList.toggle('active', dot.getAttribute('data-pack-id') === this._selectedPackId);
      });

      // Replace only the booster pack wrapper inside the centered stage
      const stageCenter = existingBuyView.querySelector('.pack-booster-stage-center');
      const oldWrapper = stageCenter ? stageCenter.querySelector('.pack-booster-wrapper') : null;

      const temp = document.createElement('div');
      temp.innerHTML = this._getPackWrapperHtml(this._selectedPackId, canOpen, slidePrepClass);
      const newWrapper = temp.firstElementChild;

      if (oldWrapper && newWrapper) {
        oldWrapper.replaceWith(newWrapper);
      } else if (stageCenter && newWrapper) {
        const nextBtn = stageCenter.querySelector('#cargo-swipe-next');
        if (nextBtn) stageCenter.insertBefore(newWrapper, nextBtn);
        else stageCenter.appendChild(newWrapper);
      }

      this._bindPackStackEvents(newWrapper, canOpen);

      // Synchronize Stepper Box in fast-path
      const oldStepper = existingBuyView.querySelector('#cargo-stepper-box');
      if (canOpen) {
        const tempStepper = document.createElement('div');
        tempStepper.innerHTML = this._renderStepperHtml(canOpen);
        const newStepper = tempStepper.firstElementChild;
        if (oldStepper && newStepper) {
          oldStepper.replaceWith(newStepper);
        } else if (newStepper) {
          const stageCenter = existingBuyView.querySelector('.pack-booster-stage-center');
          if (stageCenter) {
            stageCenter.appendChild(newStepper);
          } else {
            existingBuyView.appendChild(newStepper);
          }
        }
        this._bindStepperEvents(existingBuyView, canOpen);
      } else if (oldStepper) {
        oldStepper.remove();
      }

      this._resetParallaxTargets?.();

      if (slidePrepClass && newWrapper) {
        requestAnimationFrame(() => {
          void newWrapper.offsetWidth; // Force layout flush for reliable CSS transition start
          newWrapper.classList.remove('sliding-in-prepare-right', 'sliding-in-prepare-left');
          newWrapper.classList.add('sliding-in-active');

          const onDone = () => {
            newWrapper.classList.remove('sliding-in-active');
            this._isTransitioning = false;
            this._isSwiping = false;
            newWrapper.removeEventListener('transitionend', onDone);
          };
          newWrapper.addEventListener('transitionend', onDone, { once: true });
          setTimeout(onDone, 360);
        });
      } else {
        this._isTransitioning = false;
        this._isSwiping = false;
      }

      this._updateBalanceFooter();
      this._renderCargoPlanet();
      return;
    }

    // INITIAL RENDER: Construct full DOM when first entering buy stage
    this.stageContainer.innerHTML = `
      <div class="pack-buy-view">
        <div class="modal-scifi-header pack-header-info">
          <span class="scifi-header-badge pack-badge">${packDef.description}</span>
          <h2 class="scifi-header-title pack-title">${packDef.name.toUpperCase()}</h2>
          <div class="scifi-header-beam pack-beam" style="background: linear-gradient(90deg, transparent, ${packDef.themeColor || '#00e5ff'}, transparent);"></div>
        </div>

        <!-- 3-Pack Selector Tabs -->
        <div class="cargo-pack-selector">
          ${Object.values(PACK_DEFINITIONS).map(p => `
            <button class="cargo-pack-tab ${p.id === this._selectedPackId ? 'active' : ''}" data-pack-id="${p.id}">
              <span class="cargo-pack-tab-icon">${this._renderMiniPackLogo(p.id)}</span>
              <span class="cargo-pack-tab-label">${p.name}</span>
            </button>
          `).join('')}
        </div>

        <!-- Mobile Pack Swipe Dots Indicator (visible on mobile only) -->
        <div class="cargo-mobile-pack-dots" aria-label="Pack wechseln durch Wischen">
          <span class="cargo-swipe-hint-arrow left">◂</span>
          <div class="cargo-pack-dots-track">
            ${Object.values(PACK_DEFINITIONS).map(p => `
              <span class="cargo-pack-dot ${p.id === this._selectedPackId ? 'active' : ''}" data-pack-id="${p.id}"></span>
            `).join('')}
          </div>
          <span class="cargo-swipe-hint-arrow right">▸</span>
          <span class="cargo-swipe-hint-text">SWIPE</span>
        </div>

        <!-- Vertically centered stage container ensuring the pack sits in the exact vertical midpoint -->
        <div class="pack-booster-stage-center">
          <!-- Mobile Side Navigation Chevrons -->
          <button class="cargo-swipe-arrow cargo-swipe-arrow-prev" id="cargo-swipe-prev" aria-label="Previous Pack" title="Previous Pack">
            <span class="cargo-arrow-glyph">‹</span>
          </button>

          ${this._getPackWrapperHtml(this._selectedPackId, canOpen, slidePrepClass)}

          <button class="cargo-swipe-arrow cargo-swipe-arrow-next" id="cargo-swipe-next" aria-label="Next Pack" title="Next Pack">
            <span class="cargo-arrow-glyph">›</span>
          </button>

          ${this._renderStepperHtml(canOpen)}
        </div>
      </div>
    `;

    const initialWrapper = this.stageContainer.querySelector('.pack-booster-wrapper');
    this._bindPackStackEvents(initialWrapper, canOpen);
    this._bindStepperEvents(this.stageContainer, canOpen);
    this._resetParallaxTargets?.();

    // Execute incoming slide animation if a slide transition just occurred
    if (slidePrepClass) {
      const newWrapper = this.stageContainer.querySelector('.pack-booster-wrapper');
      if (newWrapper) {
        requestAnimationFrame(() => {
          void newWrapper.offsetWidth;
          newWrapper.classList.remove('sliding-in-prepare-right', 'sliding-in-prepare-left');
          newWrapper.classList.add('sliding-in-active');

          const onDone = () => {
            newWrapper.classList.remove('sliding-in-active');
            this._isTransitioning = false;
            this._isSwiping = false;
            newWrapper.removeEventListener('transitionend', onDone);
          };
          newWrapper.addEventListener('transitionend', onDone, { once: true });
          setTimeout(onDone, 360);
        });
      } else {
        this._isTransitioning = false;
        this._isSwiping = false;
      }
    } else {
      this._isTransitioning = false;
      this._isSwiping = false;
    }

    const packIds = Object.keys(PACK_DEFINITIONS);
    const cyclePack = (dir, targetPackId = null) => {
      if (this._isTransitioning) return;
      const currentIdx = Math.max(0, packIds.indexOf(this._selectedPackId || 'core_pack'));
      let nextIdx;
      let slideDir = dir;

      if (targetPackId) {
        nextIdx = packIds.indexOf(targetPackId);
        if (nextIdx === -1 || nextIdx === currentIdx) return;
        slideDir = nextIdx > currentIdx ? 1 : -1;
      } else {
        nextIdx = (currentIdx + dir + packIds.length) % packIds.length;
        slideDir = dir;
      }

      const nextPackId = packIds[nextIdx];

      // Desktop: In-place pack switch with celestial background pan (no mobile sliding animation)
      if (!this._isMobile()) {
        if (nextPackId === this._selectedPackId) return;
        this._selectedPackId = nextPackId;
        this._playCargoBaySfx('pack_tab_switch');
        this.renderBuyStage();
        return;
      }

      this._isTransitioning = true;
      this._lastDragEndTime = Date.now();

      const wrapper = this.stageContainer.querySelector('.pack-booster-wrapper');
      this._playCargoBaySfx('pack_tab_switch');

      // Immediately highlight active dots and desktop tabs for instantaneous feedback
      const dots = this.stageContainer.querySelectorAll('.cargo-pack-dot');
      dots.forEach(dot => {
        dot.classList.toggle('active', dot.getAttribute('data-pack-id') === nextPackId);
      });
      const tabs = this.stageContainer.querySelectorAll('.cargo-pack-tab');
      tabs.forEach(tab => {
        tab.classList.toggle('active', tab.getAttribute('data-pack-id') === nextPackId);
      });

      if (wrapper) {
        wrapper.classList.remove('swiping', 'spring-back');
        wrapper.style.transform = '';
        wrapper.style.opacity = '';

        const exitClass = slideDir > 0 ? 'sliding-out-left' : 'sliding-out-right';
        wrapper.classList.add(exitClass);

        setTimeout(() => {
          this._selectedPackId = nextPackId;
          this._incomingSlideDir = slideDir;
          this.renderBuyStage();
        }, 190);
      } else {
        this._selectedPackId = nextPackId;
        this._incomingSlideDir = slideDir;
        this.renderBuyStage();
      }
    };

    // Pack Selector Tab Click Listeners (Desktop)
    const packTabs = this.stageContainer.querySelectorAll('.cargo-pack-tab');
    packTabs.forEach(tab => {
      tab.addEventListener('click', (e) => {
        e.stopPropagation();
        const packId = tab.getAttribute('data-pack-id');
        if (packId && PACK_DEFINITIONS[packId]) {
          if (!this._isMobile()) {
            if (packId === this._selectedPackId) return;
            this._selectedPackId = packId;
            this._playCargoBaySfx('pack_tab_switch');
            this.renderBuyStage();
          } else {
            cyclePack(0, packId);
          }
        }
      });
    });

    // Mobile Pack Dots Click Listeners
    const packDots = this.stageContainer.querySelectorAll('.cargo-pack-dot');
    packDots.forEach(dot => {
      dot.addEventListener('click', (e) => {
        e.stopPropagation();
        const packId = dot.getAttribute('data-pack-id');
        if (packId && PACK_DEFINITIONS[packId]) {
          cyclePack(0, packId);
        }
      });
    });

    // Mobile Swipe Chevrons (Tapping arrows)
    const btnPrev = this.stageContainer.querySelector('#cargo-swipe-prev');
    const btnNext = this.stageContainer.querySelector('#cargo-swipe-next');
    if (btnPrev) {
      btnPrev.addEventListener('click', (e) => {
        e.stopPropagation();
        cyclePack(-1);
      });
    }
    if (btnNext) {
      btnNext.addEventListener('click', (e) => {
        e.stopPropagation();
        cyclePack(1);
      });
    }

    // Mobile Touch Drag & Horizontal Swipe Carousel (rAF-decoupled for 120Hz ProMotion smoothness)
    const buyView = this.stageContainer.querySelector('.pack-buy-view');
    if (buyView) {
      let touchStartX = 0;
      let touchStartY = 0;
      let touchStartTime = 0;
      let isTracking = false;
      let isSwiping = false;
      let currentDx = 0;
      let pendingDx = 0;
      let rafPending = false;
      let activeWrapper = null;

      const updateSwipeTransform = () => {
        if (!isSwiping || !activeWrapper) {
          rafPending = false;
          return;
        }
        const absX = Math.abs(pendingDx);
        const sign = pendingDx < 0 ? -1 : 1;
        const maxPull = 190;
        const dampedX = absX < maxPull ? pendingDx : sign * (maxPull + (absX - maxPull) * 0.3);
        const tiltZ = dampedX * 0.024;
        const tiltY = dampedX * 0.045;
        const opacity = Math.max(0.4, 1 - (absX / 600));

        activeWrapper.style.transform = `translate3d(${dampedX.toFixed(1)}px, 0, 0) rotateZ(${tiltZ.toFixed(2)}deg) rotateY(${tiltY.toFixed(2)}deg)`;
        activeWrapper.style.opacity = opacity.toFixed(3);
        rafPending = false;
      };

      buyView.addEventListener('touchstart', (e) => {
        if (this._isTransitioning) return;
        if (!e.touches || e.touches.length !== 1) return;
        if (e.target.closest('button, .cargo-pack-dot, .cargo-pack-tab, a')) return;

        activeWrapper = this.stageContainer.querySelector('.pack-booster-wrapper');
        if (!activeWrapper) return;

        touchStartX = e.touches[0].clientX;
        touchStartY = e.touches[0].clientY;
        touchStartTime = Date.now();
        isTracking = true;
        isSwiping = false;
        currentDx = 0;
        pendingDx = 0;
        rafPending = false;
      }, { passive: true });

      buyView.addEventListener('touchmove', (e) => {
        if (!isTracking || !activeWrapper || this._isTransitioning) return;
        if (!e.touches || e.touches.length !== 1) return;

        const dx = e.touches[0].clientX - touchStartX;
        const dy = e.touches[0].clientY - touchStartY;

        if (!isSwiping) {
          if (Math.abs(dx) > 8) {
            if (Math.abs(dx) > Math.abs(dy) * 1.25) {
              isSwiping = true;
              this._isSwiping = true;
              activeWrapper.classList.add('swiping');
            } else {
              isTracking = false;
              return;
            }
          }
        }

        if (isSwiping) {
          if (e.cancelable) e.preventDefault();
          currentDx = dx;
          pendingDx = dx;
          if (!rafPending) {
            rafPending = true;
            requestAnimationFrame(updateSwipeTransform);
          }
        }
      }, { passive: false });

      const finishTouch = () => {
        if (!isTracking) return;
        isTracking = false;
        rafPending = false;

        if (isSwiping && activeWrapper) {
          this._lastDragEndTime = Date.now();
          const dt = Math.max(1, Date.now() - touchStartTime);
          const vx = Math.abs(currentDx) / dt;
          const isFlick = vx > 0.32 && Math.abs(currentDx) > 22;
          const isOverThreshold = Math.abs(currentDx) > 48;

          if (isFlick || isOverThreshold) {
            const dir = currentDx < 0 ? 1 : -1;
            cyclePack(dir);
          } else {
            activeWrapper.classList.remove('swiping');
            activeWrapper.style.transform = '';
            activeWrapper.style.opacity = '';
            activeWrapper.classList.add('spring-back');
            setTimeout(() => {
              if (activeWrapper) activeWrapper.classList.remove('spring-back');
              this._isSwiping = false;
            }, 280);
          }
        } else {
          this._isSwiping = false;
        }
      };

      buyView.addEventListener('touchend', finishTouch, { passive: true });
      buyView.addEventListener('touchcancel', finishTouch, { passive: true });
    }

    this._updateBalanceFooter();
    this._renderCargoPlanet();
  }

  // Balance/stock lives fixed in the cargo-tray footer band, not inside the
  // scrollable stage content - it shouldn't move or vanish when a stage
  // re-renders or the content above it scrolls (see chat report "dieses
  // soll aber nicht Teil des Scrolls sein").
  _updateBalanceFooter() {
    const el = document.getElementById('pack-balance-footer');
    if (!el) return;
    const fugazi = wallet.getFugazi();
    const boosterPacks = inventory.getBoosterPacks();
    el.innerHTML = `
      <span class="balance-pill-label">BALANCE:</span>
      <span class="balance-pill-fugazi">⬡ ${fugazi.toLocaleString()} Fugazi</span>
      ${boosterPacks > 0 ? `<span class="balance-pill-booster">${getIconSvg('crate', 12)} ${boosterPacks}x</span>` : ''}
    `;
  }

  // Bakes the three celestial worlds onto compact sprite canvases in the
  // Cargo Bay backdrop: Core (ringed amber gas giant), Human (sapphire ocean world),
  // and Sul (bioplasmic violet alien world).
  // Pre-bakes once on open/resize, eliminating full-screen VRAM overhead while
  // enabling instant 60/120 FPS GPU-accelerated parallax pan on pack switches.
  _renderCargoPlanet() {
    const canvasCore = document.getElementById('cargo-planet-canvas');
    const canvasHuman = document.getElementById('cargo-planet-human');
    const canvasSul = document.getElementById('cargo-planet-sul');
    if (!canvasCore) return;

    // Synchronize active pack attribute on cargo scene and class on track
    const scene = document.getElementById('cargo-scene');
    if (scene) {
      scene.setAttribute('data-selected-pack', this._selectedPackId || 'core_pack');
    }
    const track = document.getElementById('cargo-planet-track');
    if (track) {
      track.className = `cargo-planet-track pack-${(this._selectedPackId || 'core_pack').replace('_pack', '')}`;
    }

    const canvasW = canvasCore.clientWidth || (canvasHuman && canvasHuman.clientWidth) || 460;
    const canvasH = canvasCore.clientHeight || (canvasHuman && canvasHuman.clientHeight) || 460;
    const isMobile = this._isMobile();
    const dpr = isMobile ? 1.0 : Math.min(window.devicePixelRatio || 1, 1.5);
    const sizeKey = `${canvasW}x${canvasH}x${dpr}`;

    // Skip canvas re-rasterization if already rendered at this resolution
    if (this._cargoPlanetsRenderedSize === sizeKey) return;
    this._cargoPlanetsRenderedSize = sizeKey;

    const renderPlanet = (canvas, theme, hasRings, ringAngle) => {
      if (!canvas) return;
      canvas.width = Math.round(canvasW * dpr);
      canvas.height = Math.round(canvasH * dpr);
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, canvasW, canvasH);
      // Foreground prominence: Core accounts for its wide ring system (~2.3x radius),
      // while Human and Sul worlds (no rings) scale to 30% radius, leaving a generous safety
      // buffer so all atmospheric corona and limb glows fully fade to alpha 0 well before canvas edges.
      const planetRadius = hasRings
        ? Math.min(canvasW, canvasH) * 0.19
        : Math.min(canvasW, canvasH) * 0.30;
      drawSpacePlanet(ctx, {
        x: canvasW * 0.5,
        y: canvasH * 0.5,
        radius: planetRadius,
        ringAngle: ringAngle,
        quality: isMobile ? 'medium' : 'high',
        isMobile: isMobile,
        maxHazeRadius: Math.min(canvasW, canvasH) * 0.44,
        theme,
        hasRings
      });
    };

    renderPlanet(canvasCore, 'core', true, -0.34);
    renderPlanet(canvasHuman, 'human', false, 0);
    renderPlanet(canvasSul, 'sul', false, 0);
  }

  // Releases off-screen/inactive celestial planet canvases during active tearing and reveals,
  // keeping only the focused world in VRAM. Restores cleanly when returning to buy stage.
  _releaseInactivePlanets() {
    const selected = this._selectedPackId || 'core_pack';
    const canvasCore = document.getElementById('cargo-planet-canvas');
    const canvasHuman = document.getElementById('cargo-planet-human');
    const canvasSul = document.getElementById('cargo-planet-sul');
    if (selected !== 'core_pack' && canvasCore) {
      canvasCore.width = 0;
      canvasCore.height = 0;
    }
    if (selected !== 'human_pack' && canvasHuman) {
      canvasHuman.width = 0;
      canvasHuman.height = 0;
    }
    if (selected !== 'sul_pack' && canvasSul) {
      canvasSul.width = 0;
      canvasSul.height = 0;
    }
    this._cargoPlanetsRenderedSize = null;
  }

  // Releases all planet canvases during card reveals to free GPU texture backing stores.
  _releaseAllPlanets() {
    const canvasCore = document.getElementById('cargo-planet-canvas');
    const canvasHuman = document.getElementById('cargo-planet-human');
    const canvasSul = document.getElementById('cargo-planet-sul');
    if (canvasCore) {
      canvasCore.width = 0;
      canvasCore.height = 0;
    }
    if (canvasHuman) {
      canvasHuman.width = 0;
      canvasHuman.height = 0;
    }
    if (canvasSul) {
      canvasSul.width = 0;
      canvasSul.height = 0;
    }
    this._cargoPlanetsRenderedSize = null;
  }

  _clearTimeouts() {
    if (this._cardRevealDebounceTimer) {
      clearTimeout(this._cardRevealDebounceTimer);
      this._cardRevealDebounceTimer = null;
    }
    if (this._autoSwipeTimeoutId) {
      clearTimeout(this._autoSwipeTimeoutId);
      this._autoSwipeTimeoutId = null;
    }
    if (this._desktopRevealTimers) {
      this._desktopRevealTimers.forEach(t => clearTimeout(t));
      this._desktopRevealTimers = [];
    }
    this._isWarpSpeed = false;
    if (this.activeTimeouts) {
      this.activeTimeouts.forEach(t => clearTimeout(t));
      this.activeTimeouts = [];
    }
  }

  _scheduleTimeout(fn, delayMs) {
    const scaledDelay = (delayMs / (this.timeScale || 1.0));
    if (!this.activeTimeouts) this.activeTimeouts = [];
    const id = setTimeout(() => {
      if (!this.isPaused) {
        fn();
      } else {
        const check = setInterval(() => {
          if (!this.isPaused) {
            clearInterval(check);
            fn();
          }
        }, 50);
        // Tracked so _clearTimeouts() can stop it: an untracked poll kept running (and could fire
        // fn() into a closed modal) whenever the modal was closed while paused.
        this.activeTimeouts.push(check);
      }
    }, scaledDelay);
    this.activeTimeouts.push(id);
    return id;
  }

  _cachePackGeometry() {
    const stack = document.getElementById('pack-booster-stack');
    const scene = document.getElementById('cargo-scene');
    if (!scene) {
      this._cachedPackCenter = { cx: (typeof window !== 'undefined' ? window.innerWidth : 800) / 2, cy: (typeof window !== 'undefined' ? window.innerHeight : 600) / 2, top: 150, left: 300, width: 280, height: 380 };
      return this._cachedPackCenter;
    }
    const sRect = scene.getBoundingClientRect();
    const sceneW = sRect.width || scene.clientWidth || (typeof window !== 'undefined' ? window.innerWidth : 800);
    const sceneH = sRect.height || scene.clientHeight || (typeof window !== 'undefined' ? window.innerHeight : 600);
    if (!stack) {
      this._cachedPackCenter = {
        cx: sceneW / 2,
        cy: sceneH / 2,
        top: sceneH / 2 - 190,
        left: sceneW / 2 - 140,
        width: 280,
        height: 380
      };
      return this._cachedPackCenter;
    }
    const pRect = stack.getBoundingClientRect();
    const cx = (pRect.left - sRect.left) + pRect.width / 2;
    const cy = (pRect.top - sRect.top) + pRect.height / 2;
    this._cachedPackCenter = {
      cx: (cx > 0 && cx < sceneW * 1.5) ? cx : sceneW / 2,
      cy: (cy > 0 && cy < sceneH * 1.5) ? cy : sceneH / 2,
      top: pRect.height > 0 ? (pRect.top - sRect.top) : (sceneH / 2 - 190),
      left: pRect.width > 0 ? (pRect.left - sRect.left) : (sceneW / 2 - 140),
      width: pRect.width || 280,
      height: pRect.height || 380
    };
    return this._cachedPackCenter;
  }

  _getPackCenter() {
    if (this._cachedPackCenter) {
      return this._cachedPackCenter;
    }
    return this._cachePackGeometry();
  }

  _getThemeArcColor() {
    const def = PACK_DEFINITIONS[this._selectedPackId] || PACK_DEFINITIONS.core_pack;
    return def.themeColor || '#00e5ff';
  }

  _startVfxLoop() {
    if (this._vfxLoopRunning) return;
    this._vfxLoopRunning = true;
    this.lastFrameTime = performance.now();

    const loop = (now) => {
      if (!this._vfxLoopRunning) return;

      const isMobile = this._isMobile();
      this._vfxMobile = isMobile; // shared with this frame's arc drawing, see _drawBranchingLightning()
      const elapsed = now - this.lastFrameTime;
      // High-refresh mobile throttle: on 120Hz ProMotion screens (e.g. iPhone 13 Pro),
      // rAF fires every 8.3ms. Throttle VFX canvas redraws to 60 FPS (~16ms) to eliminate
      // GPU fillrate saturation, clearRect churn, and thermal throttling.
      if (isMobile && elapsed < 15) {
        this.animationFrameId = requestAnimationFrame(loop);
        return;
      }

      const dt = Math.min(elapsed / 1000, 0.08) * (this.isPaused ? 0 : (this.timeScale || 1.0));
      this.lastFrameTime = now;

      const hasActiveVfx = this.sparks.length > 0 || this.giantArcs.length > 0 || this._warpStreaks.length > 0;

      if (hasActiveVfx) {
        if (this._warpStreaks.length > 0) this._updateWarpStreaks(dt);
        this.updateSparks(dt);
        this._renderGiantArcs(dt);
        this._renderMassiveSparkCascades();
        this._canvasesCleared = false;
        this.animationFrameId = requestAnimationFrame(loop);
      } else {
        if (!this._canvasesCleared) {
          this._clearCanvases();
          this._canvasesCleared = true;
        }
        this._vfxLoopRunning = false;
        this.animationFrameId = null;
      }
    };

    this.animationFrameId = requestAnimationFrame(loop);
  }

  _stopVfxLoop() {
    this._vfxLoopRunning = false;
    this._vfxMobile = null;
    this._stopArcStorms?.();
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }
    this._clearCanvases();
  }

  _clearCanvases() {
    const dpr = this.dpr || 1;
    if (this.arcsCtx && this.arcsCanvas) {
      this.arcsCtx.setTransform(1, 0, 0, 1, 0, 0);
      this.arcsCtx.clearRect(0, 0, this.arcsCanvas.width, this.arcsCanvas.height);
      this.arcsCtx.setTransform(dpr, 0, 0, dpr, 0, 0);
    }
    if (this.sparksCtx && this.sparksCanvas) {
      this.sparksCtx.setTransform(1, 0, 0, 1, 0, 0);
      this.sparksCtx.clearRect(0, 0, this.sparksCanvas.width, this.sparksCanvas.height);
      this.sparksCtx.setTransform(dpr, 0, 0, dpr, 0, 0);
    }
    this._canvasesCleared = true;
    this._arcsDrawn = false;
    this._sparksDrawn = false;
  }

  _getFlashSprite(color) {
    if (!this._flashSpriteCache) this._flashSpriteCache = new Map();
    if (this._flashSpriteCache.has(color)) return this._flashSpriteCache.get(color);
    if (typeof document === 'undefined') return null;

    const size = 256;
    const offCanvas = document.createElement('canvas');
    offCanvas.width = size;
    offCanvas.height = size;
    const offCtx = offCanvas.getContext('2d');
    if (!offCtx) return offCanvas;
    const center = size / 2;

    const grad = offCtx.createRadialGradient(center, center, 0, center, center, center);
    grad.addColorStop(0, '#ffffff');
    grad.addColorStop(0.18, color);
    grad.addColorStop(0.55, `${color}44`);
    grad.addColorStop(1, 'rgba(0, 0, 0, 0)');

    offCtx.fillStyle = grad;
    offCtx.beginPath();
    offCtx.arc(center, center, center, 0, Math.PI * 2);
    offCtx.fill();

    this._flashSpriteCache.set(color, offCanvas);
    return offCanvas;
  }

  _getStreakSprite() {
    if (this._streakSprite) return this._streakSprite;
    if (typeof document === 'undefined') return null;

    const w = 512, h = 8;
    const offCanvas = document.createElement('canvas');
    offCanvas.width = w;
    offCanvas.height = h;
    const offCtx = offCanvas.getContext('2d');
    if (!offCtx) return offCanvas;

    const grad = offCtx.createLinearGradient(0, 0, w, 0);
    grad.addColorStop(0, 'rgba(255, 255, 255, 0)');
    grad.addColorStop(0.5, 'rgba(255, 255, 255, 0.98)');
    grad.addColorStop(1, 'rgba(255, 255, 255, 0)');

    offCtx.fillStyle = grad;
    offCtx.fillRect(0, 0, w, h);

    this._streakSprite = offCanvas;
    return offCanvas;
  }

  _generateLightningPath(x1, y1, x2, y2, steps = 11, maxJitter = 58) {
    if (!this._lightningScratchPoints) this._lightningScratchPoints = [];
    const points = this._lightningScratchPoints;
    const totalPoints = steps + 1;
    while (points.length < totalPoints) {
      points.push({ x: 0, y: 0 });
    }
    points.length = totalPoints;

    points[0].x = x1;
    points[0].y = y1;

    const dx = x2 - x1;
    const dy = y2 - y1;
    const normalX = -dy;
    const normalY = dx;
    const len = Math.hypot(normalX, normalY) || 1;
    const invLen = 1 / len;
    const uNormX = normalX * invLen;
    const uNormY = normalY * invLen;

    for (let i = 1; i < steps; i++) {
      const t = i / steps;
      const jitter = (Math.random() - 0.5) * maxJitter;
      const pt = points[i];
      pt.x = (x1 + dx * t) + uNormX * jitter;
      pt.y = (y1 + dy * t) + uNormY * jitter;
    }
    points[steps].x = x2;
    points[steps].y = y2;
    return points;
  }

  _drawBranchingLightning(ctx, x1, y1, x2, y2, color, depth = 3, alpha = 1.0) {
    // _isMobile() runs matchMedia; the VFX loop resolves it once per frame instead of once per arc.
    const isMobile = this._vfxMobile ?? this._isMobile();
    const steps = isMobile ? 6 : 10;
    const points = this._generateLightningPath(x1, y1, x2, y2, steps, isMobile ? 38 : 52);

    ctx.save();
    ctx.globalCompositeOperation = 'lighter';
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    const drawTrunk = () => {
      ctx.beginPath();
      ctx.moveTo(points[0].x, points[0].y);
      for (let p = 1; p < points.length; p++) ctx.lineTo(points[p].x, points[p].y);
      ctx.stroke();
    };

    if (isMobile) {
      // Mobile 2-Pass High-Speed Lightning: Atmospheric Bloom + Blinding White Core
      ctx.globalAlpha = Math.min(1.0, alpha * 0.55);
      ctx.strokeStyle = color;
      ctx.lineWidth = 16.0;
      drawTrunk();

      ctx.globalAlpha = Math.min(1.0, alpha * 1.0);
      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = 2.5;
      drawTrunk();
    } else {
      // Pass 1: Massive Outer Plasma Bloom (wide, atmospheric glow)
      ctx.globalAlpha = Math.min(1.0, alpha * 0.50);
      ctx.strokeStyle = color;
      ctx.lineWidth = 38.0;
      drawTrunk();

      // Pass 2: Intense Radiant Plasma Conduit (saturated body)
      ctx.globalAlpha = Math.min(1.0, alpha * 0.85);
      ctx.strokeStyle = color;
      ctx.lineWidth = 18.0;
      drawTrunk();

      // Pass 3: Concentrated High-Voltage Core (electric core)
      ctx.globalAlpha = Math.min(1.0, alpha * 0.95);
      ctx.strokeStyle = color;
      ctx.lineWidth = 8.0;
      drawTrunk();

      // Pass 4: White-Hot Blinding Laser Center (pure white hot bolt)
      ctx.globalAlpha = Math.min(1.0, alpha * 1.0);
      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = 4.0;
      drawTrunk();
    }

    // Pass 5: Dramatic Multi-Branching Tendrils
    if (depth > 0) {
      for (let i = 1; i < points.length - 1; i += 2) {
        if (Math.random() < (isMobile ? 0.35 : 0.75)) {
          const bp = points[i];
          const branchAngle = Math.atan2(y2 - y1, x2 - x1) + (Math.random() - 0.5) * 1.9;
          const branchLen = (isMobile ? 40 : 65) + Math.random() * (isMobile ? 50 : 95);
          const bx2 = bp.x + Math.cos(branchAngle) * branchLen;
          const by2 = bp.y + Math.sin(branchAngle) * branchLen;
          const midX = (bp.x + bx2) * 0.5 + (Math.random() - 0.5) * 20;
          const midY = (bp.y + by2) * 0.5 + (Math.random() - 0.5) * 20;

          // Branch glow
          ctx.globalAlpha = Math.min(1.0, alpha * 0.70);
          ctx.strokeStyle = color;
          ctx.lineWidth = isMobile ? 3.5 : 5.0;
          ctx.beginPath();
          ctx.moveTo(bp.x, bp.y);
          ctx.lineTo(midX, midY);
          ctx.lineTo(bx2, by2);
          ctx.stroke();

          // Branch white core
          ctx.globalAlpha = Math.min(1.0, alpha * 0.95);
          ctx.strokeStyle = '#ffffff';
          ctx.lineWidth = isMobile ? 1.5 : 2.0;
          ctx.beginPath();
          ctx.moveTo(bp.x, bp.y);
          ctx.lineTo(bx2, by2);
          ctx.stroke();
        }
      }
    }

    ctx.restore();
  }

  _renderGiantArcs(dt) {
    if (!this.arcsCtx || !this.arcsCanvas) return;
    const ctx = this.arcsCtx;
    const dpr = this.dpr || 1;
    const idle = this._arcsCutoff || this.giantArcs.length === 0;
    // A full-scene clearRect is a fillrate pass: skip it while the canvas is already blank
    // (the spark-only phase after the detonation used to clear it every frame for nothing).
    if (idle && !this._arcsDrawn) return;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, this.arcsCanvas.width, this.arcsCanvas.height);
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    this._arcsDrawn = !idle;

    if (idle) return;

    ctx.save();
    ctx.globalCompositeOperation = 'lighter';

    for (let i = this.giantArcs.length - 1; i >= 0; i--) {
      const arc = this.giantArcs[i];
      arc.life -= dt;
      if (arc.life <= 0) {
        this.giantArcs.splice(i, 1);
        continue;
      }

      const alpha = Math.min(1.0, arc.life * 3.5);

      // 1. Draw Multi-Branching Vector Lightning
      this._drawBranchingLightning(ctx, arc.startX, arc.startY, arc.endX, arc.endY, arc.color, arc.branches, alpha);

      const canvasW = this.arcsCanvas ? (this.arcsCanvas.width / dpr) : 800;

      // 2. Radial Welding Flash at endpoints (GPU-cached sprite blit)
      const isMobile = this._vfxMobile ?? this._isMobile();
      const maxFlashRad = isMobile ? Math.min(50, canvasW * 0.16) : Math.min(260, canvasW * 0.45);
      const flashRadius = Math.min(isMobile ? (35 + Math.random() * 15) : (200 + Math.random() * 80), maxFlashRad);
      const flashSprite = this._getFlashSprite(arc.color);
      if (flashSprite) {
        ctx.globalAlpha = alpha * 0.9;
        ctx.drawImage(flashSprite, arc.endX - flashRadius, arc.endY - flashRadius, flashRadius * 2, flashRadius * 2);
      }

      // 3. Anamorphic Horizontal Flare Streak: suppressed per user request to eliminate horizontal white line artifacts
    }

    ctx.restore();
  }

  _renderElectricArcs(dt) {
    if (this._arcsCutoff) return;
    return this._renderGiantArcs(dt);
  }

  _startPreChargingStorm() {
    if (!this.arcsEnabled || this._arcsCutoff) return;
    this._stopPreChargingStorm();
    const interval = 110 / (this.timeScale || 1.0);
    this._preChargingTimer = setInterval(() => {
      if (this._state !== 'tearing' || this._arcsCutoff) {
        this._stopPreChargingStorm();
        return;
      }
      this._spawnPackSeamArc(false);
    }, interval);
  }

  _stopPreChargingStorm() {
    if (this._preChargingTimer) {
      clearInterval(this._preChargingTimer);
      this._preChargingTimer = null;
    }
  }

  _triggerGiantArcStormSequence() {
    if (!this.arcsEnabled || this._arcsCutoff) return;
    this._stopArcStormSequence();
    const arcBursts = Math.round(14 * this.arcsIntensity);
    const interval = (850 * (this.durationMultiplier || 0.8) / arcBursts) / (this.timeScale || 1.0);

    let fired = 0;
    this._stormTimer = setInterval(() => {
      if (this._state !== 'tearing' || this._arcsCutoff) {
        this._stopArcStormSequence();
        return;
      }
      this._spawnPackSeamArc(true);
      if (Math.random() < 0.60) {
        this._spawnCeilingOrFloorArc();
      }
      if (Math.random() < 0.50) {
        this.playAudioSfx('arc_crack');
      }
      fired++;
      if (fired >= arcBursts) this._stopArcStormSequence();
    }, interval);
  }

  _stopArcStormSequence() {
    if (this._stormTimer) {
      clearInterval(this._stormTimer);
      this._stormTimer = null;
    }
  }

  _stopArcStorms() {
    this._arcsCutoff = true;
    this._stopPreChargingStorm();
    this._stopArcStormSequence();
    this.giantArcs = [];
    if (this.arcsCtx && this.arcsCanvas) {
      const dpr = this.dpr || 1;
      this.arcsCtx.setTransform(1, 0, 0, 1, 0, 0);
      this.arcsCtx.clearRect(0, 0, this.arcsCanvas.width, this.arcsCanvas.height);
      this.arcsCtx.setTransform(dpr, 0, 0, dpr, 0, 0);
      this._arcsDrawn = false;
    }
  }

  _triggerBridgingArcBurst() {
    if (!this.arcsEnabled || this._arcsCutoff) return;
    const count = Math.round(10 * this.arcsIntensity);
    for (let i = 0; i < count; i++) {
      this._spawnBridgingGapArc();
    }
    this.playAudioSfx('arc_crack');
  }

  _triggerDetonationShockwaveArcs() {
    // Wenn das Paket zerrissen ist: absolut keine Blitze mehr!
    // Stoppt alle verbleibenden Lichtbögen und Timer sofort.
    this._stopArcStorms();
  }

  _spawnPackSeamArc(violent = false) {
    if (this._arcsCutoff || !this.arcsEnabled) return;
    const { cx, top, height } = this._getPackCenter();

    const y1 = top + height * (0.10 + Math.random() * 0.28);
    const y2 = top + height * (0.62 + Math.random() * 0.30);
    const color = this._getThemeArcColor();

      this.giantArcs.push({
      startX: cx + (Math.random() - 0.5) * (violent ? 32 : 14),
      startY: y1,
      endX: cx + (Math.random() - 0.5) * (violent ? 32 : 14),
      endY: y2,
      color,
      branches: violent ? 4 : 2,
      life: 0.35 + Math.random() * 0.20
    });
    if (!this._vfxLoopRunning) this._startVfxLoop();
  }

  _spawnCeilingOrFloorArc() {
    if (this._arcsCutoff || !this.arcsEnabled) return;
    const { cx, cy } = this._getPackCenter();
    const color = this._getThemeArcColor();

    const isCeiling = Math.random() < 0.65;
    const startX = cx + (Math.random() - 0.5) * 180;
    const startY = isCeiling ? -10 : this.floorY;

    this.giantArcs.push({
      startX,
      startY,
      endX: cx + (Math.random() - 0.5) * 60,
      endY: cy + (Math.random() - 0.5) * 110,
      color,
      branches: 3,
      life: 0.38 + Math.random() * 0.22
    });
    if (!this._vfxLoopRunning) this._startVfxLoop();
  }

  _spawnBridgingGapArc() {
    if (this._arcsCutoff || !this.arcsEnabled) return;
    const { cx, top, height } = this._getPackCenter();
    const gap = 40 + Math.random() * 60;
    const y = top + height * (0.15 + Math.random() * 0.70);
    const color = this._getThemeArcColor();

    this.giantArcs.push({
      startX: cx - gap / 2,
      startY: y + (Math.random() - 0.5) * 28,
      endX: cx + gap / 2,
      endY: y + (Math.random() - 0.5) * 28,
      color,
      branches: 3,
      life: 0.35 + Math.random() * 0.20
    });
    if (!this._vfxLoopRunning) this._startVfxLoop();
  }

  spawnGiganticSparkTorrent(count = 180, forcedColor = null) {
    if (!this.sparksEnabled) return;
    const { cx, cy } = this._getPackCenter();
    const originX = cx;
    const originY = cy;
    const def = PACK_DEFINITIONS[this._selectedPackId] || PACK_DEFINITIONS.core_pack;

    const sparkPalette = [
      '#ffffff',
      def.accentColor || '#f59e0b',
      def.themeColor || '#ffaa00',
      '#f59e0b',
      '#fbbf24',
      '#ffaa00'
    ];

    const getSparkColor = () => {
      if (forcedColor) return forcedColor;
      return sparkPalette[Math.floor(Math.random() * sparkPalette.length)];
    };

    for (let i = 0; i < count; i++) {
      const ang = Math.PI * 0.05 + Math.random() * Math.PI * 0.90;
      const spd = 5.0 + Math.random() * 32.0;
      const isHighFountain = Math.random() > 0.32;

      const s = this.sparkPool.pop() || { shapeOffsets: [0, 0, 0, 0] };
      s.x = originX + (Math.random() - 0.5) * 36;
      s.y = originY + (Math.random() - 0.5) * 44;
      s.vx = Math.cos(ang) * spd * (Math.random() < 0.5 ? -1 : 1);
      s.vy = isHighFountain ? -Math.sin(ang) * spd * 1.45 : Math.sin(ang) * spd * 0.55;
      s.life = 1.0;
      s.decay = 0.16 + Math.random() * 0.28;
      s.size = 2.8 + Math.random() * 4.6;
      s.color = getSparkColor();
      s.bounces = 0;
      if (!s.shapeOffsets) s.shapeOffsets = [0, 0, 0, 0];
      s.shapeOffsets[0] = Math.random() * 1.2 - 0.6;
      s.shapeOffsets[1] = Math.random() * 1.2 - 0.6;
      s.shapeOffsets[2] = Math.random() * 1.2 - 0.6;
      s.shapeOffsets[3] = Math.random() * 1.2 - 0.6;

      this.sparks.push(s);
    }
    if (!this._vfxLoopRunning) this._startVfxLoop();
  }

  _emitDetonationCoarseSparks(count = 180, forcedColor = null) {
    return this.spawnGiganticSparkTorrent(count, forcedColor);
  }

  _renderCoarseSparks() {
    return this._renderMassiveSparkCascades();
  }

  _renderMassiveSparkCascades() {
    if (!this.sparksCtx || !this.sparksCanvas) return;
    const ctx = this.sparksCtx;
    const dpr = this.dpr || 1;
    const count = this.sparks.length;
    const streaks = this._warpStreaks.length;
    if (count === 0 && streaks === 0 && !this._sparksDrawn) return;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, this.sparksCanvas.width, this.sparksCanvas.height);
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    this._sparksDrawn = count > 0 || streaks > 0;
    if (streaks > 0) this._drawWarpStreaks(ctx);
    if (count === 0) return;

    ctx.save();
    // Additiver Modus: Funken verschmelzen zu weißheißem Plasma-Licht
    ctx.globalCompositeOperation = 'lighter';

    // PASS 1: Soft Ember Tails (uniform alpha = zero state changes across entire pass!)
    ctx.globalAlpha = 0.32;
    let lastFill = '';
    for (let i = 0; i < count; i++) {
      const s = this.sparks[i];
      if (s.life <= 0.05) continue;
      const sColor = s.color || '#fbbf24';
      if (lastFill !== sColor) {
        ctx.fillStyle = sColor;
        lastFill = sColor;
      }
      const sz = s.size * 0.92;
      ctx.fillRect(
        (s.x - s.vx * 0.45 + 0.5) | 0,
        (s.y - s.vy * 0.45 + 0.5) | 0,
        sz,
        sz
      );
    }

    // PASS 2: Incandescent Spark Heads (high-intensity hot core, single alpha!)
    ctx.globalAlpha = 0.95;
    lastFill = '';
    for (let i = 0; i < count; i++) {
      const s = this.sparks[i];
      const sz = s.size;
      const rx = (s.x + 0.5) | 0;
      const ry = (s.y + 0.5) | 0;

      let col = '#ffffff';
      if (s.life < 0.22) {
        col = '#ef4444'; // Dying ash
      } else if (s.life < 0.65) {
        col = s.color || '#f59e0b';
      }

      if (lastFill !== col) {
        ctx.fillStyle = col;
        lastFill = col;
      }

      ctx.fillRect(rx - sz * 0.5, ry - sz * 0.5, sz, sz);
      if (sz > 4.6) {
        ctx.fillRect(rx - sz * 0.75, ry - sz * 0.25, sz * 1.5, sz * 0.5);
      }
    }

    ctx.restore();
  }

  updateSparks(dt) {
    const floor = this.floorY || 600;
    const count = this.sparks.length;
    const decayMult = count > 800 ? 1.35 : 1.0;
    // The tuning constants below are per 60 Hz frame. Integrating them per rAF tick made the sparks
    // fly 2.4x faster on a 144 Hz display and crawl whenever a throttled phone dropped to 30 fps,
    // while their lifetime (dt based) stayed fixed. step == 1 at 60 fps, so the look there is unchanged.
    const step = dt * 60;
    const drag = Math.pow(0.988, step);
    const gravity = 0.44 * step;

    for (let i = count - 1; i >= 0; i--) {
      const s = this.sparks[i];
      s.x += s.vx * step;
      s.y += s.vy * step;
      s.vy += gravity;
      s.vx *= drag;
      s.life -= s.decay * dt * decayMult;

      if (s.y >= floor && s.vy > 0) {
        s.vy = -s.vy * 0.46;
        s.vx *= 0.72;
        s.y = floor;
        s.bounces++;
        if (s.bounces > 3) s.life = 0;
      }

      if (s.life <= 0) {
        if (this.sparkPool.length < 3200) {
          this.sparkPool.push(s);
        }
        const last = this.sparks.pop();
        if (i < this.sparks.length) {
          this.sparks[i] = last;
        }
      }
    }
  }

  /* ============================================================
     THE CELEBRATED TEAR CHOREOGRAPHY (EXTENDED & DRAMATIC)
     ============================================================ */
  startPackTear() {
    if (this._state === 'tearing') return;
    this._state = 'tearing';
    this._stopArcStorms();
    this._clearTimeouts();
    this._setupCanvases();
    this._cachePackGeometry();
    this._stopParallax?.();
    this._arcsCutoff = false;

    // Tier 2 deep cleanup: unmount heavy hangar planes, zero static canopy, clear aura/statue caches
    this.game?.releaseDeepPreRevealCaches?.();

    // Fade out and dissolve the stepper, stack underlay, and badge immediately upon initiating tear
    const stepperBox = this.stageContainer?.querySelector('#cargo-stepper-box');
    if (stepperBox) {
      stepperBox.classList.add('dissolving');
    }
    const underlay = this.stageContainer?.querySelector('#pack-stack-underlay');
    if (underlay) {
      underlay.classList.add('dissolving');
    }
    const badge = this.stageContainer?.querySelector('#pack-count-badge');
    if (badge) {
      badge.classList.add('dissolving');
    }

    // Roll pack immediately at tear inception so all card images can be preloaded & GPU-decoded
    // during the 2.5s-4.0s tear animation window.
    const packCount = this._selectedPackCount || 1;
    const result = packCount > 1
      ? packs.openPackBatch(this._selectedPackId || 'core_pack', packCount, { deferNotify: true })
      : packs.openPack(this._selectedPackId || 'core_pack', { free: inventory.getBoosterPacks() > 0, deferNotify: true });
    if (!result.success) {
      this._state = 'idle';
      this.renderBuyStage();
      return;
    }
    this._currentPackResult = result;
    this._cardsRevealedCount = 0;
    this._predecodePromise = this._predecodeCardArt(result.cards, 4500);

    // A/B flag 'novfx' (js/ui/abFlags.js): measure the tear without its canvas sparks and lightning.
    this.arcsEnabled = this.sparksEnabled = !isAbOn('novfx');
    this._startVfxLoop();

    const wrapper = document.querySelector('.pack-booster-wrapper');
    const stack = document.getElementById('pack-booster-stack');
    const foil = document.getElementById('pack-booster-foil');
    const gapGlow = document.getElementById('foil-gap-glow');
    const burstGlow = document.getElementById('foil-burst-glow');
    const scene = document.getElementById('cargo-scene');
    if (scene) scene.classList.add('tearing-active');
    this._releaseInactivePlanets();
    const vignette = document.getElementById('cargo-vignette');
    const surgeBloom = document.getElementById('cargo-surge-bloom');
    const surgeBurst = document.getElementById('cargo-surge-burst');
    const surgeRing = document.getElementById('cargo-surge-ring');
    const surgeBlast = document.getElementById('cargo-surge-blast');
    const godRays = document.getElementById('pack-hearthstone-rays');

    const dMult = this._isMobile() ? 0.52 : (this.durationMultiplier || 0.80);
    const tLockInSlam = Math.round(360 * dMult);
    const tChargeStart = Math.round(1120 * dMult);
    const tArcsOverload = Math.round(1950 * dMult);
    const tFractureSnap = Math.round(2900 * dMult);
    const tDetonation = Math.round(3360 * dMult);
    const tDecay = Math.round(4250 * dMult);
    const tReveal = Math.round(4950 * dMult);

    const mobileArrows = this.stageContainer?.querySelectorAll('.cargo-swipe-arrow');
    mobileArrows?.forEach(arrow => arrow.classList.add('fading-out'));
    const mobileDots = this.stageContainer?.querySelector('.cargo-mobile-pack-dots');
    if (mobileDots) mobileDots.classList.add('fading-out');

    // BEAT 1: WEIGHTED LOCK-IN & PRESSURE SEAL
    if (wrapper) wrapper.classList.add('gripped', 'locked');
    if (vignette) vignette.classList.add('active');
    if (stack) stack.classList.add('locking-in');
    if (scene) scene.classList.add('lockin-jolt');

    setTimeout(() => scene && scene.classList.remove('lockin-jolt'), Math.round(320 * dMult));
    // Tactile impact spark kick (contact friction)
    const isMobileVfx = this._isMobile();
    this.spawnGiganticSparkTorrent(Math.round(isMobileVfx ? 8 : (30 * this.sparksIntensity)));
    this.playAudioSfx('lock_in');
    this._pulseHaptic([12]);

    // BEAT 1B: SEKUNDÄRES EINRASTEN — zweiter, leiserer Klick 30ms nach dem
    // Haupt-Lock-In (kein durationMultiplier-Scaling, bewusst ein fixer
    // Mikro-Versatz statt einer Sequenz-Phase).
    this._scheduleTimeout(() => this._playPackLockConfirmSfx(), 30);

    // BEAT 1C: RUHE VOR DEM STURM — Pack verharrt in der unteren Position mit themed Kupferlicht
    this._scheduleTimeout(() => {
      if (stack) {
        stack.classList.remove('locking-in');
        stack.classList.add('copper-linger');
      }
      // Mount SVG surface to the two tear halves lazily right before fracture to avoid 3x duplicate SVG trees in idle DOM
      const surfaceL = foil?.querySelector('.pack-half-surface-l');
      const surfaceR = foil?.querySelector('.pack-half-surface-r');
      if (surfaceL && !surfaceL.hasChildNodes()) {
        const surfaceHtml = this._getPackSurfaceMarkup(this._selectedPackId || 'core_pack');
        surfaceL.innerHTML = surfaceHtml;
      }
      if (surfaceR && !surfaceR.hasChildNodes()) {
        const surfaceHtml = this._getPackSurfaceMarkup(this._selectedPackId || 'core_pack');
        surfaceR.innerHTML = surfaceHtml;
      }
      // Build the (hidden) shatter DOM here, in the calm before the storm, rather than at the
      // charge peak where sparks, arcs and the storm timer are all running.
      this._prebakeShatterContainer(foil, PACK_DEFINITIONS[this._selectedPackId || 'core_pack']);
    }, tLockInSlam);

    // BEAT 2A: AUFLADUNG START — Hearthstone God-Rays ignite!
    this._scheduleTimeout(() => {
      if (gapGlow) gapGlow.classList.add('charging');
      if (godRays) godRays.classList.add('active');
      this.playAudioSfx('charge');
      if (this.audio && !this._isAudioMuted()) this.audio.playCrateCharge?.();

      if (stack) {
        stack.classList.remove('copper-linger', 'locking-in');
        stack.classList.add('zappel-stage-2', 'overcharging');
      }
      this._spawnChargingSparks(stack);
      this._startPreChargingStorm();
    }, tChargeStart);

    // BEAT 2B: LICHTBÖGEN-OVERLOAD — Giant Tesla storm & violent trembling!
    this._scheduleTimeout(() => {
      this._stopPreChargingStorm();
      if (stack) {
        stack.classList.remove('zappel-stage-2');
        stack.classList.add('zappel-stage-3', 'critical-mass');
      }
      this._triggerGiantArcStormSequence();
      this.spawnGiganticSparkTorrent(Math.round(isMobileVfx ? 16 : (60 * this.sparksIntensity)));
    }, tArcsOverload);

    // BEAT 3: NAHT-BRUCH / RISS-SNAP — Blinding rupture & lateral spray!
    this._scheduleTimeout(() => {
      this._stopArcStormSequence();
      if (gapGlow) {
        gapGlow.classList.remove('charging');
        gapGlow.classList.add('active');
      }
      if (wrapper) wrapper.classList.remove('gripped');
      if (stack) {
        stack.classList.remove('zappel-stage-3', 'overcharging', 'critical-mass');
        stack.classList.add('zappel-recoil', 'tearing');
      }
      if (foil) {
        foil.classList.add('tearing');
        const packFace = foil.querySelector('.pack-face');
        if (packFace) packFace.innerHTML = '';
      }
      if (scene) scene.classList.add('shake');

      this.playAudioSfx('latch_snap');
      this._playCargoBaySfx('pack_latch_click');
      this._pulseHaptic([15]);

      this._triggerBridgingArcBurst();
      this._spawnTearVentingSparks(stack);
      // High velocity lateral spark torrent from the breach
      this.spawnGiganticSparkTorrent(Math.round(isMobileVfx ? 28 : (160 * this.sparksIntensity)));
    }, tFractureSnap);

    // BEAT 4: DETONATION & MASSIVER FUNKENREGEN (ORDNANCE-FORGE STYLE // ZERO-STUTTER)
    this._scheduleTimeout(() => {
      // Paket ist zerrissen: Alle Blitze restlos beenden!
      this._stopArcStorms();

      if (stack) {
        stack.classList.remove('zappel-recoil', 'tearing', 'overcharging', 'critical-mass');
      }
      if (foil) {
        foil.classList.remove('tearing');
        foil.classList.add('surging');
      }
      if (burstGlow) burstGlow.classList.add('active');
      if (gapGlow) gapGlow.classList.add('flare');
      if (surgeBloom) surgeBloom.classList.add('active');
      if (surgeBurst) surgeBurst.classList.add('active');
      if (surgeRing) surgeRing.classList.add('active');
      if (surgeBlast) surgeBlast.classList.add('active');
      if (scene) {
        scene.classList.remove('shake');
        scene.classList.add('shake-hard');
      }

      this.playAudioSfx('detonation');
      if (this.audio && !this._isAudioMuted()) this.audio.playCrateBurst?.();
      this._pulseHaptic([40, 30, 85]);

      this._spawnPackDisintegration(foil, PACK_DEFINITIONS[this._selectedPackId || 'core_pack']);

      // MASSIVE GIGANTIC ORDNANCE-FORGE SPARK TORRENT (Staggered into 3 micro-waves for zero-stutter plasma cloud)
      const totalSparks = Math.round(isMobileVfx ? 55 : (450 * this.sparksIntensity));
      const wave1 = Math.round(totalSparks * 0.40);
      const wave2 = Math.round(totalSparks * 0.35);
      const wave3 = totalSparks - wave1 - wave2;
      this.spawnGiganticSparkTorrent(wave1);
      this._triggerDetonationShockwaveArcs();

      // Stagger wave 2 and 3 by consecutive frames (16ms & 32ms) for volumetric expansion and zero micro-stutter
      requestAnimationFrame(() => {
        if (this._state === 'tearing') {
          this.spawnGiganticSparkTorrent(wave2);
          requestAnimationFrame(() => {
            if (this._state === 'tearing') {
              this.spawnGiganticSparkTorrent(wave3);
            }
          });
        }
      });

      this._scheduleTimeout(() => scene && scene.classList.remove('shake-hard'), Math.round(550 * dMult));
      this._scheduleTimeout(() => surgeRing && surgeRing.classList.remove('active'), Math.round(600 * dMult));
      this._scheduleTimeout(() => surgeBlast && surgeBlast.classList.remove('active'), Math.round(620 * dMult));
      this._scheduleTimeout(() => surgeBurst && surgeBurst.classList.remove('active'), Math.round(640 * dMult));
    }, tDetonation);

    // BEAT 5: ENTSCHLEIERUNG
    this._scheduleTimeout(() => {
      this._stopArcStorms();
      if (foil) {
        foil.classList.remove('surging');
        foil.classList.add('dropping');
        foil.style.opacity = '0';
        foil.style.pointerEvents = 'none';
      }
      if (vignette) vignette.classList.remove('active');
      if (surgeBloom) surgeBloom.classList.remove('active');
      if (godRays) godRays.classList.remove('active');
    }, tDecay);

    // BEAT 6: KARTENFALL & REVEAL
    this._scheduleTimeout(() => {
      this._stopArcStorms();
      this.executePackOpen();
    }, tReveal);
  }

  // Spawns converging energy motes spiraling into the seam during tension build (Beat 2)
  _spawnChargingSparks(stack) {
    if (!stack || this._isMobile()) return;
    const isMobile = this._isMobile();
    const container = document.createElement('div');
    container.className = 'pack-charge-container';
    const fragment = document.createDocumentFragment();

    const theme = this._selectedPackId || 'core_pack';
    let sparkColor = '#f59e0b';
    if (theme === 'human_pack') sparkColor = '#00e5ff';
    else if (theme === 'sul_pack') sparkColor = '#d946ef';

    // 8 sparks accelerating inward from outer orbit to central tear seam
    for (let i = 0; i < 8; i++) {
      const spark = document.createElement('div');
      spark.className = 'pack-charge-spark';

      const angle = (i / 8) * Math.PI * 2 + (Math.random() - 0.5) * 0.4;
      const dist = 110 + Math.random() * 95;
      const sx = Math.cos(angle) * dist;
      const sy = Math.sin(angle) * dist;
      const ty = (Math.random() - 0.5) * 240;
      const duration = 0.42 + Math.random() * 0.25;
      const delay = Math.random() * 160;
      const size = 2.5 + Math.random() * 3.5;

      spark.style.setProperty('--sx', `${sx.toFixed(1)}px`);
      spark.style.setProperty('--sy', `${sy.toFixed(1)}px`);
      spark.style.setProperty('--ty', `${ty.toFixed(1)}px`);
      spark.style.setProperty('--duration', `${duration.toFixed(2)}s`);
      spark.style.setProperty('--delay', `${delay.toFixed(0)}ms`);
      spark.style.width = `${size.toFixed(1)}px`;
      spark.style.height = `${size.toFixed(1)}px`;
      spark.style.background = i % 3 === 0 ? '#ffffff' : sparkColor;
      spark.style.boxShadow = isMobile ? `0 0 6px ${sparkColor}` : `0 0 ${Math.round(size * 1.8)}px ${sparkColor}`;

      fragment.appendChild(spark);
    }

    container.appendChild(fragment);
    stack.appendChild(container);

    setTimeout(() => container.remove(), 720);
  }

  // Spawns lateral high-pressure venting micro-sparks as the seal cracks open (Beat 3)
  _spawnTearVentingSparks(stack) {
    if (!stack || this._isMobile()) return;
    const isMobile = this._isMobile();
    const container = document.createElement('div');
    container.className = 'pack-vent-container';
    const fragment = document.createDocumentFragment();

    const theme = this._selectedPackId || 'core_pack';
    let sparkColor = '#f59e0b';
    let altColor = '#fef08a';
    if (theme === 'human_pack') {
      sparkColor = '#00e5ff';
      altColor = '#ffffff';
    } else if (theme === 'sul_pack') {
      sparkColor = '#d946ef';
      altColor = '#f472b6';
    }

    // 6 high-pressure micro-sparks spraying out from the fracture line
    for (let i = 0; i < 6; i++) {
      const spark = document.createElement('div');
      spark.className = 'pack-vent-spark';

      const isLeft = i % 2 === 0;
      const dir = isLeft ? -1 : 1;
      const vx = dir * (40 + Math.random() * 85);
      const vy = (Math.random() - 0.5) * 80;
      const seamY = 25 + Math.random() * 50;
      const duration = 0.24 + Math.random() * 0.14;
      const size = 2 + Math.random() * 3;
      const color = i % 2 === 0 ? sparkColor : altColor;

      spark.style.top = `${seamY}%`;
      spark.style.setProperty('--vx', `${vx.toFixed(1)}px`);
      spark.style.setProperty('--vy', `${vy.toFixed(1)}px`);
      spark.style.setProperty('--duration', `${duration.toFixed(2)}s`);
      spark.style.width = `${size.toFixed(1)}px`;
      spark.style.height = `${size.toFixed(1)}px`;
      spark.style.background = color;
      spark.style.boxShadow = isMobile ? `0 0 6px ${color}` : `0 0 ${Math.round(size * 1.8)}px ${color}`;

      fragment.appendChild(spark);
    }

    container.appendChild(fragment);
    stack.appendChild(container);

    setTimeout(() => container.remove(), 420);
  }

  _buildShatterContainer(theme) {
    const container = document.createElement('div');
    container.className = 'pack-shatter-container';
    const fragment = document.createDocumentFragment();

    let themeColor = '#f59e0b';
    let altColor = '#fef08a';
    if (theme === 'human_pack') {
      themeColor = '#00e5ff';
      altColor = '#38bdf8';
    } else if (theme === 'sul_pack') {
      themeColor = '#d946ef';
      altColor = '#f472b6';
    }

    const SHARD_POLYGONS = [
      'polygon(0% 0%, 100% 10%, 85% 90%, 10% 80%)',
      'polygon(20% 0%, 95% 0%, 100% 70%, 35% 100%, 0% 45%)',
      'polygon(0% 15%, 80% 0%, 100% 85%, 25% 100%)',
      'polygon(10% 5%, 95% 0%, 100% 95%, 5% 90%)',
      'polygon(0% 0%, 85% 15%, 100% 100%, 15% 95%)',
      'polygon(35% 0%, 100% 40%, 65% 100%, 0% 75%)'
    ];

    // Mobile optimization: scale particle density to prevent DOM allocation chokepoints
    const isMobile = this._isMobile();
    const shardCount = isMobile ? 2 : 6;
    const sparkCount = isMobile ? 0 : 6;
    const emberCount = isMobile ? 0 : 5;
    const microShardCount = isMobile ? 0 : 6;
    const streamerCount = isMobile ? 1 : 3;
    const moteCount = isMobile ? 0 : 6;

    // Spawn high-impact Armor Shards
    for (let i = 0; i < shardCount; i++) {
      const isLeft = i < Math.ceil(shardCount / 2);
      const shard = document.createElement('div');
      shard.className = `pack-shatter-shard ${isLeft ? 'shard-left' : 'shard-right'} shard-theme-${theme}`;

      const poly = SHARD_POLYGONS[i % SHARD_POLYGONS.length];
      shard.style.clipPath = poly;
      shard.style.webkitClipPath = poly;

      // Physics variables for 3D trajectory
      const dir = isLeft ? -1 : 1;
      const vx = dir * (190 + Math.random() * 200);
      const vy = (Math.random() - 0.5) * 400;
      const vz = 60 + Math.random() * 200;
      const rx = (Math.random() - 0.5) * 360;
      const ry = dir * (80 + Math.random() * 220);
      const rz = (Math.random() - 0.5) * 320;
      const scale = 0.5 + Math.random() * 0.45;
      const delay = Math.random() * 40;

      shard.style.setProperty('--dx', `${vx.toFixed(1)}px`);
      shard.style.setProperty('--dy', `${vy.toFixed(1)}px`);
      shard.style.setProperty('--dz', `${vz.toFixed(1)}px`);
      shard.style.setProperty('--rx', `${rx.toFixed(1)}deg`);
      shard.style.setProperty('--ry', `${ry.toFixed(1)}deg`);
      shard.style.setProperty('--rz', `${rz.toFixed(1)}deg`);
      shard.style.setProperty('--scale', scale.toFixed(2));
      shard.style.setProperty('--delay', `${delay.toFixed(1)}ms`);

      const startTop = 22 + Math.random() * 56;
      shard.style.top = `${startTop}%`;
      shard.style.left = isLeft ? `calc(50% - ${22 + Math.random() * 38}px)` : `calc(50% + ${Math.random() * 38}px)`;

      fragment.appendChild(shard);
    }

    // Spawn High-Velocity Supersonic Plasma Sparks
    for (let p = 0; p < sparkCount; p++) {
      const spark = document.createElement('div');
      spark.className = 'pack-shatter-spark';

      const isLeft = p % 2 === 0;
      const baseAngle = isLeft ? 180 : 0;
      const spread = (Math.random() - 0.5) * 110;
      const angleRad = ((baseAngle + spread) * Math.PI) / 180;

      const dist = 150 + Math.random() * 240;
      const tx = Math.cos(angleRad) * dist;
      const ty = Math.sin(angleRad) * dist;
      const tz = 40 + Math.random() * 160;

      const duration = 0.42 + Math.random() * 0.22;
      const size = 3.5 + Math.random() * 5;
      const color = p % 3 === 0 ? '#ffffff' : (p % 3 === 1 ? altColor : themeColor);

      spark.style.setProperty('--tx', `${tx.toFixed(1)}px`);
      spark.style.setProperty('--ty', `${ty.toFixed(1)}px`);
      spark.style.setProperty('--tz', `${tz.toFixed(1)}px`);
      spark.style.setProperty('--duration', `${duration.toFixed(2)}s`);
      spark.style.width = `${size.toFixed(1)}px`;
      spark.style.height = `${size.toFixed(1)}px`;
      spark.style.background = color;
      spark.style.boxShadow = isMobile ? `0 0 5px ${color}` : `0 0 ${Math.round(size * 1.6)}px ${color}`;

      fragment.appendChild(spark);
    }

    // Spawn Lingering Zero-G Drifting Embers
    for (let e = 0; e < emberCount; e++) {
      const ember = document.createElement('div');
      ember.className = 'pack-drift-ember';

      const angle = Math.random() * Math.PI * 2;
      const driftDist = 45 + Math.random() * 135;
      const ex = Math.cos(angle) * driftDist;
      const ey = Math.sin(angle) * driftDist - (25 + Math.random() * 35);
      const ez = 25 + Math.random() * 90;
      const duration = 0.95 + Math.random() * 0.35;
      const delay = 40 + Math.random() * 100;
      const size = 2.5 + Math.random() * 4;

      ember.style.setProperty('--ex', `${ex.toFixed(1)}px`);
      ember.style.setProperty('--ey', `${ey.toFixed(1)}px`);
      ember.style.setProperty('--ez', `${ez.toFixed(1)}px`);
      ember.style.setProperty('--duration', `${duration.toFixed(2)}s`);
      ember.style.setProperty('--delay', `${delay.toFixed(0)}ms`);
      ember.style.width = `${size.toFixed(1)}px`;
      ember.style.height = `${size.toFixed(1)}px`;
      ember.style.background = e % 2 === 0 ? themeColor : altColor;
      ember.style.boxShadow = isMobile ? `0 0 4px ${themeColor}` : `0 0 ${Math.round(size * 2)}px ${themeColor}`;

      fragment.appendChild(ember);
    }

    // Spawn Secondary Fast-Spinning Micro-Shards
    for (let m = 0; m < microShardCount; m++) {
      const isLeft = m % 2 === 0;
      const shard = document.createElement('div');
      shard.className = 'pack-micro-shard';

      const poly = SHARD_POLYGONS[(m + 2) % SHARD_POLYGONS.length];
      shard.style.clipPath = poly;
      shard.style.webkitClipPath = poly;

      const dir = isLeft ? -1 : 1;
      const mx = dir * (130 + Math.random() * 180);
      const my = (Math.random() - 0.5) * 340;
      const rz = dir * (360 + Math.random() * 720);
      const duration = 0.34 + Math.random() * 0.16;
      const size = 8 + Math.random() * 9;
      const delay = Math.random() * 40;

      shard.style.setProperty('--mx', `${mx.toFixed(1)}px`);
      shard.style.setProperty('--my', `${my.toFixed(1)}px`);
      shard.style.setProperty('--mrz', `${rz.toFixed(0)}deg`);
      shard.style.setProperty('--duration', `${duration.toFixed(2)}s`);
      shard.style.setProperty('--delay', `${delay.toFixed(1)}ms`);
      shard.style.width = `${size.toFixed(1)}px`;
      shard.style.height = `${size.toFixed(1)}px`;
      shard.style.background = m % 2 === 0 ? themeColor : (m % 3 === 0 ? '#ffffff' : altColor);
      shard.style.top = `${30 + Math.random() * 40}%`;
      shard.style.left = isLeft ? `calc(50% - ${14 + Math.random() * 24}px)` : `calc(50% + ${Math.random() * 24}px)`;

      fragment.appendChild(shard);
    }

    // Spawn Plasma Ribbon Streamers
    for (let s = 0; s < streamerCount; s++) {
      const streamer = document.createElement('div');
      streamer.className = 'pack-plasma-streamer';

      const isLeft = s < Math.ceil(streamerCount / 2);
      const dir = isLeft ? -1 : 1;
      const vx = dir * (220 + Math.random() * 160);
      const vy = (Math.random() - 0.5) * 120;
      const angle = dir * (-(8 + Math.random() * 22));
      const len = 20 + Math.random() * 16;
      const delay = 10 + Math.random() * 40;

      streamer.style.setProperty('--px', `${vx.toFixed(1)}px`);
      streamer.style.setProperty('--py', `${vy.toFixed(1)}px`);
      streamer.style.setProperty('--pangle', `${angle.toFixed(1)}deg`);
      streamer.style.setProperty('--delay', `${delay.toFixed(1)}ms`);
      streamer.style.width = `${len.toFixed(1)}px`;
      streamer.style.background = `linear-gradient(90deg, ${themeColor}, transparent)`;
      streamer.style.top = `${35 + Math.random() * 30}%`;
      streamer.style.left = isLeft
        ? `calc(50% - ${5 + Math.random() * 20}px)`
        : `calc(50% + ${Math.random() * 20}px)`;

      fragment.appendChild(streamer);
    }

    // Spawn Instantaneous Impact Motes
    for (let k = 0; k < moteCount; k++) {
      const mote = document.createElement('div');
      mote.className = 'pack-impact-mote';

      const angle = Math.random() * Math.PI * 2;
      const dist = 18 + Math.random() * 52;
      const ix = Math.cos(angle) * dist;
      const iy = Math.sin(angle) * dist;
      const duration = 0.18 + Math.random() * 0.16;
      const size = 2.5 + Math.random() * 5;
      const delay = Math.random() * 30;
      const color = k % 4 === 0 ? '#ffffff' : (k % 3 === 0 ? altColor : themeColor);

      mote.style.setProperty('--ix', `${ix.toFixed(1)}px`);
      mote.style.setProperty('--iy', `${iy.toFixed(1)}px`);
      mote.style.setProperty('--duration', `${duration.toFixed(2)}s`);
      mote.style.setProperty('--delay', `${delay.toFixed(1)}ms`);
      mote.style.width = `${size.toFixed(1)}px`;
      mote.style.height = `${size.toFixed(1)}px`;
      mote.style.background = color;
      mote.style.boxShadow = isMobile ? `0 0 4px ${color}` : `0 0 ${Math.round(size * 2.5)}px ${color}`;

      fragment.appendChild(mote);
    }

    container.appendChild(fragment);
    return container;
  }

  _prebakeShatterContainer(foil, packDef) {
    if (!foil) return;
    try {
      if (this._prebakedShatterContainer) {
        this._prebakedShatterContainer.remove();
        this._prebakedShatterContainer = null;
      }
      const theme = this._selectedPackId || 'core_pack';
      const container = this._buildShatterContainer(theme);
      container.style.display = 'none';
      container.style.pointerEvents = 'none';
      foil.appendChild(container);
      this._prebakedShatterContainer = container;
    } catch (e) {
      this._prebakedShatterContainer = null;
    }
  }

  // Spawns explosive armor shards, supersonic plasma sparks, and lingering zero-G embers (Beat 4)
  _spawnPackDisintegration(foil, packDef) {
    if (!foil) return;

    let container = this._prebakedShatterContainer;
    if (container && container.parentElement === foil) {
      container.style.display = '';
      this._prebakedShatterContainer = null;
    } else {
      const theme = this._selectedPackId || 'core_pack';
      container = this._buildShatterContainer(theme);
      foil.appendChild(container);
    }

    // Clean fade-out before Beat 6 fires (scaled by dMult so mobile 0.52x tears remove shards before executePackOpen)
    const dMult = this._isMobile() ? 0.52 : (this.durationMultiplier || 0.80);
    this._scheduleTimeout(() => {
      if (container) {
        container.style.transition = 'opacity 0.2s ease';
        container.style.opacity = '0';
      }
    }, Math.round(780 * dMult));
    this._scheduleTimeout(() => {
      if (container) container.remove();
    }, Math.round(960 * dMult));
  }

  _getCardFrontImageUrl(card) {
    if (!card || card.type === 'ticket') return null;
    const isGold = card.type === 'special_edition';
    const cardName = card.cardName || card.title || '';
    const faceUrl = getCardFaceUrl(cardName, isGold);
    if (faceUrl) return faceUrl;
    const rawCard = getCardByName(cardName) || card;
    return getCardThumbUrl(card.image || rawCard?.images?.[0] || 'Marine_1.png');
  }

  executePackOpen() {
    this._stopVfxLoop();
    this.sparks = [];
    this.giantArcs = [];
    if (this.arcsCanvas) {
      this.arcsCanvas.width = 0;
      this.arcsCanvas.height = 0;
    }
    if (this.sparksCanvas) {
      this.sparksCanvas.width = 0;
      this.sparksCanvas.height = 0;
    }
    this._releaseAllPlanets();
    document.getElementById('cargo-scene')?.classList.remove('tearing-active');
    document.getElementById('cargo-scene')?.classList.remove('shake', 'shake-hard', 'lockin-jolt');
    document.getElementById('cargo-vignette')?.classList.remove('active');
    document.getElementById('cargo-surge-bloom')?.classList.remove('active');
    document.getElementById('cargo-surge-burst')?.classList.remove('active');
    document.getElementById('cargo-surge-ring')?.classList.remove('active');
    document.getElementById('cargo-surge-blast')?.classList.remove('active');
    document.getElementById('pack-hearthstone-rays')?.classList.remove('active');
    if (typeof document !== 'undefined' && typeof document.querySelectorAll === 'function') {
      document.querySelectorAll('.pack-charge-container, .pack-vent-container, .pack-shatter-container').forEach(el => el.remove?.());
    }
    if (this._prebakedShatterContainer) {
      this._prebakedShatterContainer.remove?.();
      this._prebakedShatterContainer = null;
    }
    if (this.stageContainer && this.stageContainer.classList?.contains?.('stage-booster')) {
      this.stageContainer.innerHTML = '';
      this.stageContainer.classList.remove('stage-booster');
    }

    // Use pre-rolled result from startPackTear(), or roll now if executed directly
    let result = this._currentPackResult;
    if (!result) {
      const packCount = this._selectedPackCount || 1;
      result = packs.openPackBatch(this._selectedPackId || 'core_pack', packCount);
      if (!result.success) {
        this._state = 'idle';
        this.renderBuyStage();
        return;
      }
      this._currentPackResult = result;
      this._cardsRevealedCount = 0;
      this._predecodePromise = this._predecodeCardArt(result.cards, 4500);
    }

    this._state = 'opened';
    // The wallet/inventory write already happened at startPackTear(); only the listener fan-out
    // (pills, achievements, cosmetics, telemetry) was held back so it stays off the tear's first frame.
    // On mobile, defer listener fan-out past the initial reveal stage mount and Card 0 flip (750ms)
    // so synchronous DOM/achievement callbacks never collide with 3D layer creation.
    if (this._isMobile()) {
      this._scheduleTimeout(() => packs.flushNotifications(), 1450);
    } else {
      packs.flushNotifications();
    }
    // Predecode was initiated at startPackTear() and had 2.5s-4.0s of animation window.
    // Wait at most 400ms more if still decoding, so slow network never hangs the reveal.
    const waitPromise = this._predecodePromise
      ? Promise.race([
          this._predecodePromise,
          new Promise((resolve) => setTimeout(resolve, 400)),
        ])
      : Promise.resolve();

    waitPromise.then(() => {
      if (this._currentPackResult !== result || this._state !== 'opened') return;
      this.renderCardsRevealStage(result);
    });
  }

  _clearPredecodedArt() {
    if (Array.isArray(this._predecodedArt)) {
      for (const img of this._predecodedArt) {
        try {
          img.onload = null;
          img.onerror = null;
          img.src = '';
        } catch {}
      }
    }
    this._predecodedArt = null;
  }

  // Resolves once every art image of the pulled cards is decoded, or after maxWaitMs, whichever
  // comes first - a slow network must never hold the reveal hostage. The Image objects stay
  // referenced in _predecodedArt until the next pack so the decoded bitmaps stay in the image cache.
  _predecodeCardArt(cards, maxWaitMs = 5000) {
    this._clearPredecodedArt();
    this._predecodedArt = [];
    if (typeof Image === 'undefined') return Promise.resolve();
    const jobs = [];
    const isMobile = this._isMobile();
    // On mobile with multi-pack (>5 cards), predecode only Card 0 (the single card visible in carousel)
    // during the tear VFX window to prevent WebKit memory pressure/jetsam kills during Beat 4 detonation.
    const initialBatchCount = isMobile ? ((cards?.length || 0) > 5 ? 1 : 2) : (cards?.length || 0);
    const initialCards = cards?.slice(0, initialBatchCount) || [];
    const remainingCards = cards?.slice(initialBatchCount) || [];

    for (const card of initialCards) {
      if (card?.type === 'ticket') continue;
      const primaryUrl = this._getCardFrontImageUrl(card);
      if (primaryUrl) {
        const img = new Image();
        img.src = primaryUrl;
        this._predecodedArt.push(img);
        jobs.push(typeof img.decode === 'function' ? img.decode().catch(() => {}) : Promise.resolve());
      } else if (card?.image && card.image !== 'ticket_icon') {
        const thumbUrl = getCardThumbUrl(card.image);
        if (thumbUrl) {
          const tImg = new Image();
          tImg.src = thumbUrl;
          this._predecodedArt.push(tImg);
          jobs.push(typeof tImg.decode === 'function' ? tImg.decode().catch(() => {}) : Promise.resolve());
        }
      }
    }

    if (remainingCards.length > 0 && !isMobile) {
      const decodeNextSlice = (startIdx = 0) => {
        if (this._state !== 'tearing' && this._state !== 'opened') return;
        const slice = remainingCards.slice(startIdx, startIdx + 4);
        if (!slice.length) return;
        for (const card of slice) {
          if (card?.type === 'ticket') continue;
          const primaryUrl = this._getCardFrontImageUrl(card);
          if (primaryUrl) {
            const img = new Image();
            img.src = primaryUrl;
            this._predecodedArt.push(img);
            if (typeof img.decode === 'function') img.decode().catch(() => {});
          } else if (card?.image && card.image !== 'ticket_icon') {
            const thumbUrl = getCardThumbUrl(card.image);
            if (thumbUrl) {
              const tImg = new Image();
              tImg.src = thumbUrl;
              this._predecodedArt.push(tImg);
              if (typeof tImg.decode === 'function') tImg.decode().catch(() => {});
            }
          }
        }
        if (startIdx + 4 < remainingCards.length) {
          setTimeout(() => decodeNextSlice(startIdx + 4), 120);
        }
      };
      setTimeout(() => decodeNextSlice(0), 400);
    }

    if (!jobs.length) return Promise.resolve();
    return Promise.race([
      Promise.all(jobs),
      new Promise((resolve) => setTimeout(resolve, maxWaitMs)),
    ]);
  }

  renderCardsRevealStage(result) {
    if (!this.stageContainer) return;
    this._resetWarp();
    this._isAutoRevealing = false;
    this._isWarpSpeed = false;
    this._autoSwipeTimeoutId = null;
    this._desktopRevealTimers = [];
    this._goldAscendEndTime = 0;
    this.stageContainer.classList.remove('stage-booster');
    document.getElementById('cargo-scene')?.classList.add('cards-revealed');

    const totalCards = result.cards?.length || 5;
    const packCount = Math.max(1, Math.min(5, Math.ceil(totalCards / 5)));
    // Card flip animation speed scales with pack count:
    // 1 pack: 0.65s, 2 packs: 0.55s, 3 packs: 0.45s, 4 packs: 0.35s, 5 packs: 0.25s
    const flipDurationSec = Math.max(0.25, +(0.65 - (packCount - 1) * 0.10).toFixed(2));
    this._flipDurationSec = flipDurationSec;
    const isMobile = this._isMobile();
    const isMultiPack = (result.cards?.length || 0) > 5;
    const initialVisibleSlots = isMobile ? 3 : 5;

    this.stageContainer.innerHTML = `
      <div class="pack-reveal-view">
        <div class="pack-legend-dimmer" id="pack-legend-dimmer"></div>

        <div class="pack-reveal-stage-center">
          <div class="pack-cards-row ${result.cards.length > 5 ? 'is-scrollable' : ''}" id="pack-cards-row" style="--flip-duration: ${flipDurationSec}s;">
            ${result.cards.map((card, idx) => {
              const slotClass = (isMultiPack && idx >= initialVisibleSlots) ? 'pack-card-slot' : 'pack-card-slot fan-deal-in';
              const isCulled3d = isMultiPack && idx > (isMobile ? 2 : 4);
              const culled3dClass = isCulled3d ? ' is-culled-3d' : '';
              const deferFrontDom = isCulled3d && isMobile;
              return `
              <div class="${slotClass}" data-index="${idx}" style="--slot-idx: ${idx};">
                <div class="pack-card-3d${culled3dClass}" id="pack-card-${idx}"${deferFrontDom ? ' data-front-deferred="1"' : ''}>
                  <!-- Card Back -->
                  <div class="card-face card-face-back">
                    <div class="card-back-pattern"></div>
                    <div class="card-back-logo">⬡</div>
                    <div class="card-back-tap-hint">TAP</div>
                  </div>

                  <!-- Card Front -->
                  <div class="card-face card-face-front card-type-${card.type} rarity-${(card.rarity || 'common').toLowerCase()} ${card.type === 'special_edition' ? 'card-gold-edition' : ''}">
                    <div class="card-reveal-glint"></div>
                    ${deferFrontDom ? '' : this._renderCardFrontHtml(card, idx)}
                  </div>
                </div>
              </div>
            `;
            }).join('')}
          </div>
          <div class="pack-scroll-dots ${result.cards.length > 9 ? 'is-carousel' : ''}" id="pack-scroll-dots"${(!this._usesSwipeReveal() && result.cards.length <= 5) ? ' style="display: none !important;"' : ''}>
            <div class="pack-scroll-dots-track" id="pack-scroll-dots-track">
              ${result.cards.map((_, dotIdx) => `<span class="pack-scroll-dot" data-dot-idx="${dotIdx}"></span>`).join('')}
            </div>
          </div>
        </div>

        <!-- Footer zone: fixed-height container so the card row above
             never shifts when these buttons swap in/out -->
        <div class="pack-footer-zone">
          <div class="pack-reveal-actions" id="pack-reveal-actions">
            <button id="btn-reveal-all" class="hud-btn ctrl-flex btn-reveal-all pack-action-btn">
              ${getIconSvg('theme_cosmic', 12)} Reveal All
            </button>
          </div>
          <div class="pack-summary-bar" id="pack-summary-bar" style="display: none;"></div>
        </div>
      </div>
    `;

    // Drop predecoded image objects from JS heap immediately after mounting DOM
    this._clearPredecodedArt();

    this._currentMobileCardIdx = 0;
    this._lastCenteredSlotIdx = 0;
    this._lastCenteredDotIdx = 0;

    const cardsRow = document.getElementById('pack-cards-row');
    if (cardsRow) {
      cardsRow.scrollLeft = 0;
    }

    this._updateDotsCarousel(0, result.cards.length);

    // Mobile Hearthstone-style reveal: Card 0 (the first card on the far left) is automatically revealed once deal-in settles
    if (this._isMobile()) {
      this._scheduleTimeout(() => {
        if (this._state === 'opened') {
          this.revealCard(0);
        }
      }, 750);
    }

    // The whole reveal (view fade-in, every card's deal-in) enters from opacity 0, and those
    // animations run on the compositor. On a Galaxy Z Fold (Android Chrome, unfolded) they were
    // seen to never get past their first frame after the tear: the Cargo Bay stayed empty - no
    // cards, no Reveal All - until the app was minimised and restored, which restarts the
    // compositor. Once the entrance has had ample time (deal-in ends at 0.62s + 4 x 0.11s), any
    // of these animations still not finished is finished from the main thread, so the cards land
    // in their final state no matter what the compositor did. A no-op wherever they ran normally.
    const revealView = this.stageContainer.querySelector('.pack-reveal-view');
    this._scheduleTimeout(() => this._settleRevealEntrance(revealView), this._isMobile() ? 1250 : 1600);

    // Staggered card deal sound effect
    if (this.audio && !this._isAudioMuted()) {
      const dealCount = isMobile ? Math.min(result.cards.length, 3) : Math.min(result.cards.length, 10);
      for (let i = 0; i < dealCount; i++) {
        setTimeout(() => {
          if (!this._isAudioMuted()) this.audio.playCardDraw?.();
        }, 120 + i * 85);
      }
    }

    // First tap reveals; a second tap on an already-revealed card opens
    // the big inspect view instead - except for tickets, which jump directly
    // to the Cyber Slot Machine without inspecting.
    const cardSlots = this.stageContainer.querySelectorAll('.pack-card-slot');
    cardSlots.forEach(slot => {
      slot.addEventListener('click', () => {
        if (this._isAutoRevealing) return;
        if (this._isMobile() && (this._isSwiping || (Date.now() - this._lastDragEndTime < 260))) {
          return;
        }
        const idx = parseInt(slot.getAttribute('data-index'), 10);
        const cardEl = document.getElementById(`pack-card-${idx}`);
        const card = this._currentPackResult?.cards?.[idx];
        if (cardEl && cardEl.classList.contains('flipped')) {
          if (card && card.type === 'ticket') {
            hubNavigator.navigateTo('matrix');
            return;
          }
          return;
        } else {
          if (this._usesSwipeReveal() || (this._currentPackResult?.cards?.length || 0) > 5) {
            this._goToMobileCard(idx);
          } else {
            this.revealCard(idx);
          }
        }
      });
    });

    // Mobile & Multi-Pack: Tapping dots switches to the respective card and reveals it
    const scrollDots = this.stageContainer.querySelectorAll('.pack-scroll-dot');
    scrollDots.forEach((dot, dotIdx) => {
      dot.addEventListener('click', (e) => {
        e.stopPropagation();
        if (this._isAutoRevealing) return;
        if (this._usesSwipeReveal() || (this._currentPackResult?.cards?.length || 0) > 5) {
          this._goToMobileCard(dotIdx);
        }
      });
    });

    // Reveal All button
    const btnRevealAll = document.getElementById('btn-reveal-all');
    if (btnRevealAll) {
      const handleReveal = (e) => {
        if (e) {
          e.preventDefault();
          e.stopPropagation();
        }
        this.revealAllCards();
      };
      btnRevealAll.addEventListener('click', handleReveal);
      btnRevealAll.addEventListener('touchend', handleReveal, { passive: false });
    }

    // Mobile & Multi-Pack: the row scrolls; whichever card is nearest the row's center
    // gets the centered highlight, and gallery dots update.
    if (cardsRow) {
      cardsRow.addEventListener('scroll', () => {
        // Warp Speed drives the row itself; centring, dots and shimmer would only be work nobody sees.
        if (this._warpActive || this._dotRaf) return;
        this._dotRaf = requestAnimationFrame(() => {
          this._updateCenteredCard();
          this._dotRaf = null;
        });
      });

      // Mouse wheel horizontal scroll support on desktop
      cardsRow.addEventListener('wheel', (e) => {
        if ((this._currentPackResult?.cards?.length || 0) <= 5 && !this._usesSwipeReveal()) return;
        if (Math.abs(e.deltaX) < Math.abs(e.deltaY)) {
          cardsRow.scrollLeft += e.deltaY;
          e.preventDefault();
        }
      }, { passive: false });

      // Mouse drag-to-scroll support for desktop
      this._cleanupDragListeners();
      let isMouseDown = false;
      let startX = 0;
      let scrollLeftStart = 0;
      let hasDragged = false;

      cardsRow.addEventListener('mousedown', (e) => {
        if ((this._currentPackResult?.cards?.length || 0) <= 5 && !this._usesSwipeReveal()) return;
        if (e.button !== 0) return;
        isMouseDown = true;
        hasDragged = false;
        startX = e.pageX - cardsRow.offsetLeft;
        scrollLeftStart = cardsRow.scrollLeft;
      });

      this._onCardsRowMouseMove = (e) => {
        if (!isMouseDown) return;
        const x = e.pageX - cardsRow.offsetLeft;
        const walk = x - startX;
        if (Math.abs(walk) > 5) {
          hasDragged = true;
          cardsRow.scrollLeft = scrollLeftStart - walk;
        }
      };

      this._onCardsRowMouseUp = () => {
        if (isMouseDown && hasDragged) {
          this._lastDragEndTime = Date.now();
        }
        isMouseDown = false;
      };

      window.addEventListener('mousemove', this._onCardsRowMouseMove);
      window.addEventListener('mouseup', this._onCardsRowMouseUp);
    }

    // Mobile touch drag & horizontal swipe detection (swipe left to advance, swipe right for previous)
    if (this._isMobile() && cardsRow) {
      let touchStartX = 0;
      let touchStartY = 0;
      let touchStartTime = 0;
      let isTracking = false;
      let hasSwiped = false;

      cardsRow.addEventListener('touchstart', (e) => {
        if (!this._usesSwipeReveal() || this._isAutoRevealing) return;
        if (!e.touches || e.touches.length !== 1) return;
        if (e.target.closest('button')) return;
        touchStartX = e.touches[0].clientX;
        touchStartY = e.touches[0].clientY;
        touchStartTime = Date.now();
        isTracking = true;
        hasSwiped = false;
        this._isSwiping = false;
      }, { passive: true });

      cardsRow.addEventListener('touchmove', (e) => {
        if (!this._usesSwipeReveal() || this._isAutoRevealing || !isTracking || !e.touches || e.touches.length !== 1) return;
        const dx = e.touches[0].clientX - touchStartX;
        const dy = e.touches[0].clientY - touchStartY;
        if (Math.abs(dx) > 10 && Math.abs(dx) > Math.abs(dy) * 1.2) {
          hasSwiped = true;
          this._isSwiping = true;
        }
      }, { passive: true });

      const onTouchEnd = (e) => {
        if (!isTracking) return;
        isTracking = false;
        if (!this._usesSwipeReveal() || this._isAutoRevealing) return;
        const changedTouch = e.changedTouches?.[0];
        if (changedTouch) {
          const dx = changedTouch.clientX - touchStartX;
          const dt = Math.max(1, Date.now() - touchStartTime);
          const vx = Math.abs(dx) / dt;
          const isFlick = vx > 0.22 && Math.abs(dx) > 20;
          const isOverThreshold = Math.abs(dx) > 40;

          if (isFlick || isOverThreshold) {
            this._lastDragEndTime = Date.now();
            const dir = dx < 0 ? 1 : -1;
            const maxCardIdx = Math.max(0, (this._currentPackResult?.cards?.length || 5) - 1);
            const targetIdx = Math.max(0, Math.min(maxCardIdx, (this._currentMobileCardIdx || 0) + dir));
            this._goToMobileCard(targetIdx);
          }
        }
        if (hasSwiped) {
          this._lastDragEndTime = Date.now();
        }
        setTimeout(() => {
          this._isSwiping = false;
          this._updateCenteredCard();
          if (this._state === 'opened' && !this._isAutoRevealing) {
            const currentCard = document.getElementById(`pack-card-${this._currentMobileCardIdx}`);
            if (currentCard && !currentCard.classList.contains('flipped')) {
              this.revealCard(this._currentMobileCardIdx);
            }
          }
        }, 120);
      };

      cardsRow.addEventListener('touchend', onTouchEnd, { passive: true });
      cardsRow.addEventListener('touchcancel', onTouchEnd, { passive: true });
    }

    this._slotCenters = null;
    this._updateCenteredCard();
    this._updateBalanceFooter();
  }

  // See renderCardsRevealStage(): finishes the reveal's entrance animations if they stalled.
  _settleRevealEntrance(revealView) {
    if (this._state !== 'opened' || !revealView?.isConnected) return;
    if (typeof revealView.getAnimations !== 'function') return;
    const ENTRANCE = new Set(['revealViewFadeIn', 'fanDealArc']);
    let settled = 0;
    for (const anim of revealView.getAnimations({ subtree: true })) {
      if (!ENTRANCE.has(anim.animationName) || anim.playState === 'finished') continue;
      try {
        anim.finish();
        settled++;
      } catch { /* an infinite or detached animation cannot be finished - leave it */ }
    }
    const slots = revealView.querySelectorAll?.('.pack-card-slot');
    slots?.forEach(slot => {
      slot.classList.remove('fan-deal-in');
      slot.style.opacity = '1';
    });
    if (settled > 0) {
      console.warn(`[PackOpening] ${settled} reveal entrance animation(s) had stalled and were finished manually`);
    }
  }

  _goToMobileCard(targetIdx) {
    const isMultiPack = (this._currentPackResult?.cards?.length || 0) > 5;
    if (!this._isMobile() && !isMultiPack) return;
    if (this._isMobile() && !this._usesSwipeReveal() && !isMultiPack) return;
    const cardsRow = document.getElementById('pack-cards-row');
    if (!cardsRow) return;
    const slots = cardsRow.querySelectorAll('.pack-card-slot');
    if (targetIdx < 0 || targetIdx >= slots.length) return;

    this._currentMobileCardIdx = targetIdx;
    this._lastCenteredDotIdx = targetIdx;
    this._hydrateNearbyCardImages(targetIdx);
    this._updateDotsCarousel(targetIdx, slots.length);

    if (!this._slotCenters || this._slotCenters.length !== slots.length) {
      this._slotCenters = [];
      for (let i = 0; i < slots.length; i++) {
        const s = slots[i];
        this._slotCenters[i] = s ? (s.offsetLeft + s.offsetWidth / 2) : 0;
      }
    }

    let targetScrollLeft = 0;
    if (this._usesSwipeReveal()) {
      const slotCenter = (this._slotCenters && this._slotCenters[targetIdx])
        ? this._slotCenters[targetIdx]
        : (slots[targetIdx] ? (slots[targetIdx].offsetLeft + slots[targetIdx].offsetWidth / 2) : 0);
      targetScrollLeft = Math.max(0, slotCenter - cardsRow.clientWidth / 2);
    } else {
      const maxScroll = cardsRow.scrollWidth - cardsRow.clientWidth;
      targetScrollLeft = maxScroll > 0 ? (targetIdx / (slots.length - 1)) * maxScroll : 0;
    }

    // Disable CSS scroll-snap temporarily during programmatic smooth scroll so WebKit's
    // compositor momentum/snap engine does not fight the smooth-scroll interpolation.
    cardsRow.style.scrollSnapType = 'none';
    if (this._isWarpSpeed) {
      if (typeof cardsRow.scrollTo === 'function') {
        cardsRow.scrollTo({
          left: targetScrollLeft,
          behavior: 'auto'
        });
      } else {
        cardsRow.scrollLeft = targetScrollLeft;
      }
    } else {
      cardsRow.scrollTo({
        left: targetScrollLeft,
        behavior: 'smooth'
      });
      if (this._snapRestoreTimer) clearTimeout(this._snapRestoreTimer);
      this._snapRestoreTimer = setTimeout(() => {
        if (cardsRow) cardsRow.style.scrollSnapType = '';
      }, 380);
    }

    // Reveal any previous cards that were swiped past so nothing stays unrevealed
    for (let i = 0; i < targetIdx; i++) {
      const prevCard = document.getElementById(`pack-card-${i}`);
      if (prevCard && !prevCard.classList.contains('flipped')) {
        this.revealCard(i);
      }
    }

    // Reveal the card once it has settled into center
    if (!this._isAutoRevealing) {
      this._scheduleTimeout(() => {
        if (this._state === 'opened') {
          this.revealCard(targetIdx);
        }
      }, 280);
    }
  }

  _updateCenteredCard() {
    // Purely a touch substitute for :hover (see CSS: gated to (hover:none)
    // there too) - skip the work entirely on hover-capable devices rather
    // than compute classes that would just sit inert, unless multi-pack is open
    // and gallery dots need centering synchronization.
    if (this._isHoverCapable === undefined) {
      this._isHoverCapable = window.matchMedia('(hover: hover)').matches;
    }
    const isMultiPack = (this._currentPackResult?.cards?.length || 0) > 5;
    if ((this._isHoverCapable || !this._usesSwipeReveal()) && !isMultiPack) return;
    const cardsRow = document.getElementById('pack-cards-row');
    const dotsEl = document.getElementById('pack-scroll-dots');
    if (!cardsRow) return;
    const slots = cardsRow.children;
    if (!slots || slots.length === 0) return;

    // Cache slot center offsets once to eliminate layout thrashing during touch scroll
    if (!this._slotCenters || this._slotCenters.length !== slots.length) {
      this._slotCenters = [];
      for (let i = 0; i < slots.length; i++) {
        const s = slots[i];
        if (s.classList.contains('pack-card-slot')) {
          this._slotCenters[i] = s.offsetLeft + s.offsetWidth / 2;
        } else {
          this._slotCenters[i] = 0;
        }
      }
    }

    const rowScrollLeft = cardsRow.scrollLeft;
    const rowCenterOffset = rowScrollLeft + cardsRow.clientWidth / 2;

    let closestIdx = 0;
    if (this._usesSwipeReveal()) {
      let closestDist = Infinity;
      for (let i = 0; i < slots.length; i++) {
        const s = slots[i];
        if (!s.classList.contains('pack-card-slot')) continue;
        const slotCenter = this._slotCenters[i];
        const dist = Math.abs(slotCenter - rowCenterOffset);
        if (dist < closestDist) { closestDist = dist; closestIdx = i; }
      }
    } else {
      const maxScroll = cardsRow.scrollWidth - cardsRow.clientWidth;
      if (maxScroll > 0) {
        const progress = Math.max(0, Math.min(1, rowScrollLeft / maxScroll));
        closestIdx = Math.min(slots.length - 1, Math.round(progress * (slots.length - 1)));
      } else {
        closestIdx = 0;
      }
    }

    if (dotsEl && closestIdx !== this._lastCenteredDotIdx) {
      this._lastCenteredDotIdx = closestIdx;
      this._updateDotsCarousel(closestIdx, slots.length);
    }

    if (this._isMobile() && this._state === 'opened') {
      if (this._usesSwipeReveal()) {
        const idxChanged = closestIdx !== this._currentMobileCardIdx;
        this._currentMobileCardIdx = closestIdx;
        if (!this._isAutoRevealing) {
          const cardEl = document.getElementById(`pack-card-${closestIdx}`);
          if (cardEl && !cardEl.classList.contains('flipped')) {
            if (idxChanged || !this._cardRevealDebounceTimer) {
              if (this._cardRevealDebounceTimer) {
                clearTimeout(this._cardRevealDebounceTimer);
              }
              const delay = this._isSwiping ? 180 : 80;
              this._cardRevealDebounceTimer = setTimeout(() => {
                this._cardRevealDebounceTimer = null;
                if (this._state === 'opened') {
                  this.revealCard(closestIdx);
                }
              }, delay);
            }
          }
        }
      }
    }

    if (closestIdx !== this._lastCenteredSlotIdx) {
      this._lastCenteredSlotIdx = closestIdx;
      this._hydrateNearbyCardImages(closestIdx);
      for (let i = 0; i < slots.length; i++) {
        const s = slots[i];
        if (!s.classList.contains('pack-card-slot')) continue;
        const isCentered = i === closestIdx;
        const wasCentered = s.classList.contains('centered');
        s.classList.toggle('centered', isCentered);
        if (isCentered && !wasCentered) {
          s.classList.remove('just-centered');
          if (!this._isSwiping) {
            requestAnimationFrame(() => {
              s.classList.add('just-centered');
            });
          }
        }
      }
    }
  }

  _ensureCardFrontMounted(idx) {
    const cardEl = document.getElementById(`pack-card-${idx}`);
    if (!cardEl || !cardEl.dataset?.frontDeferred) return cardEl;
    delete cardEl.dataset.frontDeferred;
    const card = this._currentPackResult?.cards?.[idx];
    const frontEl = cardEl.querySelector?.('.card-face-front');
    if (card && frontEl) {
      frontEl.innerHTML = `<div class="card-reveal-glint"></div>${this._renderCardFrontHtml(card, idx)}`;
    }
    return cardEl;
  }

  _hydrateNearbyCardImages(centerIdx) {
    if (!this.stageContainer) return;
    const total = this._currentPackResult?.cards?.length || 0;
    const isMultiPack = total > 5;
    const isMobile = this._isMobile();
    const min = Math.max(0, centerIdx - 1);
    const max = centerIdx + (isMobile ? 2 : 3);
    for (let i = min; i <= max; i++) {
      const cardEl = this._ensureCardFrontMounted(i);
      const img = cardEl?.querySelector?.('.pack-card-baked-img[data-src]');
      if (img && img.dataset?.src) {
        img.src = img.dataset.src;
        delete img.dataset.src;
      }
    }
    if (isMultiPack) {
      const windowRadius = isMobile ? 2 : 4;
      for (let i = 0; i < total; i++) {
        const cardEl = document.getElementById(`pack-card-${i}`);
        if (!cardEl) continue;
        const inWindow = Math.abs(i - centerIdx) <= windowRadius;
        if (inWindow || cardEl.classList?.contains?.('flipping')) {
          this._ensureCardFrontMounted(i);
          cardEl.classList?.remove?.('is-culled-3d');
        } else {
          cardEl.classList?.add?.('is-culled-3d');
        }
      }
    }
  }

  _updateDotsCarousel(activeIdx, totalCards) {
    const dotsContainer = document.getElementById('pack-scroll-dots');
    if (!dotsContainer) return;
    const track = document.getElementById('pack-scroll-dots-track') || dotsContainer;
    const dots = dotsContainer.querySelectorAll('.pack-scroll-dot');
    if (!dots || dots.length === 0) return;

    const count = totalCards || dots.length;
    const windowSize = 9;
    const halfWindow = Math.floor(windowSize / 2); // 4

    if (count <= windowSize) {
      track.style.transform = '';
      for (let i = 0; i < dots.length; i++) {
        const dot = dots[i];
        dot.classList.toggle('active', i === activeIdx);
        dot.classList.remove('dot-edge', 'dot-sub-edge', 'dot-mid-edge', 'dot-hidden');
      }
      return;
    }

    const maxOffset = count - windowSize;
    const offsetIndex = Math.max(0, Math.min(activeIdx - halfWindow, maxOffset));

    let stepPx = 13;
    if (dots.length >= 2 && typeof dots[1].offsetLeft === 'number' && dots[1].offsetLeft > dots[0].offsetLeft) {
      stepPx = dots[1].offsetLeft - dots[0].offsetLeft;
    }

    const trackTranslateX = -(offsetIndex * stepPx);
    track.style.transform = `translateX(${trackTranslateX}px)`;

    const windowStart = offsetIndex;
    const windowEnd = offsetIndex + windowSize - 1;

    for (let i = 0; i < dots.length; i++) {
      const dot = dots[i];
      const isActive = (i === activeIdx);
      dot.classList.toggle('active', isActive);

      dot.classList.remove('dot-edge', 'dot-sub-edge', 'dot-mid-edge', 'dot-hidden');

      if (isActive) {
        continue;
      }

      if (i < windowStart || i > windowEnd) {
        dot.classList.add('dot-hidden');
      } else if (i === windowStart && windowStart > 0) {
        dot.classList.add('dot-edge');
      } else if (i === windowStart + 1 && windowStart > 0) {
        dot.classList.add('dot-sub-edge');
      } else if (i === windowStart + 2 && windowStart > 0) {
        dot.classList.add('dot-mid-edge');
      } else if (i === windowEnd && windowEnd < count - 1) {
        dot.classList.add('dot-edge');
      } else if (i === windowEnd - 1 && windowEnd < count - 1) {
        dot.classList.add('dot-sub-edge');
      } else if (i === windowEnd - 2 && windowEnd < count - 1) {
        dot.classList.add('dot-mid-edge');
      }
    }
  }

  _renderCardFrontHtml(card, idx = 0) {
    if (card.type === 'ticket') {
      return `
        <div class="ticket-card-content">
          <div class="ticket-glow-circle"></div>
          <div class="ticket-icon">${getIconSvg('ticket', 72)}</div>
          <div class="ticket-title">SLOT TICKET</div>
          <div class="ticket-badge">CYBER RARE</div>
          <div class="ticket-desc">1 Free Spin for the Slot Machine</div>
        </div>
      `;
    }

    const isGold = card.type === 'special_edition';
    const cardName = card.cardName || card.title || '';
    const faceUrl = getCardFaceUrl(cardName, isGold);
    const newBadgeHtml = card.isNew ? `
      <div class="pack-card-new-badge">
        <span class="badge-star">★</span> NEW <span class="badge-star">★</span>
      </div>
    ` : '';
    const goldSheenHtml = isGold ? '<div class="card-gold-holo-sheen"></div>' : '';

    if (faceUrl) {
      const isMultiPack = (this._currentPackResult?.cards?.length || 0) > 5;
      const isEager = !isMultiPack || idx < (this._isMobile() ? 2 : 5);
      const srcAttr = isEager ? `src="${faceUrl}"` : `data-src="${faceUrl}"`;
      return `
        <div class="card-face-skeleton" aria-hidden="true">
          <div class="card-skeleton-radar"></div>
          <div class="card-skeleton-text">DECRYPTING ARCHIVE</div>
        </div>
        <img ${srcAttr} class="pack-card-baked-img" alt="${cardName}" draggable="false" loading="lazy" decoding="async"
             onload="const s = this.parentElement?.querySelector('.card-face-skeleton'); if (s) s.style.display='none';"
             onerror="this.style.display='none'; const t = this.parentElement?.querySelector('.card-fallback-template'); const fb = this.parentElement?.querySelector('.card-fallback-wrapper'); if (t && fb) { fb.appendChild(t.content.cloneNode(true)); fb.style.display='block'; }" />
        <template class="card-fallback-template">
          ${this._renderDynamicCardFront(card, cardName, isGold, goldSheenHtml)}
        </template>
        <div class="card-fallback-wrapper" style="display: none; width: 100%; height: 100%;"></div>
        ${goldSheenHtml}
        ${newBadgeHtml}
      `;
    }

    return `
      <div class="card-face-skeleton" aria-hidden="true">
        <div class="card-skeleton-radar"></div>
        <div class="card-skeleton-text">DECRYPTING ARCHIVE</div>
      </div>
      ${this._renderDynamicCardFront(card, cardName, isGold, goldSheenHtml)}
      ${newBadgeHtml}
    `;
  }

  _getDynamicCardTitleStyle(name) {
    if (!name) return '';
    const len = name.length;
    if (len > 22) return 'font-size: 6.2px; letter-spacing: 0px;';
    if (len > 18) return 'font-size: 6.9px; letter-spacing: 0px;';
    if (len > 14) return 'font-size: 7.8px; letter-spacing: 0.1px;';
    return 'font-size: 8.7px; letter-spacing: 0.2px;';
  }

  _getDynamicCardDescStyle(descLen, loreLen, total) {
    if (total > 260) return 'font-size: 9.0px; line-height: 1.18;';
    if (total > 195) return 'font-size: 9.8px; line-height: 1.20;';
    if (total > 140) {
      if (descLen <= 60) return 'font-size: 11.2px; line-height: 1.24;';
      return 'font-size: 10.8px; line-height: 1.22;';
    }
    if (total > 95) return 'font-size: 11.8px; line-height: 1.25;';
    return 'font-size: 12.6px; line-height: 1.26;';
  }

  _getDynamicCardLoreStyle(descLen, loreLen, total) {
    if (total > 260) return 'font-size: 8.2px; line-height: 1.18;';
    if (total > 195) return 'font-size: 9.0px; line-height: 1.19;';
    if (total > 140) {
      if (loreLen > 100) return 'font-size: 9.6px; line-height: 1.22;';
      return 'font-size: 10.2px; line-height: 1.22;';
    }
    if (total > 95) return 'font-size: 10.8px; line-height: 1.23;';
    return 'font-size: 11.4px; line-height: 1.24;';
  }

  _renderDynamicCardFront(card, cardName, isGold, goldSheenHtml) {
    const rawCard = getCardByName(cardName) || card;
    const cost = card.cost ?? rawCard.cost ?? 0;
    const imgPath = getCardThumbUrl(card.image || rawCard.images?.[0] || 'Marine_1.png');
    const desc = card.description || rawCard.description || '';
    const lore = rawCard.lore || '';
    const descLen = (desc || '').length;
    const loreLen = (lore || '').length;
    const total = descLen + loreLen;
    const titleStyle = this._getDynamicCardTitleStyle(cardName);
    const descStyle = this._getDynamicCardDescStyle(descLen, loreLen, total);
    const loreStyle = this._getDynamicCardLoreStyle(descLen, loreLen, total);
    const rarity = (card.rarity || rawCard.rarity || 'common').toLowerCase();
    const rarityClass = `rarity-${rarity}`;
    const meltClass = (rawCard.type === 'Melt' || rawCard.isMelt) ? 'card-type-melt' : '';
    let casteClass = rawCard.caste ? `caste-${rawCard.caste.toLowerCase()}` : '';
    if (!casteClass && rawCard.race === 'Sul') casteClass = 'race-sul-plasma';
    if (!casteClass && rawCard.race !== 'Sul' && (meltClass || rawCard.name?.startsWith('Hyper '))) casteClass = 'caste-hyper';
    const raceClass = rawCard.race ? `race-${rawCard.race.toLowerCase()}` : '';

    const displayCost = card.costDisplay || (cost > 0 ? cost : 'FREE');
    const costFreeClass = (!card.costDisplay && cost <= 0) ? ' card-cost-free' : '';

    return `
      <div class="card-slot card-zoom-view ${rarityClass} ${raceClass} ${meltClass} ${casteClass} filled zoom-visible" style="width: 100%; height: 100%; margin: 0; border: none; background: transparent;">
        <div class="card-inner ${isGold ? 'card-gold-edition' : ''}">
          ${goldSheenHtml}
          <img src="${imgPath}" alt="${cardName}" class="card-bg-img" draggable="false" />
          <div class="card-content-overlay">
            <div class="card-header">
              <span class="card-name" style="${titleStyle}" title="${cardName}">${cardName}</span>
              <div class="card-header-right">
                <span class="card-cost${costFreeClass}">${displayCost}</span>
              </div>
            </div>
            <div class="card-spacer"></div>
            <div class="card-body" style="padding: 16px 8px 9px 8px; background: linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, rgba(4, 7, 12, 0.72) 20%, rgba(2, 4, 8, 0.96) 100%);">
              ${desc ? `<p class="card-desc" style="${descStyle}; font-family: 'Rajdhani', sans-serif; font-weight: 600; color: #f1f5f9; text-shadow: 0 1px 2px #000, 0 0 5px rgba(0,0,0,0.98); -webkit-line-clamp: 14;">${desc}</p>` : ''}
              ${lore ? `<p class="card-lore" style="${loreStyle}; font-family: 'Rajdhani', sans-serif; font-weight: 500; font-style: italic; color: #cbd5e1; text-shadow: 0 1px 2px #000, 0 0 5px rgba(0,0,0,0.98); -webkit-line-clamp: 6; ${desc ? 'margin-top: 5px; padding-top: 4px; border-top: 1px solid rgba(203, 213, 225, 0.28);' : ''}">${lore}</p>` : ''}
            </div>
          </div>
        </div>
      </div>
    `;
  }

  revealCard(idx, audioOpts = {}) {
    const cardEl = this._ensureCardFrontMounted(idx);
    if (!cardEl || cardEl.classList.contains('flipped') || cardEl.dataset?.revealing === 'true') return;
    cardEl.classList?.remove?.('is-culled-3d');

    if (!cardEl.querySelector) cardEl.querySelector = () => null;
    // Smart Reveal-Gating (Fallback 3): Check if card image is already loaded and decoded
    const imgEl = cardEl.querySelector('.pack-card-baked-img, .card-bg-img');
    if (imgEl && imgEl.dataset?.src && !imgEl.src) {
      imgEl.src = imgEl.dataset.src;
      delete imgEl.dataset.src;
    }
    const isImageReady = !imgEl || imgEl.complete;

    const performFlip = () => {
      if (cardEl.dataset) delete cardEl.dataset.revealing;
      cardEl.classList?.remove?.('reveal-hold');
      if (cardEl.classList.contains('flipped')) return;

      cardEl.classList?.remove?.('is-culled-3d');
      cardEl.classList.add('flipped');
      cardEl.classList.add('flipping');
      setTimeout(() => {
        cardEl.classList?.remove?.('flipping');
        const total = this._currentPackResult?.cards?.length || 0;
        if (total > 5 && !this._warpActive) {
          const activeIdx = this._currentMobileCardIdx || 0;
          const windowRadius = this._isMobile() ? 2 : 4;
          if (Math.abs(idx - activeIdx) > windowRadius) {
            cardEl.classList?.add?.('is-culled-3d');
          }
        }
      }, Math.round((this._flipDurationSec || 0.65) * 1000 + 60));
      this._cardsRevealedCount++;

      const cardData = this._currentPackResult?.cards?.[idx];
      if (cardData) {
        if (cardData.type === 'special_edition') {
          // The 1.5 s ascension (dimmer + every other slot filtered) would fight the cascade; at warp
          // speed a Gold Edition gets a short sting instead.
          if (this._warpActive) this._triggerWarpGoldSting(cardEl.closest('.pack-card-slot'));
          else this._triggerGoldAscend(cardEl.closest('.pack-card-slot'));
          if (this.audio && !this._isAudioMuted()) this.audio.playGauss?.();
        } else if (cardData.type === 'ticket') {
          if (this._allowWarpSfx()) this._playCargoBaySfx('reveal_ticket_flip', { category: 'card_flip', ...audioOpts });
        } else if (this._allowWarpSfx()) {
          const sfxKey = (this._isWarpSpeed || audioOpts.registryKey === 'warp_domino_flip')
            ? 'warp_domino_flip'
            : 'reveal_normal_flip';
          this._playCargoBaySfx(sfxKey, { category: 'card_flip', ...audioOpts });
        }
      }

      const totalCards = this._currentPackResult?.cards?.length || 5;
      const allFlipped = () => {
        for (let i = 0; i < totalCards; i++) {
          const c = document.getElementById(`pack-card-${i}`);
          if (!c || !c.classList?.contains?.('flipped')) return false;
        }
        return true;
      };

      if (this._cardsRevealedCount >= totalCards || allFlipped()) {
        if (!this._isAutoRevealing) {
          if (!this._isWarpSpeed && cardData && cardData.type === 'special_edition') {
            this._scheduleTimeout(() => {
              this.showSummary();
            }, 1800);
          } else {
            this.showSummary();
          }
        }
      }
    };

    if (isImageReady) {
      performFlip();
    } else {
      if (!cardEl.dataset) cardEl.dataset = {};
      cardEl.dataset.revealing = 'true';
      cardEl.classList.add('reveal-hold');
      const safetyTimer = setTimeout(performFlip, 280);
      const onReady = () => {
        clearTimeout(safetyTimer);
        performFlip();
      };
      if (typeof imgEl.decode === 'function') {
        imgEl.decode().then(onReady).catch(onReady);
      } else {
        imgEl.onload = onReady;
        imgEl.onerror = onReady;
      }
    }
  }

  revealAllCards() {
    const btn = document.getElementById('btn-reveal-all');
    if (btn) {
      if (btn.disabled) return;
    }
    this._switchToWarpSpeedButton();

    // Wähle für den Reveal-All-Ablauf eine einheitliche Sounddatei aus dem Pool
    // (bevorzugt die knackige card-place-2.ogg oder den ersten Pool-Eintrag),
    // damit die Karten mit aufsteigender Tonhöhe wie ein edler Fächer klingen.
    const regFiles = this.audio?.registry?.cargo_bay?.reveal_normal_flip?.files;
    const chosenFile = (Array.isArray(regFiles) && regFiles.length > 0)
      ? (regFiles.includes('card-place-2.ogg') ? 'card-place-2.ogg' : regFiles[0])
      : 'card-place-2.ogg';

    // Mobile Portrait only: sequentially swipe cards horizontally across the carousel.
    // Gold cards pause swipe until their full ascension celebration completes.
    if (this._usesSwipeReveal()) {
      this._startMobileAutoSwipeReveal(chosenFile);
      return;
    }

    const totalCards = this._currentPackResult?.cards?.length || 5;
    if (totalCards > 5) {
      this._startMobileAutoSwipeReveal(chosenFile);
      return;
    }
    this._desktopRevealTimers = [];
    const delay = totalCards > 5 ? 70 : 140;
    for (let i = 0; i < totalCards; i++) {
      const tId = setTimeout(() => {
        // Fächer-Effekt: Tonhöhe steigt pro Karte dezent an (+3.5% pro Karte, max +17.5%)
        const rate = parseFloat((1.0 + (i % 5) * 0.035).toFixed(3));
        this.revealCard(i, { file: chosenFile, rate, category: 'card_flip' });
      }, i * delay);
      this._desktopRevealTimers.push(tId);
    }
  }

  _switchToWarpSpeedButton() {
    this._armWarp();
    const actionsEl = document.getElementById('pack-reveal-actions');
    const existingBtn = document.getElementById('btn-reveal-all');

    if (actionsEl && typeof actionsEl.innerHTML === 'string') {
      actionsEl.innerHTML = `
        <button id="btn-warp-speed" class="hud-btn ctrl-flex btn-warp-speed btn-reveal-all pack-action-btn" data-action="warp-speed">
          ${getIconSvg('energy', 12)} Warp Speed
        </button>
      `;
      const warpBtn = document.getElementById('btn-warp-speed');
      if (warpBtn) {
        const handleWarp = (e) => {
          if (e) {
            e.preventDefault();
            e.stopPropagation();
          }
          this.activateWarpSpeed();
        };
        warpBtn.addEventListener('click', handleWarp);
        warpBtn.addEventListener('touchend', handleWarp, { passive: false });
      }
    } else if (existingBtn) {
      existingBtn.id = 'btn-warp-speed';
      existingBtn.classList?.remove?.('btn-reveal-all');
      existingBtn.classList?.add?.('btn-warp-speed');
      existingBtn.innerHTML = `${getIconSvg('energy', 12)} Warp Speed`;
      existingBtn.disabled = false;
      if (existingBtn.style) {
        existingBtn.style.opacity = '1';
        existingBtn.style.pointerEvents = 'auto';
      }
      const handleWarp = (e) => {
        if (e) {
          e.preventDefault?.();
          e.stopPropagation?.();
        }
        this.activateWarpSpeed();
      };
      existingBtn.addEventListener?.('click', handleWarp);
      existingBtn.addEventListener?.('touchend', handleWarp, { passive: false });
    }
  }

  /* ============================================================
     WARP SPEED (accelerated Reveal All)
     Stage 1 = the Reveal All click arms it (layers promoted, faces decoded, overlay baked) while the
     normal-speed reveal plays. Stage 2 = the Warp click only starts animations: one flash in which the
     HUD swap hides, radial speed streaks on the existing sparks canvas, then ONE rAF scheduler flips the
     remaining cards. Every animated property is transform/opacity (no filter, no box-shadow), and the
     result is a compact haul view instead of 25 cards nobody could read at that speed.
     ============================================================ */

  _prefersReducedMotion() {
    try {
      return !!(typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches);
    } catch {
      return false;
    }
  }

  _armWarp() {
    if (this._warpArmed) return;
    this._warpArmed = true;
    if (typeof document === 'undefined') return;
    document.getElementById('cargo-scene')?.classList?.add?.('warp-armed');
    if (!isAbOn('nowarpfx') && typeof document.createElement === 'function') {
      try {
        this._ensureWarpOverlay();
        this._getStreakSprite(); // baked now, not in the click frame
      } catch { /* pure preparation: a failure here costs the flash, never the reveal */ }
    }
    this._hydrateWarpCards(0, 8);
  }

  // Back to the unarmed state: stops a running cascade (the generation token invalidates every timer
  // it scheduled), drops the streaks and releases the layers armed for the run.
  _resetWarp() {
    this._warpGen++;
    this._warpArmed = false;
    this._warpActive = false;
    this._warpGeom = null;
    this._warpStreaks = [];
    if (this._warpRaf && typeof cancelAnimationFrame === 'function') cancelAnimationFrame(this._warpRaf);
    this._warpRaf = null;
    try { this._warpFlashAnim?.cancel?.(); } catch { /* already finished */ }
    this._warpFlashAnim = null;
    if (typeof document === 'undefined') return;
    document.getElementById('cargo-scene')?.classList?.remove?.('warp-armed');
    this._warpFlashEl?.remove?.();
    this._warpFlashEl = null;
  }

  _ensureWarpOverlay() {
    if (this._warpFlashEl?.isConnected) return this._warpFlashEl;
    if (typeof document === 'undefined' || typeof document.createElement !== 'function') return null;
    const scene = document.getElementById('cargo-scene');
    if (!scene?.appendChild) return null;
    const el = document.createElement('div');
    el.id = 'cargo-warp-flash';
    el.className = 'cargo-warp-flash';
    el.setAttribute?.('aria-hidden', 'true');
    scene.appendChild(el);
    this._warpFlashEl = el;
    return el;
  }

  // Faces beyond the first screenful are lazy (data-src + loading="lazy"); the cascade outruns that,
  // so the ones it is about to reach are loaded eagerly and decoded off the main thread.
  _hydrateWarpCards(from, to) {
    if (typeof document === 'undefined') return;
    const total = this._currentPackResult?.cards?.length || 0;
    for (let i = Math.max(0, from); i <= Math.min(to, total - 1); i++) {
      const cardEl = this._ensureCardFrontMounted(i);
      cardEl?.classList?.remove?.('is-culled-3d');
      const img = cardEl?.querySelector?.('.pack-card-baked-img');
      if (!img || img.dataset?.warpHydrated) continue;
      if (img.dataset) img.dataset.warpHydrated = '1';
      img.loading = 'eager';
      if (img.dataset?.src) {
        img.src = img.dataset.src;
        delete img.dataset.src;
      }
      if (typeof img.decode === 'function') img.decode().catch(() => {});
    }
  }

  // A running Gold Edition ascension (normal speed) is finished on the spot - the player asked for speed.
  _cancelGoldAscend() {
    this._goldAscendEndTime = 0;
    if (typeof document === 'undefined' || !this.stageContainer?.querySelector) return;
    const view = this.stageContainer.querySelector('.pack-reveal-view');
    view?.querySelectorAll?.('.pack-card-slot.ascend').forEach((slot) => slot.classList.remove('ascend'));
    view?.querySelectorAll?.('.legend-fx').forEach((fx) => fx.remove());
    view?.querySelector?.('.pack-cards-row')?.classList?.remove?.('has-ascend');
    document.getElementById('pack-legend-dimmer')?.classList?.remove?.('active');
  }

  _allowWarpSfx() {
    if (!this._warpActive) return true;
    if (isAbOn('nowarpaudio')) return false;
    const now = performance.now();
    if (now - this._warpLastSfxAt < WARP_SFX_MIN_GAP_MS) return false;
    this._warpLastSfxAt = now;
    return true;
  }

  // One screen-wide flash. Web Animations API: it restarts without the remove-class / force-reflow trick
  // and runs on the compositor (opacity only).
  _fireWarpFlash(peak, durationMs, tone = 'cyan') {
    const el = this._ensureWarpOverlay();
    if (!el) return;
    if (el.dataset) el.dataset.tone = tone;
    if (typeof el.animate === 'function') {
      try { this._warpFlashAnim?.cancel?.(); } catch { /* already finished */ }
      this._warpFlashAnim = el.animate(
        [{ opacity: 0 }, { opacity: peak, offset: 0.24 }, { opacity: 0 }],
        { duration: durationMs, easing: 'cubic-bezier(0.2, 0.7, 0.3, 1)' }
      );
    } else if (el.style) {
      el.style.opacity = String(peak);
      this._scheduleTimeout(() => { el.style.opacity = ''; }, durationMs * 0.4);
    }
  }

  _spawnWarpStreaks() {
    if (!this.sparksCanvas || this.sparksCanvas.width === 0) {
      this._setupCanvases();
    }
    if (!this.sparksCanvas) return;
    const dpr = this.dpr || 1;
    const w = this.sparksCanvas.width / dpr;
    const h = this.sparksCanvas.height / dpr;
    const reach = Math.hypot(w, h) / 2;
    const mobile = this._isMobile();
    const n = mobile ? 18 : 28;
    for (let i = 0; i < n; i++) {
      this._warpStreaks.push({
        ang: (i / n) * Math.PI * 2 + (Math.random() - 0.5) * 0.22,
        r: reach * (0.05 + Math.random() * 0.12),
        v: reach * (2.4 + Math.random() * 1.6),
        len: reach * (0.16 + Math.random() * 0.28),
        w: (mobile ? 2.0 : 2.6) + Math.random() * 3,
        life: 1,
        decay: 2.8 + Math.random() * 0.8
      });
    }
    if (!this._vfxLoopRunning) this._startVfxLoop();
  }

  _updateWarpStreaks(dt) {
    const list = this._warpStreaks;
    for (let i = list.length - 1; i >= 0; i--) {
      const s = list[i];
      s.r += s.v * dt;
      s.life -= s.decay * dt;
      if (s.life <= 0) {
        list[i] = list[list.length - 1];
        list.pop();
      }
    }
  }

  // Radial speed lines from the middle of the scene, drawn into the sparks canvas' own pass.
  _drawWarpStreaks(ctx) {
    const sprite = this._getStreakSprite();
    if (!sprite || !this.sparksCanvas) return;
    const dpr = this.dpr || 1;
    const cx = (this.sparksCanvas.width / dpr) / 2;
    const cy = (this.sparksCanvas.height / dpr) * 0.46;
    ctx.save();
    ctx.globalCompositeOperation = 'lighter';
    for (const s of this._warpStreaks) {
      const cos = Math.cos(s.ang);
      const sin = Math.sin(s.ang);
      ctx.globalAlpha = Math.min(1, s.life * 1.5) * 0.9;
      ctx.setTransform(dpr * cos, dpr * sin, -dpr * sin, dpr * cos, dpr * cx, dpr * cy);
      ctx.drawImage(sprite, s.r, -s.w / 2, s.len, s.w);
    }
    ctx.restore();
  }

  _applyWarpHud() {
    const warpBtn = typeof document !== 'undefined'
      ? (document.getElementById('btn-warp-speed') || document.getElementById('btn-reveal-all'))
      : null;
    if (warpBtn) {
      warpBtn.disabled = true;
      warpBtn.classList?.add?.('is-active');
      warpBtn.innerHTML = `${getIconSvg('energy', 12)} WARP ACTIVE`;
      if (warpBtn.style) warpBtn.style.pointerEvents = 'none';
    }
    this.stageContainer?.querySelector?.('.pack-reveal-view')?.classList?.add?.('warp-run');
  }

  // Row geometry measured once per run, so no flip has to read layout while the row is being animated.
  _measureWarpGeometry(cardsRow) {
    if (!cardsRow?.querySelectorAll) return null;
    const slots = cardsRow.querySelectorAll('.pack-card-slot');
    const centers = [];
    for (let i = 0; i < slots.length; i++) centers[i] = slots[i].offsetLeft + slots[i].offsetWidth / 2;
    return {
      swipe: this._usesSwipeReveal(),
      rowW: cardsRow.clientWidth,
      maxScroll: Math.max(0, cardsRow.scrollWidth - cardsRow.clientWidth),
      centers,
      n: slots.length
    };
  }

  _warpScrollTo(idx) {
    const g = this._warpGeom;
    const row = typeof document !== 'undefined' ? document.getElementById('pack-cards-row') : null;
    if (!g || !row) return;
    let left = 0;
    if (g.swipe) left = Math.max(0, (g.centers[idx] || 0) - g.rowW / 2);
    else if (g.maxScroll > 0 && g.n > 1) left = (idx / (g.n - 1)) * g.maxScroll;
    row.scrollLeft = left;
    this._currentMobileCardIdx = idx;
  }

  _allCardsFlipped() {
    const total = this._currentPackResult?.cards?.length || 0;
    for (let i = 0; i < total; i++) {
      const c = document.getElementById(`pack-card-${i}`);
      if (!c || !c.classList?.contains?.('flipped')) return false;
    }
    return true;
  }

  activateWarpSpeed() {
    if (this._isWarpSpeed) return;
    this._armWarp(); // normally done by the Reveal All click; harmless when it already ran
    this._isWarpSpeed = true;
    this._warpActive = true;
    const gen = ++this._warpGen;
    const fx = !isAbOn('nowarpfx');

    // The normal-speed reveal stops here; the cascade below takes over every card still face-down.
    if (this._autoSwipeTimeoutId) {
      clearTimeout(this._autoSwipeTimeoutId);
      this._autoSwipeTimeoutId = null;
    }
    if (this._cardRevealDebounceTimer) {
      clearTimeout(this._cardRevealDebounceTimer);
      this._cardRevealDebounceTimer = null;
    }
    if (this._desktopRevealTimers?.length) {
      this._desktopRevealTimers.forEach((t) => clearTimeout(t));
      this._desktopRevealTimers = [];
    }
    if (this._snapRestoreTimer) {
      clearTimeout(this._snapRestoreTimer);
      this._snapRestoreTimer = null;
    }
    this._cancelGoldAscend();
    this._isAutoRevealing = true; // taps do nothing meanwhile, and no single flip shows the summary

    if (this.audio && !this._isAudioMuted()) {
      this._playCargoBaySfx('warp_speed_ignition', { category: 'ui', volume: 0.85 });
    }

    const cardsRow = typeof document !== 'undefined' ? document.getElementById('pack-cards-row') : null;
    if (cardsRow) {
      cardsRow.style?.setProperty?.('--flip-duration', '0.14s');
      if (cardsRow.style) cardsRow.style.scrollSnapType = 'none';
      cardsRow.classList?.add?.('is-warp-speed');
    }
    this._warpGeom = this._measureWarpGeometry(cardsRow);

    if (fx) {
      const reduce = this._prefersReducedMotion();
      this._fireWarpFlash(reduce ? 0.3 : 0.75, 260);
      if (!reduce) this._spawnWarpStreaks();
      // The HUD swap (button text, dots) happens in the flash's brightest moment, not before it.
      this._scheduleTimeout(() => { if (gen === this._warpGen) this._applyWarpHud(); }, 60);
    } else {
      this._applyWarpHud();
    }

    if (typeof this.spawnGiganticSparkTorrent === 'function' && this.sparksEnabled) {
      this.spawnGiganticSparkTorrent(this._isMobile() ? 8 : 14, '#00e5ff');
    }
    this._pulseHaptic([25, 30, 55]);

    this._startWarpCascade(gen, fx ? WARP_LEAD_MS : 0);
  }

  // One rAF scheduler instead of N setTimeouts: at most two flips per frame, each at its due time.
  _startWarpCascade(gen, leadMs = 0) {
    const cards = this._currentPackResult?.cards || [];
    const total = cards.length;
    const queue = [];
    for (let i = 0; i < total; i++) {
      const c = document.getElementById(`pack-card-${i}`);
      if (!c || !c.classList?.contains?.('flipped')) queue.push(i);
    }
    const carousel = this._usesSwipeReveal() || total > 5;
    const interval = carousel ? WARP_FLIP_INTERVAL_MS.carousel : WARP_FLIP_INTERVAL_MS.row;
    const regFiles = this.audio?.registry?.cargo_bay?.warp_domino_flip?.files
      || this.audio?.registry?.cargo_bay?.reveal_normal_flip?.files;
    const chosenFile = (Array.isArray(regFiles) && regFiles.length > 0)
      ? (regFiles.includes('card-place-2.ogg') ? 'card-place-2.ogg' : regFiles[0])
      : 'card-place-2.ogg';

    let nextDue = performance.now() + leadMs;
    let k = 0;
    const tick = () => {
      if (gen !== this._warpGen || this._state !== 'opened') return;
      const now = performance.now();
      if (now - nextDue > interval * 3) nextDue = now; // a long hitch is not made up for in a burst
      let flipped = 0;
      while (queue.length && now >= nextDue && flipped < 2) {
        const idx = queue.shift();
        flipped++;
        if (carousel) this._warpScrollTo(idx);
        this._hydrateWarpCards(idx + 1, idx + 6);
        this.revealCard(idx, {
          file: chosenFile,
          rate: parseFloat((1.0 + (k++ % 5) * 0.05).toFixed(3)),
          category: 'card_flip',
          registryKey: 'warp_domino_flip'
        });
        nextDue += interval + (cards[idx]?.type === 'special_edition' ? WARP_GOLD_STING_MS : 0);
      }
      if (queue.length) {
        this._warpRaf = requestAnimationFrame(tick);
      } else {
        this._warpRaf = null;
        this._awaitWarpFlips(gen, performance.now() + 450);
      }
    };
    if (!queue.length) {
      this._awaitWarpFlips(gen, performance.now());
      return;
    }
    this._warpRaf = requestAnimationFrame(tick);
  }

  // The last flips may still be waiting for an image decode (revealCard holds up to 280 ms).
  _awaitWarpFlips(gen, deadline) {
    const check = () => {
      if (gen !== this._warpGen || this._state !== 'opened') return;
      if (this._allCardsFlipped() || performance.now() >= deadline) {
        this._scheduleTimeout(() => this._finishWarp(gen), WARP_SETTLE_MS);
        return;
      }
      this._warpRaf = requestAnimationFrame(check);
    };
    check();
  }

  _finishWarp(gen) {
    if (gen !== this._warpGen || this._state !== 'opened') return;
    const fx = !isAbOn('nowarpfx');
    const show = () => {
      if (gen !== this._warpGen) return;
      this._renderWarpHaul();
      this._isAutoRevealing = false;
      this.showSummary();
      // Layers armed for the run are released once the haul's entrance has played.
      this._scheduleTimeout(() => {
        if (gen !== this._warpGen) return;
        this._warpActive = false;
        document.getElementById('cargo-scene')?.classList?.remove?.('warp-armed');
      }, 520);
    };
    if (fx) {
      this._fireWarpFlash(this._prefersReducedMotion() ? 0.25 : 0.5, 240);
      this._scheduleTimeout(show, 70); // swap while the flash is at its peak
    } else {
      show();
    }
  }

  // Gold Edition at warp speed: the card pops, a gold flash fires, the cascade holds for the sting.
  _triggerWarpGoldSting(slotEl) {
    if (!slotEl) return;
    slotEl.classList?.add?.('warp-gold-sting');
    this._scheduleTimeout(() => slotEl.classList?.remove?.('warp-gold-sting'), WARP_GOLD_STING_MS + 30);
    if (!isAbOn('nowarpfx')) this._fireWarpFlash(this._prefersReducedMotion() ? 0.2 : 0.45, WARP_GOLD_STING_MS, 'gold');
    this._pulseHaptic([25, 20, 60]);
  }

  _buildHaulHtml(cards) {
    const h = summarizeHaul(cards);
    const chips = [];
    if (h.gold) chips.push(`<span class="haul-chip is-gold"><b>${h.gold}</b> GOLD</span>`);
    if (h.newCards) chips.push(`<span class="haul-chip is-new"><b>${h.newCards}</b> NEW</span>`);
    if (h.tickets) chips.push(`<span class="haul-chip is-ticket"><b>${h.tickets}</b> ${h.tickets === 1 ? 'SPIN' : 'SPINS'}</span>`);

    const tiles = [];
    h.highlights.forEach((card, i) => {
      const gold = card.type === 'special_edition';
      const name = card.cardName || card.title || '';
      const url = this._getCardFrontImageUrl(card);
      tiles.push(`
        <div class="pack-haul-card ${gold ? 'is-gold' : ''} ${card.isNew ? 'is-new' : ''}" style="--haul-i: ${i + 1};">
          ${url
            ? `<img src="${escapeHtml(url)}" alt="${escapeHtml(name)}" class="pack-haul-img" draggable="false" decoding="async" />`
            : `<span class="pack-haul-name">${escapeHtml(name)}</span>`}
          ${card.isNew ? '<span class="pack-haul-new">NEW</span>' : ''}
        </div>`);
    });
    if (h.tickets) {
      // The ticket card of the reveal, with the number of tickets on a ribbon across its top-right corner.
      tiles.push(`
        <div class="pack-haul-card pack-haul-ticket" style="--haul-i: ${h.highlights.length + 1};">
          <div class="pack-haul-ticket-glow"></div>
          <div class="pack-haul-ticket-body">
            <div class="pack-haul-ticket-icon">${getIconSvg('ticket', 72)}</div>
            <div class="pack-haul-ticket-title">SLOT TICKET</div>
            <div class="pack-haul-ticket-badge">CYBER RARE</div>
            <div class="pack-haul-ticket-desc">1 Free Spin for the Slot Machine</div>
          </div>
          <div class="pack-haul-ribbon">×${h.tickets}</div>
        </div>`);
    }

    // Nothing but copies of cards the player already owns: one big tile says so (duplicates are never
    // counted on the pills).
    if (!tiles.length) {
      tiles.push(`
        <div class="pack-haul-card pack-haul-copies" style="--haul-i: 0;">
          <div class="pack-haul-copies-body">
            <div class="pack-haul-copies-stack" aria-hidden="true"><span></span><span></span><span></span></div>
            <div class="pack-haul-copies-title">COPIES ONLY</div>
            <div class="pack-haul-copies-badge">NO NEW CARDS</div>
          </div>
        </div>`);
    }

    let glowTheme = 'default';
    if (h.gold > 0) glowTheme = 'gold';
    else if (h.tickets > 0) glowTheme = 'ticket';
    else if (h.newCards > 0) glowTheme = 'new';

    return `
      <div class="pack-haul" id="pack-haul" data-tiles="${tiles.length}" ${glowTheme !== 'default' ? `data-glow="${glowTheme}"` : ''} role="status" aria-live="polite">
        <div class="pack-haul-glow" aria-hidden="true"></div>
        ${chips.length ? `<div class="pack-haul-stats" style="--haul-i: 0;">${chips.join('')}</div>` : ''}
        <div class="pack-haul-cards">${tiles.join('')}</div>
      </div>`;
  }

  // Replaces the card row (and its dots) inside the reveal's stage-centre; the footer zone with the
  // summary buttons keeps its per-breakpoint layout untouched. The row's DOM and listeners go with it.
  _renderWarpHaul() {
    const center = this.stageContainer?.querySelector?.('.pack-reveal-stage-center');
    const cards = this._currentPackResult?.cards;
    if (!center || !cards) return;
    this._cleanupDragListeners();
    this._slotCenters = null;
    this._warpGeom = null;
    center.innerHTML = this._buildHaulHtml(cards);
    center.classList.add('is-haul');
    this.stageContainer.querySelector('.pack-reveal-view')?.classList?.remove?.('warp-run');
    document.getElementById('pack-legend-dimmer')?.classList?.remove?.('active');
  }

  _startMobileAutoSwipeReveal(chosenFile) {
    if (this._isAutoRevealing) return;
    this._isAutoRevealing = true;

    const totalCards = this._currentPackResult?.cards?.length || 5;

    // Determine starting card index: find the first unrevealed card
    let startIdx = 0;
    for (let i = 0; i < totalCards; i++) {
      const cardEl = document.getElementById(`pack-card-${i}`);
      if (!cardEl || !cardEl.classList.contains('flipped')) {
        startIdx = i;
        break;
      }
    }

    // If an ascend animation is currently running on any gold card,
    // wait for it to finish before starting any card swipes.
    const remainingGoldTime = Math.max(0, (this._goldAscendEndTime || 0) - Date.now());
    if (remainingGoldTime > 0) {
      this._autoSwipeTimeoutId = this._scheduleTimeout(() => {
        this._autoSwipeTimeoutId = null;
        this._stepMobileAutoSwipe(startIdx, totalCards, chosenFile);
      }, remainingGoldTime + 300);
      return;
    }

    this._stepMobileAutoSwipe(startIdx, totalCards, chosenFile);
  }

  _stepMobileAutoSwipe(idx, totalCards, chosenFile) {
    if (this._state !== 'opened' || !this._isAutoRevealing) {
      this._isAutoRevealing = false;
      return;
    }

    // When all cards have been processed and revealed
    if (idx >= totalCards) {
      this._isAutoRevealing = false;
      this.showSummary();
      return;
    }

    const cardEl = document.getElementById(`pack-card-${idx}`);
    const isAlreadyFlipped = cardEl && cardEl.classList.contains('flipped');
    const cardData = this._currentPackResult?.cards?.[idx];
    const isGold = cardData?.type === 'special_edition';
    const rate = parseFloat((1.0 + idx * 0.035).toFixed(3));

    const packCount = Math.max(1, Math.min(5, Math.ceil(totalCards / 5)));
    const scrollWait = this._isWarpSpeed ? 20 : Math.max(160, 320 - (packCount - 1) * 38);

    // If the target card is not currently centered, smoothly scroll it into center
    if (this._currentMobileCardIdx !== idx) {
      this._goToMobileCard(idx);
      // Wait for smooth scroll to finish centering the card
      this._autoSwipeTimeoutId = this._scheduleTimeout(() => {
        this._autoSwipeTimeoutId = null;
        if (this._state !== 'opened' || !this._isAutoRevealing) return;
        this._revealAndAdvanceMobileCard(idx, totalCards, chosenFile, isAlreadyFlipped, isGold, rate);
      }, scrollWait);
    } else {
      this._revealAndAdvanceMobileCard(idx, totalCards, chosenFile, isAlreadyFlipped, isGold, rate);
    }
  }

  _revealAndAdvanceMobileCard(idx, totalCards, chosenFile, isAlreadyFlipped, isGold, rate) {
    if (this._state !== 'opened' || !this._isAutoRevealing) return;

    if (!isAlreadyFlipped) {
      this.revealCard(idx, { file: chosenFile, rate, category: 'card_flip' });
    }

    // Determine how long to hold this card in center view before swiping to the next card.
    // Gold Edition cards have a full 1500ms ascension celebration (screen dimmer, shockwave,
    // light rays, Gauss SFX, haptic pulse). Per spec:
    // "beim treffer einer goldkarte kann dies erst nach einblendung der Goldkarte passieren"
    let holdDurationMs;
    if (this._isWarpSpeed) {
      holdDurationMs = isGold ? 90 : (isAlreadyFlipped ? 20 : 45);
      if (idx === totalCards - 1) {
        holdDurationMs = 110;
      }
    } else if (isGold) {
      // 1500ms ascend duration + 350ms settling hold
      holdDurationMs = 1850;
    } else {
      // Normal card: flip animation + hold time scales with pack count (faster on multi-pack)
      const packCount = Math.max(1, Math.min(5, Math.ceil(totalCards / 5)));
      holdDurationMs = isAlreadyFlipped ? Math.max(110, 250 - (packCount - 1) * 35) : Math.max(260, 700 - (packCount - 1) * 110);
    }

    // On the final card, if it's not gold, give a slightly longer hold before swapping to summary buttons
    if (idx === totalCards - 1 && !isGold && !this._isWarpSpeed) {
      const packCount = Math.max(1, Math.min(5, Math.ceil(totalCards / 5)));
      holdDurationMs = Math.max(380, 850 - (packCount - 1) * 115);
    }

    this._autoSwipeTimeoutId = this._scheduleTimeout(() => {
      this._autoSwipeTimeoutId = null;
      if (this._state !== 'opened' || !this._isAutoRevealing) return;
      this._stepMobileAutoSwipe(idx + 1, totalCards, chosenFile);
    }, holdDurationMs);
  }

  // Gold Edition gets its own celebrated moment: lifts and re-centers over
  // the scene instead of just flipping in place. On desktop, the re-center offset
  // is computed from the slot's actual on-screen position (not assumed to be
  // centered already). On mobile, the carousel is already centered on the card,
  // so offsets are zeroed to prevent vertical clipping against the scroll row.
  _triggerGoldAscend(slotEl) {
    if (!slotEl) return;
    const scene = this.stageContainer?.closest('.cargo-scene') || this.stageContainer;
    if (!scene) return;

    this._goldAscendEndTime = Date.now() + 1500;

    const isMobile = this._isMobile();
    if (!isMobile) {
      const sceneRect = scene.getBoundingClientRect();
      const slotRect = slotEl.getBoundingClientRect();
      const dx = (sceneRect.left + sceneRect.width / 2) - (slotRect.left + slotRect.width / 2);
      const dy = (sceneRect.top + sceneRect.height / 2) - (slotRect.top + slotRect.height / 2);
      slotEl.style.setProperty('--ascend-dx', `${Math.round(dx)}px`);
      slotEl.style.setProperty('--ascend-dy', `${Math.round(dy)}px`);
    } else {
      slotEl.style.setProperty('--ascend-dx', '0px');
      slotEl.style.setProperty('--ascend-dy', '0px');
      const idx = parseInt(slotEl.getAttribute('data-index'), 10);
      if (!isNaN(idx) && this._currentMobileCardIdx !== idx) {
        this._goToMobileCard(idx);
      }
    }

    const dimmer = document.getElementById('pack-legend-dimmer');
    const cardsRow = slotEl.closest('.pack-cards-row');
    slotEl.classList.add('ascend');
    if (cardsRow) cardsRow.classList.add('has-ascend');
    if (dimmer) dimmer.classList.add('active');
    this._pulseHaptic([25, 20, 25, 20, 60]);

    // Light rays + shockwave ring, injected only for this one card rather
    // than carried unused in every slot's markup - removed again once the
    // ascension settles.
    const fx = document.createElement('div');
    fx.className = 'legend-fx';
    fx.innerHTML = '<div class="legend-ray"></div><div class="legend-ring"></div>';
    slotEl.appendChild(fx);

    this._scheduleTimeout(() => {
      slotEl.classList.remove('ascend');
      if (cardsRow) cardsRow.classList.remove('has-ascend');
      if (dimmer) dimmer.classList.remove('active');
      fx.remove();
    }, 1500);
  }

  showSummary() {
    packs.flushNotifications();
    const summaryDelayMs = this._isWarpSpeed ? 140 : 420;
    setTimeout(() => {
      const actionsEl = typeof document !== 'undefined' ? document.getElementById('pack-reveal-actions') : null;
      if (actionsEl) {
        actionsEl.style.display = 'none';
      }

      const summaryEl = typeof document !== 'undefined' ? document.getElementById('pack-summary-bar') : null;
      if (!summaryEl) return;

      const boosterCount = inventory.getBoosterPacks();
      const packDef = PACK_DEFINITIONS[this._selectedPackId] || PACK_DEFINITIONS.core_pack;
      const packCost = packDef.cost ?? PACK_COST_FUGAZI;
      const canOpenAgain = boosterCount > 0 || wallet.getFugazi() >= packCost;
      const hasSpins = (wallet.getTickets?.() || 0) > 0;

      summaryEl.innerHTML = `
        <div class="summary-buttons">
          <button id="btn-reopen-pack" class="hud-btn pack-btn-reopen pack-action-btn ${!canOpenAgain ? 'no-funds' : ''}">
            Next
          </button>
          ${hasSpins ? `
            <button id="btn-matrix-pack" class="hud-btn pack-btn-matrix pack-action-btn">
              Supply
            </button>
          ` : `
            <button id="btn-arsenal-pack" class="hud-btn pack-btn-arsenal pack-action-btn">
              Arsenal
            </button>
          `}
        </div>
      `;
      summaryEl.style.display = 'flex';
      this._updateBalanceFooter();

      const btnReopen = document.getElementById('btn-reopen-pack');
      if (btnReopen) {
        const handleReopen = (e) => {
          if (e) {
            e.preventDefault();
            e.stopPropagation();
          }
          this._state = 'idle';
          this.renderBuyStage();
        };
        btnReopen.addEventListener('click', handleReopen);
        btnReopen.addEventListener('touchend', handleReopen, { passive: false });
      }
      const btnMatrix = document.getElementById('btn-matrix-pack');
      if (btnMatrix) {
        const handleMatrix = (e) => {
          if (e) {
            e.preventDefault();
            e.stopPropagation();
          }
          hubNavigator.navigateTo('matrix');
        };
        btnMatrix.addEventListener('click', handleMatrix);
        btnMatrix.addEventListener('touchend', handleMatrix, { passive: false });
      }
      const btnArsenal = document.getElementById('btn-arsenal-pack');
      if (btnArsenal) {
        const handleArsenal = (e) => {
          if (e) {
            e.preventDefault();
            e.stopPropagation();
          }
          hubNavigator.navigateTo('arsenal');
        };
        btnArsenal.addEventListener('click', handleArsenal);
        btnArsenal.addEventListener('touchend', handleArsenal, { passive: false });
      }
    }, summaryDelayMs);
  }
}
